import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connector, autoTune } from '../services/api';
import type { ConnectorDataset, DatasetAgentsPreviewResponse, TrainingJobRequestBody, TrainingPreviewResponse } from '../types/api';
import DatasetAgentsPreviewPanel from './training/DatasetAgentsPreviewPanel';
import TrainingPreviewResultView from './training/TrainingPreviewResultView';
import {
  applyDatasetAgentPreviewToSelections,
  buildAgentReactionFilter,
  buildDatasetAgentSelectionsFromPreview,
  buildDatasetAgentsFilterPayload,
  buildTrainingAgentsPayloadFromSelections,
  countSelectedEligibleDatasetAgents,
  datasetAgentSelectionsMissingBaseModel,
  recalculateMissingAgentGroupEligibility,
  type DatasetAgentTrainingSelection,
} from '../utils/datasetAgentsPreview';
import { listFineTuningBaseModelOptions } from '../utils/trainableBaseModels';
import AgentConfigurator from './agent-configurator/AgentConfigurator';
import type { AgentConfiguratorState, AgentWorker, ReminderPromptPosition } from '../types/agentConfigurator';
import { mergeMetaPromptsWithFinalStepDefault, normalizeReminderPromptPosition } from '../types/agentConfigurator';
import { NOTIFICATION_AUTO_HIDE_MS } from '../constants/uiTiming';
import { normalizeWorkerIdString } from '../utils/workerAgentIds';
import Notification from './Notification';
import VUserPanel from './admin/VUserPanel';
import DatasetsPage from './DatasetsPage';
import AutoFineTuningPanel from './admin/AutoFineTuningPanel';
import PipelinePanel from './admin/PipelinePanel';
import FineTuningGraphPanel from './FineTuningGraphPanel';
import FineTuningJobProgress from './training/FineTuningJobProgress';
import {
  enrichTrainingJobRowForDisplay,
  mergeTrainingJobHistoryRows,
  resolveEffectiveTrainingJobStatus,
  resolveFineTuningJobProgressMetrics,
} from '../utils/trainingJobProgress';
import ConnectorTabBar from './connector/ConnectorTabBar';
import ConnectorSectionShell from './connector/ConnectorSectionShell';
import ConnectorSelectIntegrationPrompt from './connector/ConnectorSelectIntegrationPrompt';
import { getConnectorTab, type ConnectorTabType } from './connector/connectorTabConfig';
import ConnectorChatViewerPanel from './ConnectorChatViewerPanel';
import ConnectorRagDocumentsPanel from './ConnectorRagDocumentsPanel';
import ConnectorWorkflowPanel from './connector/workflow/ConnectorWorkflowPanel';
import type { WorkflowEditBridge } from '../types/workflowBridge';
import NLTSidebar from './NLTSidebar';
import NLTConfigCompareChats from './NLTConfigCompareChats';
import { nlt } from '../services/api';
import type { NltPatchReviewState } from '../types/nltPatchReview';
import type { AgentConfiguratorPatchReview } from '../types/nltPatchReview';
import {
  allFieldsAccepted,
  finalizeFieldDecisionsForApply,
  mergeConfigWithFieldDecisions,
} from '../utils/nltConfigFieldDiff';
import type { NltCompareQuote } from '../utils/nltQuote';
import { capitalizeError } from '../utils/errorUtils';
import MultiAgentConfigVisualization from './MultiAgentConfigVisualization';
import ConnectorEvaluationGraphs from './connector/ConnectorEvaluationGraphs';
import ConnectorIntegrationsPicker from './connector/ConnectorIntegrationsPicker';
import ConfirmModal from './ConfirmModal';
import ConnectorOnboarding from './ConnectorOnboarding';
import HistoryContextControl, { BoolToggleRow } from './ui/HistoryContextControl';
import { numberInputDisplay, clampHistoryDepth } from '../utils/numberInputDraft';
import ModalFormField from './ui/ModalFormField';
import './ui/ModalFormField.css';
import { useModalFormValidation } from '../hooks/useModalFormValidation';
import { useSmoothProgressDisplay } from '../hooks/useSmoothProgressDisplay';
import { useJobsApiProgressDisplay } from '../hooks/useJobsApiProgressDisplay';
import {
  INTEGRATION_ERROR_BANNER_ID,
  INTEGRATION_URL_ID,
  TRAINING_JOB_AGENTS_ID,
  TRAINING_JOB_DATASETS_ID,
  TRAINING_JOB_ERROR_BANNER_ID,
  TRAINING_JOB_MODEL_NAME_ID,
  TRAINING_JOB_PREVIEW_ID,
  validateIntegrationForm,
  validateTrainingJobForm,
} from '../utils/connectorModalValidation';
import { useOptimizationStore } from '../store/optimizationStore';
import { unifiedWebSocketService } from '../services/unifiedWebSocket';
import type { MultiAgentConfig, ConnectorMultiAgentConfig } from '../services/unifiedWebSocket';
import { useAuthContext } from '../contexts/AuthContext';
import { 
  calculateExponentialProgress, 
  calculateTotalProgress, 
  getElapsedSeconds 
} from '../utils/exponentialProgress';
import {
  extractJobRawError,
  formatFineTuningGroupErrorMessage,
  formatJobErrorMessage,
  formatOptimizationJobStartError,
  formatTrainingJobFailedAgentsErrorMessage,
  formatTrainingJobUserErrorMessage,
  getErrorMessage,
  isFineTuningProviderLevelError,
  normalizeJobErrorCompare,
  resolveFineTuningJobError,
  resolveEvaluationJobError,
  resolveJobErrorDisplay,
  resolveOptimizationJobError,
} from '../utils/errorUtils';
import FailedJobErrorMessage from './ui/FailedJobErrorMessage';
import {
  formatConnectorIntegrationCreatedAt,
  getConnectorIntegrationCreatedByDisplay,
  mergeIntegrationDetailLayers,
  pickIntegrationApiKey,
} from '../utils/connectorIntegrationDisplay';
import { getFineTuningGroups } from '../utils/fineTuningGroups';
import {
  canCancelJob,
  assertTrainingJobRetrySucceeded,
  canRetryTrainingJob,
  extractOptimizationJobIdFromResponse,
  isActiveJobStatus,
  buildJobRetryUiRowKey,
  isJobRetryUiRowActive,
  resolveTrainingJobRecordId,
} from '../utils/trainingJobRetry';
import {
  enrichOptimizationJobRowForDisplay,
  inferOptimizationJobTerminalStatus,
  isOptimizationJobRetryEligible,
  isOptimizationJobStepsComplete,
  mergeOptimizationJobHistoryRows,
  resolveEffectiveOptimizationJobStatus,
  resolveOptimizationJobProgressDisplay,
  resolveOptimizationJobProgressPercent,
  resolveOptimizationJobRecordId,
  resolveOptimizationJobMaxStepNumber,
  resolveOptimizationJobStepDisplayIndex,
  shouldTrackOptimizationJobRow,
  unwrapOptimizationJobRow,
} from '../utils/optimizationJobProgress';
import OptimizationJobProgressBar from './optimization/OptimizationJobProgressBar';
import {
  clearPersistedOptimizationJobId,
  connectorOptPollingStorageKey,
  hasAnyPersistedOptimizationJobId,
  isCancelledOptimizationJobStatus,
  isFailedOptimizationJobStatus,
  isSucceededOptimizationJobStatus,
  isTerminalOptimizationJobStatus,
  normalizeOptimizationJobStatus,
  readPersistedOptimizationJobId,
} from '../utils/optimizationJobLifecycle';
import { getAvailableModelsForConfigurator, getConfiguratorModelLabels } from '../utils/configuratorModels';
import {
  resolveStoredIntegrationId,
  setStoredSelectedIntegrationId,
} from '../utils/selectedIntegrationStorage';
import { hasConnectorPendingConfig, takeConnectorPendingConfig } from '../utils/connectorPendingConfig';
import {
  buildAgentConfiguratorStateFromMultiAgentIterationResult,
  OPTIMIZATION_COORDINATOR_MISSING_MESSAGE,
  resolveOptimizationCompareConfigs,
} from '../utils/agentConfiguratorFromOptimization';
import { mergePreservedConnectorConfig } from '../utils/connectorConfigPreservation';
import {
  getGpt5ModelSpec,
  isEligibleForFineTuningJob,
  resolveModelsReasoning,
  shouldShowTemperature,
} from '../utils/gpt5ConfiguratorModels';
import { formatOptimizationDatasetNameForApi } from '../utils/optimizationDatasetName';
import type {
  EvaluationMode,
  SelectionScenario,
  EvaluatorParamsFormState,
} from '../types/optimizationJob';
import {
  DEFAULT_EVALUATION_MODE,
  DEFAULT_SELECTION_SCENARIO,
  buildOptimizationJobApiBody,
  buildEvaluatorParamsPayload,
  createDefaultEvaluatorParamsForm,
  mergeEvaluatorParamsFormForMode,
  validateEvaluatorParamsForm,
  deriveOptimizeByTokenFromSelectionScenario,
} from '../types/optimizationJob';
import {
  buildOptimizationPolicy,
  createDefaultOptimizationPolicyUi,
  hasModifiableOptimizationTargets,
  syncOptimizationPolicyUi,
  type OptimizationPolicyUiState,
} from '../utils/optimizationPolicy';
import type { OptimizationDatasetNamePayload } from '../utils/optimizationDatasetName';
import ConnectorConfigTuningFields from './ConnectorConfigTuningFields';
import TuningModalShell from './TuningModalShell';
import './TuningModalShell.css';
import './FineTuningJobModal.css';
import EvaluationRunModal from './EvaluationRunModal';
import EvaluationJobDetailsModal from './EvaluationRunModal/EvaluationJobDetailsModal';
import EvaluationJobProgressBar from './EvaluationRunModal/EvaluationJobProgressBar';
import EvaluationRunResultView from './EvaluationRunModal/EvaluationRunResultView';
import type { EvaluationJob, EvaluationRunAgainSeed } from '../types/evaluationJob';
import {
  canCancelEvaluationJob,
  canRetryEvaluationJob,
  extractEvaluationJobIdFromResponse,
  isActiveEvaluationJobStatus,
  isTerminalEvaluationJobStatus,
  mergeEvaluationJobHistoryRows,
  parseEvaluationRunAgainSeed,
  resolveEvaluationJobProgressDisplay,
  resolveEvaluationJobDisplayStatus,
  resolveEvaluationJobRawStatus,
  resolveEvaluationJobRecordId,
  formatEvaluationJobDisplayStatusLabel,
  hasEvaluationJobExecutionStarted,
  shouldTrackEvaluationJobRow,
  unwrapEvaluationJobRow,
} from '../utils/evaluationJobProgress';
import {
  clearPersistedEvaluationJobId,
  persistEvaluationJobId,
  readPersistedEvaluationJobId,
} from '../utils/evaluationJobPersist';
import { EVALUATION_MODE_LABELS } from '../types/evaluation';
import OptimizationPreviewResultView from './optimization/OptimizationPreviewResultView';
import { buildOptimizationPreviewRequest } from '../utils/optimizationPreviewRequest';
import type { OptimizationPreviewData } from '../types/optimizationPreview';
import {
  clearJobCancellationPendingId,
  getJobCancelButtonLabel,
  isJobCancellationPending,
  isLegacyJobStopPending,
  LEGACY_JOB_STOP_PENDING_ID,
} from '../utils/jobActionUi';
import './ConnectorPage.css';
import './connector/ConnectorShell.css';

function optimizationPayloadCoordinator(c: AgentConfiguratorState['coordinator']) {
  const mr = resolveModelsReasoning(c.model, c.models_reasoning);
  const row: Record<string, unknown> = {
    name: c.name || 'master',
    description: c.description || '',
    prompt: c.prompt,
    model: c.model,
    use_rag: c.use_rag,
    use_ltm: c.use_ltm,
    use_diary_read: Boolean(c.use_diary_read),
    use_diary_write: Boolean(c.use_diary_write),
    use_portrait_read: Boolean(c.use_portrait_read),
    use_portrait_write: Boolean(c.use_portrait_write),
  };
  if (mr !== undefined) row.models_reasoning = mr;
  if (shouldShowTemperature(c.model, mr)) row.temperature = c.temperature;
  else if (!getGpt5ModelSpec(c.model)) row.temperature = c.temperature;
  const coordReminder = typeof c.reminder_prompt === 'string' ? c.reminder_prompt.trim() : '';
  if (coordReminder) row.reminder_prompt = coordReminder;
  const coordId = typeof c.id === 'string' ? c.id.trim() : '';
  if (coordId) row.id = coordId;
  const coordAgentId = typeof c.agent_id === 'string' ? c.agent_id.trim() : '';
  if (coordAgentId) row.agent_id = coordAgentId;
  else if (coordId) row.agent_id = coordId;
  if (c.use_rag && Array.isArray(c.rag_doc_ids) && c.rag_doc_ids.length > 0) {
    row.rag_doc_ids = [...c.rag_doc_ids];
  }
  return row as ConnectorMultiAgentConfig['config']['coordinator'];
}

function optimizationPayloadWorker(w: AgentWorker) {
  const mr = resolveModelsReasoning(w.model, w.models_reasoning);
  const row: Record<string, unknown> = {
    id: w.id,
    name: w.name,
    description: w.description || '',
    prompt: w.prompt,
    mode: w.mode || 'asynchronous',
    model: w.model,
    use_rag: w.use_rag,
    use_ltm: w.use_ltm,
    use_diary_read: Boolean(w.use_diary_read),
    use_diary_write: Boolean(w.use_diary_write),
    use_portrait_read: Boolean(w.use_portrait_read),
    use_portrait_write: Boolean(w.use_portrait_write),
  };
  if (mr !== undefined) row.models_reasoning = mr;
  if (shouldShowTemperature(w.model, mr)) row.temperature = w.temperature;
  else if (!getGpt5ModelSpec(w.model)) row.temperature = w.temperature;
  const workerReminder = typeof w.reminder_prompt === 'string' ? w.reminder_prompt.trim() : '';
  if (workerReminder) row.reminder_prompt = workerReminder;
  if (w.use_rag && Array.isArray(w.rag_doc_ids) && w.rag_doc_ids.length > 0) {
    row.rag_doc_ids = [...w.rag_doc_ids];
  }
  return row as ConnectorMultiAgentConfig['config']['workers'][number];
}

function buildConnectorOptimizationPayload(params: {
  integrationId: string;
  configLabel: string;
  currentConfig: AgentConfiguratorState;
  optimizationIterations: number;
  optimizationPolicyUi: OptimizationPolicyUiState;
  dataset_name: OptimizationDatasetNamePayload;
  selectionScenario: SelectionScenario;
  evaluationMode: EvaluationMode;
  evaluatorParams: EvaluatorParamsFormState;
}): ConnectorMultiAgentConfig {
  const {
    integrationId,
    configLabel,
    currentConfig,
    optimizationIterations,
    optimizationPolicyUi,
    dataset_name,
    selectionScenario,
    evaluationMode,
    evaluatorParams,
  } = params;
  const optimization_policy = buildOptimizationPolicy(currentConfig, optimizationPolicyUi);
  const trimmedConfigLabel = configLabel.trim();
  const mp = currentConfig.metaPrompts;
  const approach = currentConfig.approach || 'BASELINE';
  const resolvedEvaluationMode: EvaluationMode =
    approach === 'MUTATIONS' ? 'config-scorer' : evaluationMode;
  const evaluator_params = buildEvaluatorParamsPayload(resolvedEvaluationMode, evaluatorParams, {
    includeDirection: true,
  });

  return {
    integration_id: integrationId,
    ...(trimmedConfigLabel ? { config_label: trimmedConfigLabel } : {}),
    approach,
    selection_scenario: selectionScenario,
    evaluation_mode: resolvedEvaluationMode,
    ...(Object.keys(evaluator_params).length > 0 ? { evaluator_params } : {}),
    optimize_by_token: deriveOptimizeByTokenFromSelectionScenario(selectionScenario),
    optimization_policy,
    config: {
      dataset_name,
      iterations: optimizationIterations || 4,
      approach,
      coordinator: optimizationPayloadCoordinator(currentConfig.coordinator),
      workers: currentConfig.workers.map((worker) => optimizationPayloadWorker(worker)),
      async_orchestrator: currentConfig.async_orchestrator
        ? { ...currentConfig.async_orchestrator }
        : undefined,
      metaPrompts: mp ? { ...mp } : undefined,
      reminder_prompt_position:
        currentConfig.reminder_prompt_position === 'after' ? 'after' : 'before',
    },
  };
}

function mapOptimizationJobStepToConnectorResult(step: any, job?: unknown) {
  const config = step?.config || {};
  const coordinator = config.coordinator || config['master-agent'] || config.master_agent || {};
  const workers = Array.isArray(config.workers)
    ? config.workers
    : Array.isArray(config['worker-agents'])
      ? config['worker-agents']
      : [];
  const metaPrompts = config.metaPrompts || config.meta_prompts;
  const seq =
    typeof step?.seq === 'number'
      ? step.seq
      : resolveOptimizationJobStepDisplayIndex(step, job);

  return {
    prompt: coordinator.prompt || config.prompt || 'No prompt available',
    embedding_point: step?.embedding_point,
    evaluation_point: step?.evaluation_point,
    // Фиксированные цели прогона (не метрика отбора) — для отдельных графиков качества/токенов актора
    quality_point: step?.quality_point,
    actor_tokens_point: step?.actor_tokens_point,
    candidates_validation_scores: step?.candidates_validation_scores,
    iteration: seq,
    seq,
    temperature: coordinator.temperature,
    'master-agent': {
      id: coordinator.id || coordinator.agent_id || 'coordinator',
      name: coordinator.name || 'Agent Coordinator',
      prompt: coordinator.prompt || '',
      temperature: coordinator.temperature ?? 0.7,
      model_name: coordinator.model || coordinator.model_name || 'gpt-4.1-mini',
      description: coordinator.description || '',
      reminder_prompt: coordinator.reminder_prompt,
    },
    'worker-agents': workers.map((worker: any, index: number) => ({
      id: worker.id || String(index + 1),
      name: worker.name || worker.agent_name || `Worker ${index + 1}`,
      prompt: worker.prompt || '',
      temperature: worker.temperature ?? 0.7,
      model_name: worker.model || worker.model_name || 'gpt-4.1-mini',
      description: worker.description || '',
      mode: worker.mode === 'asynchronous' ? 'asynchronous' : 'synchronous',
      reminder_prompt: worker.reminder_prompt,
    })),
    meta_prompts: metaPrompts,
    metaPrompts,
    async_orchestrator: config.async_orchestrator,
    reminder_prompt_position: config.reminder_prompt_position,
  };
}

function safeJsonParse(input: any): any | null {
  if (typeof input !== 'string') return null;
  try {
    return JSON.parse(input);
  } catch {
    return null;
  }
}

function extractConfigString(raw: any): { configString?: string; label?: string; dialog?: any[] } {
  const d = raw?.data ?? raw;

  const configNode = d?.config ?? d?.data?.config ?? d?.data;
  const labelCandidates = [
    d?.label,
    d?.data?.label,
    d?.config?.label,
    d?.data?.config?.label,
    configNode?.label,
    configNode?.config?.label,
    configNode?.config?.config?.label,
  ];
  const label = labelCandidates.find((value) => typeof value === 'string') as string | undefined;
  const configString = configNode?.config?.config ?? configNode?.config ?? d?.config?.config;
  const dialog = configNode?.config?.dialog ?? configNode?.dialog ?? d?.config?.dialog ?? configNode?.config?.config?.dialog ?? [];

  return {
    configString: typeof configString === 'string' ? configString : undefined,
    label: typeof label === 'string' ? label : undefined,
    dialog: Array.isArray(dialog) ? dialog : [],
  };
}


/** Преобразует backend-статус Fine-Tuning Job в человекочитаемую подпись */
function getTrainingJobStatusLabel(status: string | undefined | null): string {
  if (status == null || status === '') return '—';
  const s = String(status).toLowerCase().trim();
  const map: Record<string, string> = {
    succeeded: 'Succeeded',
    completed: 'Succeeded',
    partially_succeeded: 'Partially succeeded',
    failed: 'Failed',
    running: 'Running',
    pending: 'Pending',
    queued: 'Queued',
    cancelled: 'Cancelled',
  };
  return map[s] ?? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Renders optimize preview result in a readable card layout instead of raw JSON */
/** Fine-tuning job model alias — must match backend max length. */
const TRAINING_JOB_MODEL_NAME_MAX_LEN = 100;

/** Persist active FT job id per integration so polling survives SPA unmount (navigate away from Connector). */
const CONNECTOR_FT_POLLING_STORAGE_PREFIX = 'connector_ft_poll_job:';

function connectorFtPollingStorageKey(integrationId: string): string {
  return CONNECTOR_FT_POLLING_STORAGE_PREFIX + integrationId;
}

function readPersistedPollingJobId(storageKeyPrefix: string, integrationId: string): string | null {
  return readPersistedConnectorFtPolling(storageKeyPrefix, integrationId)?.jobId ?? null;
}

function readPersistedConnectorFtPolling(
  storageKeyPrefix: string,
  integrationId: string
): { jobId: string; progressPercent?: number } | null {
  try {
    const raw = sessionStorage.getItem(storageKeyPrefix + integrationId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { jobId?: unknown; progressPercent?: unknown };
    const jobId = typeof parsed?.jobId === 'string' ? parsed.jobId.trim() : '';
    if (!jobId) return null;
    const progressPercent =
      typeof parsed.progressPercent === 'number' && Number.isFinite(parsed.progressPercent)
        ? parsed.progressPercent
        : undefined;
    return { jobId, progressPercent };
  } catch {
    return null;
  }
}

function writePersistedConnectorFtPolling(
  integrationId: string,
  jobId: string,
  progressPercent?: number | null
): void {
  if (!integrationId || !jobId.trim()) return;
  try {
    const payload: { v: 1; jobId: string; progressPercent?: number } = { v: 1, jobId: jobId.trim() };
    if (typeof progressPercent === 'number' && Number.isFinite(progressPercent)) {
      payload.progressPercent = progressPercent;
    }
    sessionStorage.setItem(connectorFtPollingStorageKey(integrationId), JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

function clearConnectorFtPollingPersist(integrationId: string | null | undefined): void {
  if (!integrationId) return;
  try {
    sessionStorage.removeItem(connectorFtPollingStorageKey(integrationId));
  } catch {
    // ignore
  }
}

function buildTrainingJobLiveRow(response: unknown, jobId: string): Record<string, unknown> {
  const normalized = enrichTrainingJobRowForDisplay(response);
  return {
    ...normalized,
    id: jobId,
    job_id: jobId,
  };
}

/** Default message depth for training/import/auto dataset flows (API: history_depth). */
const DEFAULT_HISTORY_DEPTH = 10;

const OPTIMIZATION_JOB_POLL_INTERVAL_MS = 2_000;
const OPTIMIZATION_JOB_FAST_POLL_INTERVAL_MS = 1_000;
const OPTIMIZATION_JOB_MAX_FINALIZING_MS = 120_000;

const ConnectorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ConnectorTabType>('integrations');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null);
  
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [dialogEnvelope, setDialogEnvelope] = useState<any[]>([]);
  const [configLabel, setConfigLabel] = useState<string | undefined>(undefined);
  const [configValue, setConfigValue] = useState<AgentConfiguratorState | null>(null);
  const [currentConfig, setCurrentConfig] = useState<AgentConfiguratorState | null>(null);
  const [nltPatchReview, setNltPatchReview] = useState<NltPatchReviewState | null>(null);
  const [nltFocusedPatchPath, setNltFocusedPatchPath] = useState<string | null>(null);
  const [nltOpen, setNltOpen] = useState(false);
  /** Published by the Workflow panel while it is mounted — lets NLT tune the graph. */
  const [nltWorkflowBridge, setWorkflowEditBridge] = useState<WorkflowEditBridge | null>(null);
  const optimizationInitialConfigRef = useRef<AgentConfiguratorState | null>(null);
  const [nltPendingQuote, setNltPendingQuote] = useState<NltCompareQuote | null>(null);
  const configLoadedFromStoreRef = useRef(false);
  /** Full last loaded / imported config JSON — merged on Save so fields absent from the UI survive. */
  const connectorConfigPreservationRef = useRef<Record<string, unknown> | null>(null);
  const optimizationRanThisSession = useRef(false);

  // Optimization state
  const {
    displayed: smoothProgress,
    setTarget: setSmoothProgressTarget,
    reset: resetSmoothProgress,
  } = useSmoothProgressDisplay();
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const optimizationJobPollRef = useRef<NodeJS.Timeout | null>(null);
  const optimizationJobPollIntervalRef = useRef<number>(OPTIMIZATION_JOB_POLL_INTERVAL_MS);
  const optimizationJobIdRef = useRef<string | null>(null);
  const optimizationJobStallSinceRef = useRef<number | null>(null);
  /** Jobs finished in this page session — do not resume polling without explicit Track/restart. */
  const dismissedOptimizationJobIdsRef = useRef<Set<string>>(new Set());
  /** Latest poll payloads — list API often lags behind GET /jobs/{id}. */
  const optimizationJobLiveSnapshotsRef = useRef<Map<string, Record<string, unknown>>>(new Map());
  const trainingJobLiveSnapshotsRef = useRef<Map<string, Record<string, unknown>>>(new Map());
  const [connectorOptimizationJobId, setConnectorOptimizationJobId] = useState<string | null>(null);
  const [trackedOptimizationJobSnapshot, setTrackedOptimizationJobSnapshot] =
    useState<Record<string, unknown> | null>(null);
  const [terminalOptimizationJob, setTerminalOptimizationJob] = useState<{
    id: string;
    status: string;
    rawError?: string;
    retryable: boolean;
  } | null>(null);
  const [optimizationJobRetryingRowKey, setOptimizationJobRetryingRowKey] = useState<string | null>(null);
  const [optimizationJobCancellingId, setOptimizationJobCancellingId] = useState<string | null>(null);
  const [trainingJobCancellingId, setTrainingJobCancellingId] = useState<string | null>(null);
  const [optimizationJobHistory, setOptimizationJobHistory] = useState<any[]>([]);
  const [optimizationJobHistoryLoading, setOptimizationJobHistoryLoading] = useState(false);
  /** Accumulated time while tab/window was hidden — excluded from progress clock. */
  const progressPausedMsRef = useRef(0);
  const progressHiddenAtRef = useRef<number | null>(null);
  const savedScrollYRef = useRef<number>(0);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [optimizationStoppedByUser, setOptimizationStoppedByUser] = useState(false);
  const [optimizationIterations, setOptimizationIterations] = useState(4);
  const [optimizationDatasets, setOptimizationDatasets] = useState<string[]>([]);
  const [optimizationPolicyUi, setOptimizationPolicyUi] = useState<OptimizationPolicyUiState>({
    agents: {},
    metaPrompts: {},
  });
  const [selectionScenario, setSelectionScenario] = useState<SelectionScenario>(DEFAULT_SELECTION_SCENARIO);
  const [evaluationMode, setEvaluationMode] = useState<EvaluationMode>(DEFAULT_EVALUATION_MODE);
  const [evaluatorParams, setEvaluatorParams] = useState<EvaluatorParamsFormState>(() =>
    createDefaultEvaluatorParamsForm(DEFAULT_EVALUATION_MODE)
  );
  const [showEvaluationRunModal, setShowEvaluationRunModal] = useState(false);
  const [evaluationRunAgainSeed, setEvaluationRunAgainSeed] = useState<EvaluationRunAgainSeed | null>(null);
  const [evaluationRunAgainKey, setEvaluationRunAgainKey] = useState<string | null>(null);
  const EVALUATION_JOB_POLL_INTERVAL_MS = 2_000;
  const evaluationJobPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const evaluationPollingJobIdRef = useRef<string | null>(null);
  const evaluationDetailsJobIdRef = useRef<string | null>(null);
  const evaluationJobLiveSnapshotsRef = useRef<Map<string, Record<string, unknown>>>(new Map());
  const evaluationJobHistoryRef = useRef<Record<string, unknown>[]>([]);
  const evaluationJobPollInFlightRef = useRef(false);
  const pollEvaluationJobStatusRef = useRef<
    ((jobId: string, opts?: { force?: boolean }) => Promise<void>) | null
  >(null);
  const pollActiveEvaluationJobsRef = useRef<
    ((opts?: { force?: boolean; includeJobId?: string }) => Promise<void>) | null
  >(null);
  const [evaluationPollingJobId, setEvaluationPollingJobId] = useState<string | null>(null);
  const [evaluationJobSnapshot, setEvaluationJobSnapshot] = useState<EvaluationJob | null>(null);
  const [evaluationDetailsModalOpen, setEvaluationDetailsModalOpen] = useState(false);
  const [evaluationDetailsJob, setEvaluationDetailsJob] = useState<EvaluationJob | null>(null);
  const [evaluationDetailsLoading, setEvaluationDetailsLoading] = useState(false);
  const [evaluationJobHistory, setEvaluationJobHistory] = useState<Record<string, unknown>[]>([]);
  const [evaluationJobHistoryLoading, setEvaluationJobHistoryLoading] = useState(false);
  const [evaluationJobCancellingId, setEvaluationJobCancellingId] = useState<string | null>(null);
  const [evaluationJobRetryingRowKey, setEvaluationJobRetryingRowKey] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [optimizePreviewLoading, setOptimizePreviewLoading] = useState(false);
  const [optimizePreviewResult, setOptimizePreviewResult] = useState<OptimizationPreviewData | null>(null);
  const [optimizePreviewError, setOptimizePreviewError] = useState<string | null>(null);
  const [showOptimizePreviewModal, setShowOptimizePreviewModal] = useState(false);
  const [connectorDatasets, setConnectorDatasets] = useState<ConnectorDataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);

  const { isAuthenticated, user } = useAuthContext();
  
  // Optimization store
  const {
    selectedDataset,
    setSelectedDataset,
    // Используем отдельные поля для ConnectorPage
    connectorMultiAgentIsOptimizing,
    connectorMultiAgentIsOptimized,
    setConnectorMultiAgentIsOptimizing,
    setConnectorMultiAgentOptimizationProgress,
    setConnectorMultiAgentIterationResults,
    setConnectorMultiAgentOptimizedPrompts,
    setConnectorMultiAgentIsOptimized,
    setConnectorMultiAgentSelectedIterationIndex,
    processConnectorMultiAgentOptimizationResult,
    clearConnectorMultiAgentChat,
    connectorMultiAgentIterationResults,
    connectorMultiAgentSelectedIterationIndex,
    connectorMultiAgentProgressStartTime,
    setConnectorMultiAgentProgressStartTime,
    connectorMultiAgentCurrentProgressStage,
    setConnectorMultiAgentCurrentProgressStage,
    connectorMultiAgentExpectedIterationTime,
    setConnectorMultiAgentExpectedIterationTime,
    loadDefaults,
    // Используем отдельные поля конфигурации для ConnectorPage
    setConnectorCoordinatorPrompt,
    setConnectorCoordinatorModel,
    setConnectorCoordinatorTemperature,
    setConnectorCoordinatorDescription,
    setConnectorCoordinatorReminderPrompt,
    setConnectorCoordinatorName,
    setConnectorCoordinatorBackendId,
    setConnectorCoordinatorAgentId,
    setConnectorReminderPromptPosition,
    setConnectorConfigLabel,
    setConnectorWorkers,
    setConnectorMetaPrompts,
    // Читаем данные из store для восстановления конфигурации
    connectorCoordinatorPrompt,
    connectorCoordinatorModel,
    connectorCoordinatorTemperature,
    connectorCoordinatorDescription,
    connectorConfigLabel,
    connectorWorkers,
    connectorMetaPrompts,
    connectorReminderPromptPosition,
    connectorCoordinatorBackendId,
    connectorCoordinatorAgentId,
  } = useOptimizationStore();

  const {
    displayed: optimizationJobsApiProgress,
    syncFromBackend: syncOptimizationJobsApiProgress,
    complete: completeOptimizationJobsApiProgress,
    reset: resetOptimizationJobsApiProgress,
  } = useJobsApiProgressDisplay({
    expectedDurationSeconds: connectorMultiAgentExpectedIterationTime,
  });
  const {
    displayed: trainingJobsApiProgress,
    syncFromBackend: syncTrainingJobsApiProgress,
    complete: completeTrainingJobsApiProgress,
    reset: resetTrainingJobsApiProgress,
  } = useJobsApiProgressDisplay({ expectedDurationSeconds: 120 });
  const {
    displayed: evaluationJobsApiProgress,
    syncFromBackend: syncEvaluationJobsApiProgress,
    complete: completeEvaluationJobsApiProgress,
    reset: resetEvaluationJobsApiProgress,
  } = useJobsApiProgressDisplay({ expectedDurationSeconds: 90 });
  const trackedOptimizationJobProgressDisplay = useMemo(
    () =>
      connectorOptimizationJobId && trackedOptimizationJobSnapshot
        ? resolveOptimizationJobProgressDisplay(trackedOptimizationJobSnapshot, optimizationIterations)
        : null,
    [connectorOptimizationJobId, optimizationIterations, trackedOptimizationJobSnapshot]
  );
  const trackedOptimizationJobStatus = trackedOptimizationJobSnapshot
    ? resolveEffectiveOptimizationJobStatus(trackedOptimizationJobSnapshot)
    : null;
  const isOptimizationCancelPending = useMemo(
    () =>
      isJobCancellationPending(
        connectorOptimizationJobId ?? optimizationJobIdRef.current,
        optimizationJobCancellingId,
        trackedOptimizationJobStatus
      ) || isLegacyJobStopPending(optimizationJobCancellingId),
    [connectorOptimizationJobId, optimizationJobCancellingId, trackedOptimizationJobStatus]
  );
  const optimizationProgressMessage = isOptimizationCancelPending
    ? 'Cancelling…'
    : trackedOptimizationJobProgressDisplay?.message;
  const connectorOptimizationProgressDisplay = connectorOptimizationJobId
    ? optimizationJobsApiProgress
    : smoothProgress;

  const evaluationJobProgressMeta = useMemo(
    () => (evaluationJobSnapshot ? resolveEvaluationJobProgressDisplay(evaluationJobSnapshot) : null),
    [evaluationJobSnapshot]
  );
  const evaluationProgressBarMeta = useMemo(() => {
    if (!evaluationJobProgressMeta) return null;
    const otherActiveCount = evaluationJobHistory.filter((row) => {
      const jobRow = unwrapEvaluationJobRow(row);
      const id = String(jobRow.id || '').trim();
      if (!id || id === evaluationPollingJobId) return false;
      return shouldTrackEvaluationJobRow(jobRow);
    }).length;
    if (otherActiveCount <= 0) return evaluationJobProgressMeta;
    const note = `${otherActiveCount} more evaluation job${otherActiveCount === 1 ? '' : 's'} in queue — see Evaluation Jobs below`;
    return {
      ...evaluationJobProgressMeta,
      meta: evaluationJobProgressMeta.meta
        ? `${evaluationJobProgressMeta.meta} · ${note}`
        : note,
    };
  }, [evaluationJobHistory, evaluationJobProgressMeta, evaluationPollingJobId]);
  const isEvaluationCancelPending = useMemo(
    () =>
      isJobCancellationPending(
        evaluationPollingJobId,
        evaluationJobCancellingId,
        evaluationJobSnapshot ? resolveEvaluationJobRawStatus(evaluationJobSnapshot) : null
      ),
    [evaluationJobCancellingId, evaluationJobSnapshot, evaluationPollingJobId]
  );
  const evaluationProgressDisplay = evaluationPollingJobId
    ? evaluationJobsApiProgress
    : evaluationJobProgressMeta?.percent ?? 0;
  const isEvaluationJobRunning = Boolean(evaluationPollingJobId);

  useEffect(() => {
    setConnectorMultiAgentOptimizationProgress(connectorOptimizationProgressDisplay);
  }, [connectorOptimizationProgressDisplay, setConnectorMultiAgentOptimizationProgress]);

  // Form states for creating/editing integration
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formData, setFormData] = useState({ url: '', apiKey: '' });
  const [formLoading, setFormLoading] = useState(false);
  const createIntegrationFormRef = useRef<HTMLDivElement | null>(null);
  const editIntegrationFormRef = useRef<HTMLDivElement | null>(null);
  const createIntegrationValidation = useModalFormValidation(createIntegrationFormRef);
  const editIntegrationValidation = useModalFormValidation(editIntegrationFormRef);

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsCreatedByExpanded, setDetailsCreatedByExpanded] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    isVisible: false,
    type: 'success',
    message: '',
  });

  // Delete confirmation modal state
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    integrationId: string | null;
  }>({
    isOpen: false,
    integrationId: null,
  });

  /** Export optimized step → Integration Configurator: confirm overwrite; if optimizing, offer export-only vs export+stop */
  const [exportConfiguratorModal, setExportConfiguratorModal] = useState<{
    open: boolean;
    mode: 'replace_confirm' | 'while_running';
  }>({ open: false, mode: 'replace_confirm' });
  const pendingExportIterationRef = useRef<any>(null);
  const [exportConfiguratorBusy, setExportConfiguratorBusy] = useState(false);

  // Training job state (used for new inline Training Job button)
  const [trainingJobLoading, setTrainingJobLoading] = useState(false);
  const [trainingJobResult, setTrainingJobResult] = useState<any>(null);
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);
  /** Latest polling job id for stale-response checks (synced with state). */
  const pollingJobIdRef = useRef<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [trainingJobRetryingRowKey, setTrainingJobRetryingRowKey] = useState<string | null>(null);
  const [trainingJobHistory, setTrainingJobHistory] = useState<any[]>([]);
  const [trainingJobHistoryLoading, setTrainingJobHistoryLoading] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollTrainingJobStatusRef = useRef<(jobId: string, opts?: { force?: boolean }) => Promise<void>>(async () => undefined);
  const ftPollingRestoreSkipResetRef = useRef(false);
  const prevPollingJobIdForProgressRef = useRef<string | null>(null);
  const [showTrainingJobModal, setShowTrainingJobModal] = useState(false);
  const [showConfigTuningModal, setShowConfigTuningModal] = useState(false);
  const [configTuningJobSubmitting, setConfigTuningJobSubmitting] = useState(false);
  const [trainingJobForm, setTrainingJobForm] = useState<{
    model_name: string;
    dataset_ids: string[];
    group_by_base_model: boolean;
    merge_train_validation: boolean;
    limit_history: boolean;
    history_depth: number | string;
  }>(() => ({
    model_name: '',
    /** Registry dataset ids (GET /connector/datasets); sent as `dataset_ids` to preview/start. */
    dataset_ids: [] as string[],
    group_by_base_model: true,
    merge_train_validation: true,
    limit_history: true,
    history_depth: DEFAULT_HISTORY_DEPTH,
  }));
  // Agent reactions (like/dislike) for training job - можно выбрать оба одновременно
  const [agentReactions, setAgentReactions] = useState<Record<string, { like: boolean; dislike: boolean }>>({});
  // Дополнительные флаги для агентов: использовать комментарии и groundtruth при обучении
  const [agentFilterFlags, setAgentFilterFlags] = useState<Record<string, { comment: boolean; groundtruth: boolean }>>({});
  // Preview state
  const [previewResult, setPreviewResult] = useState<TrainingPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [datasetAgentSelections, setDatasetAgentSelections] = useState<DatasetAgentTrainingSelection[]>([]);
  const [datasetAgentsPreviewLoading, setDatasetAgentsPreviewLoading] = useState(false);
  const [datasetAgentsPreviewError, setDatasetAgentsPreviewError] = useState<string | null>(null);
  const [datasetAgentsMinExamples, setDatasetAgentsMinExamples] = useState<number | undefined>(undefined);
  const [fineTuningBaseModels, setFineTuningBaseModels] = useState<string[]>([]);
  const [trainingJobPreviewConfirmed, setTrainingJobPreviewConfirmed] = useState(false);
  const trainingJobPreviewAbortRef = useRef<AbortController | null>(null);
  const trainingJobCreateAbortRef = useRef<AbortController | null>(null);
  const datasetAgentsPreviewAbortRef = useRef<AbortController | null>(null);
  // Tracks the integration+datasets+grouping the current selections were built for,
  // so reaction/merge toggles merge fresh counts instead of resetting base models.
  const datasetAgentsPreviewKeyRef = useRef<string>('');
  const trainingJobPreviewPayloadRef = useRef<TrainingJobRequestBody | null>(null);
  const trainingJobModalScrollRef = useRef<HTMLDivElement | null>(null);
  const trainingJobValidation = useModalFormValidation(trainingJobModalScrollRef);
  const [pendingOpenFtJobId, setPendingOpenFtJobId] = useState<string | null>(null);
  const [pendingOpenExperimentId, setPendingOpenExperimentId] = useState<string | null>(null);
  const [datasetsBlockingModalOpen, setDatasetsBlockingModalOpen] = useState(false);

  const isTrainingRequestCanceled = (e: unknown) =>
    (e as { code?: string; name?: string })?.code === 'ERR_CANCELED' ||
    (e as { name?: string })?.name === 'CanceledError';

  const selectedIntegration = useMemo(() => {
    if (!selectedIntegrationId) return null;
    return integrations.find((i) => (i?.id || i?._id) === selectedIntegrationId) || null;
  }, [integrations, selectedIntegrationId]);

  const connectorIntegrationRestoreRef = useRef(false);

  const selectIntegration = useCallback(
    (integrationId: string | null) => {
      setSelectedIntegrationId(integrationId);
      setStoredSelectedIntegrationId('connector', user?.id, integrationId);
    },
    [user?.id]
  );

  const loadMyIntegrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await connector.listMyIntegrations();
      const items = resp?.data ?? resp?.items ?? resp ?? [];
      setIntegrations(Array.isArray(items) ? items : []);
    } catch (e: any) {
      setError(getErrorMessage(e, 'Failed to load integrations'));
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConnectorDatasets = async (integrationId: string) => {
    setDatasetsLoading(true);
    try {
      const resp = await connector.getDatasets(integrationId);
      const datasets = resp?.data ?? [];
      setConnectorDatasets(Array.isArray(datasets) ? datasets : []);
    } catch (e: any) {
      console.error('Failed to load datasets:', e);
      setConnectorDatasets([]);
    } finally {
      setDatasetsLoading(false);
    }
  };

  const resetConnectorEvaluationState = useCallback(() => {
    setConnectorMultiAgentIterationResults([]);
    setConnectorMultiAgentOptimizedPrompts([]);
    setConnectorMultiAgentIsOptimized(false);
    setConnectorMultiAgentSelectedIterationIndex(0);
    clearConnectorMultiAgentChat();
  }, [
    setConnectorMultiAgentIterationResults,
    setConnectorMultiAgentOptimizedPrompts,
    setConnectorMultiAgentIsOptimized,
    setConnectorMultiAgentSelectedIterationIndex,
    clearConnectorMultiAgentChat,
  ]);

  const clearConnectorLegacyOptimizationSession = useCallback(() => {
    if (!unifiedWebSocketService.hasStoredSession()) return;
    const mode = unifiedWebSocketService.getStoredMode();
    const isConnector = unifiedWebSocketService.getStoredIsConnector();
    if (mode === 'multi' && isConnector) {
      unifiedWebSocketService.clearSession();
    }
  }, []);

  const loadConfig = async (integrationId: string) => {
    if (!connectorMultiAgentIsOptimizing) {
      resetConnectorEvaluationState();
    }
    connectorConfigPreservationRef.current = null;
    setConfigLoading(true);
    setConfigError(null);
    try {
      const resp = await connector.getIntegrationConfig(integrationId);
      const { configString, label, dialog } = extractConfigString(resp);
      setConfigLabel(label);
      setConnectorConfigLabel(label);
      setDialogEnvelope(dialog || []);
      const parsed = safeJsonParse(configString);
      if (parsed && typeof parsed === 'object' && parsed.coordinator && Array.isArray(parsed.workers)) {
        connectorConfigPreservationRef.current = parsed as Record<string, unknown>;
        setConfigValue(parsed as AgentConfiguratorState);
        // Синхронизируем загруженные данные в store для configurator (не для evaluation — там только результаты оптимизации)
        if (parsed.coordinator) {
          setConnectorCoordinatorPrompt(parsed.coordinator.prompt || '');
          setConnectorCoordinatorModel(parsed.coordinator.model || 'gpt-4.1-mini');
          setConnectorCoordinatorTemperature(parsed.coordinator.temperature ?? 0.8);
          setConnectorCoordinatorDescription(parsed.coordinator.description || '');
          setConnectorCoordinatorReminderPrompt(
            typeof parsed.coordinator.reminder_prompt === 'string'
              ? parsed.coordinator.reminder_prompt
              : ''
          );
          setConnectorCoordinatorName(
            typeof parsed.coordinator.name === 'string' ? parsed.coordinator.name : 'Coordinator'
          );
          setConnectorCoordinatorBackendId(
            typeof parsed.coordinator.id === 'string' ? parsed.coordinator.id.trim() || undefined : undefined
          );
          setConnectorCoordinatorAgentId(
            typeof parsed.coordinator.agent_id === 'string'
              ? parsed.coordinator.agent_id.trim() || undefined
              : typeof parsed.coordinator.id === 'string'
                ? parsed.coordinator.id.trim() || undefined
                : undefined
          );
        }
        if (parsed.workers && Array.isArray(parsed.workers)) {
          setConnectorWorkers(
            parsed.workers.map((w: any, index: number) => ({
              id: normalizeWorkerIdString(w?.id, index),
              name: w.name || '',
              prompt: w.prompt || '',
              model: w.model || 'gpt-4.1-mini',
              temperature: w.temperature ?? 0.7,
              description: w.description || '',
              mode: (w.mode === 'asynchronous' ? 'asynchronous' : 'synchronous') as 'synchronous' | 'asynchronous',
              reminder_prompt:
                typeof w.reminder_prompt === 'string' ? w.reminder_prompt : undefined,
            }))
          );
        }
        setConnectorMetaPrompts(mergeMetaPromptsWithFinalStepDefault(parsed.metaPrompts));
      } else {
        connectorConfigPreservationRef.current = null;
        setConfigValue(null);
      }
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 403) {
        setConfigError('403 Forbidden: you do not have access to this integration.');
      } else {
        setConfigError(e?.message || 'Failed to load integration config');
      }
      connectorConfigPreservationRef.current = null;
      setConfigValue(null);
    } finally {
      setConfigLoading(false);
    }
  };

  // Автоматически выбираем интеграцию при открытии онбординга на вкладке integrations
  // ВАЖНО: НЕ переключаем таб - каждая вкладка имеет свой onboarding
  useEffect(() => {
    if (showOnboarding && activeTab === 'integrations') {
      // Ищем и выбираем интеграцию с URL https://dev.sdk.aipsy.tech
      // Ждем, пока интеграции загрузятся
      if (integrations.length > 0 && !loading) {
        const targetUrl = 'https://dev.sdk.aipsy.tech';
        const targetIntegration = integrations.find(
          (integration) => {
            const url = integration?.url || integration?.data?.url || integration?.endpoint || '';
            // Нормализуем URL для сравнения (убираем trailing slash)
            const normalizedUrl = url.replace(/\/$/, '');
            const normalizedTarget = targetUrl.replace(/\/$/, '');
            return normalizedUrl === normalizedTarget;
          }
        );
        
        if (targetIntegration) {
          const integrationId = String(targetIntegration?.id || targetIntegration?._id || '');
          if (integrationId && integrationId !== selectedIntegrationId) {
            selectIntegration(integrationId);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnboarding, activeTab, integrations, loading]);

  const getEffectiveElapsedSeconds = (startTime: number): number => {
    let pausedMs = progressPausedMsRef.current;
    if (progressHiddenAtRef.current !== null) {
      pausedMs += Date.now() - progressHiddenAtRef.current;
    }
    return Math.max(0, getElapsedSeconds(startTime) - pausedMs / 1000);
  };

  const resetProgressPauseTracking = () => {
    progressPausedMsRef.current = 0;
    progressHiddenAtRef.current = null;
  };

  const applyConnectorTimeBasedProgress = useCallback(() => {
    if (connectorMultiAgentProgressStartTime === null) return;
    const stageProgress = calculateExponentialProgress(
      getEffectiveElapsedSeconds(connectorMultiAgentProgressStartTime),
      connectorMultiAgentExpectedIterationTime
    );
    const totalProgress = calculateTotalProgress(
      connectorMultiAgentCurrentProgressStage,
      optimizationIterations,
      stageProgress
    );
    setSmoothProgressTarget(totalProgress);
  }, [
    connectorMultiAgentProgressStartTime,
    connectorMultiAgentExpectedIterationTime,
    connectorMultiAgentCurrentProgressStage,
    optimizationIterations,
    setSmoothProgressTarget,
  ]);

  const handleOptimizationTabLeave = useCallback(() => {
    if (!connectorMultiAgentIsOptimizing) return;
    if (progressHiddenAtRef.current === null) {
      progressHiddenAtRef.current = Date.now();
    }
  }, [connectorMultiAgentIsOptimizing]);

  const handleOptimizationTabReturn = useCallback(() => {
    if (!connectorMultiAgentIsOptimizing) return;

    if (progressHiddenAtRef.current !== null) {
      progressPausedMsRef.current += Date.now() - progressHiddenAtRef.current;
      progressHiddenAtRef.current = null;
    }

    if (optimizationJobIdRef.current || connectorOptimizationJobId) {
      void optimizationJobPollFnRef.current?.();
      return;
    }

    applyConnectorTimeBasedProgress();
  }, [
    connectorMultiAgentIsOptimizing,
    connectorOptimizationJobId,
    applyConnectorTimeBasedProgress,
  ]);

  useEffect(() => {
    void loadMyIntegrations();
    void loadDefaults();
    
    // Config Tuning uses Jobs API — clear any stale legacy WebSocket session instead of restoring it.
    const checkAndRestoreSession = async () => {
      clearConnectorLegacyOptimizationSession();

      if (hasAnyPersistedOptimizationJobId()) {
        return;
      }

      if (!hasAnyPersistedOptimizationJobId()) {
        // Нет Jobs API polling — сбрасываем только активный прогон.
        setConnectorMultiAgentIsOptimizing(false);
        setConnectorMultiAgentProgressStartTime(null);
        setConnectorMultiAgentCurrentProgressStage(0);
        resetSmoothProgress();
        setShowCompletionMessage(false);
        // Stale evaluation from a previous visit — clear unless Profile just queued a load.
        if (connectorMultiAgentIterationResults.length > 0 && !hasConnectorPendingConfig()) {
          resetConnectorEvaluationState();
        }
      }
    };
    
    void checkAndRestoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearConnectorLegacyOptimizationSession]);

  // Восстанавливаем последнюю выбранную интеграцию после загрузки списка
  useEffect(() => {
    if (loading || integrations.length === 0) return;
    if (connectorIntegrationRestoreRef.current) return;
    if (searchParams.get('integrationId')) {
      connectorIntegrationRestoreRef.current = true;
      return;
    }

    connectorIntegrationRestoreRef.current = true;

    setSelectedIntegrationId((current) => {
      if (current) return current;
      return resolveStoredIntegrationId('connector', user?.id, integrations);
    });
  }, [loading, integrations, searchParams, user?.id]);

  // Проверяем URL параметры integrationId / ftJobId при загрузке
  useEffect(() => {
    const urlIntegrationId = searchParams.get('integrationId');
    const urlFtJobId = searchParams.get('ftJobId');
    if (!urlIntegrationId && !urlFtJobId) return;
    if (integrations.length === 0) return;

    const clearUrlParams = () => {
      const nextParams = new URLSearchParams(searchParams);
      if (urlIntegrationId) nextParams.delete('integrationId');
      if (urlFtJobId) nextParams.delete('ftJobId');
      setSearchParams(nextParams, { replace: true });
    };

    if (urlIntegrationId) {
      const integration = integrations.find((i) => String(i?.id || i?._id) === urlIntegrationId);
      if (!integration) return;
      if (selectedIntegrationId !== urlIntegrationId) {
        selectIntegration(urlIntegrationId);
      }
    }

    if (urlFtJobId) {
      setPendingOpenFtJobId(urlFtJobId);
      clearUrlParams();
      return;
    }

    const pendingConfig = takeConnectorPendingConfig();
    if (pendingConfig) {
      connectorConfigPreservationRef.current = pendingConfig as Record<string, unknown>;
      setConfigValue(pendingConfig);
      setCurrentConfig(pendingConfig);
      setConfigLabel(connectorConfigLabel || undefined);
      configLoadedFromStoreRef.current = true;
    }

    clearUrlParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    integrations,
    searchParams,
    selectedIntegrationId,
    connectorConfigLabel,
  ]);

  useEffect(() => {
    if (!selectedIntegrationId || !pendingOpenFtJobId) return;
    let cancelled = false;
    (async () => {
      try {
        setActiveTab('integrations');
        const resp = await connector.getTrainingJob(pendingOpenFtJobId, selectedIntegrationId);
        if (cancelled) return;
        const liveRow = buildTrainingJobLiveRow(resp, pendingOpenFtJobId);
        trainingJobLiveSnapshotsRef.current.set(pendingOpenFtJobId, liveRow);
        setTrainingJobHistory((prev) =>
          mergeTrainingJobHistoryRows(prev, trainingJobLiveSnapshotsRef.current)
        );
        setTrainingJobResult({ data: liveRow });
        const status = resolveEffectiveTrainingJobStatus(liveRow);
        const progressPercent =
          typeof liveRow.progress_percent === 'number' ? liveRow.progress_percent : undefined;
        if (status === 'running' || status === 'queued' || status === 'pending') {
          const isSameTrackedJob = pollingJobIdRef.current === pendingOpenFtJobId;
          ftPollingRestoreSkipResetRef.current = true;
          if (progressPercent != null) {
            syncTrainingJobsApiProgress(progressPercent);
          }
          writePersistedConnectorFtPolling(selectedIntegrationId, pendingOpenFtJobId, progressPercent ?? null);
          if (!isSameTrackedJob) {
            setPollingJobId(pendingOpenFtJobId);
          }
          pollingJobIdRef.current = pendingOpenFtJobId;
        }
        setTimeout(() => {
          document.getElementById('training-job-result-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 120);
      } catch (e: any) {
        if (!cancelled) {
          setNotification({
            isVisible: true,
            type: 'error',
            message: getErrorMessage(e, 'Failed to open fine-tuning job details'),
          });
        }
      } finally {
        if (!cancelled) setPendingOpenFtJobId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedIntegrationId, pendingOpenFtJobId, setActiveTab]);

  useEffect(() => {
    if (!selectedIntegrationId) return;
    
    // Загружаем конфигурацию с сервера только если configValue еще не установлен из store
    // Это позволяет использовать данные, загруженные из истории оптимизации
    if (!configLoadedFromStoreRef.current && (!configValue || !configValue.coordinator?.prompt)) {
      void loadConfig(selectedIntegrationId);
    } else {
      // Сбрасываем флаг после использования, чтобы при следующем выборе интеграции загружалась с сервера
      configLoadedFromStoreRef.current = false;
    }
    
    void loadConnectorDatasets(selectedIntegrationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIntegrationId]);

  // Автообновление списка датасетов при возврате на вкладку (без перезагрузки страницы)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && selectedIntegrationId) {
        void loadConnectorDatasets(selectedIntegrationId);
      }
      if (document.visibilityState === 'visible') {
        handleOptimizationTabReturn();
      } else if (document.visibilityState === 'hidden') {
        handleOptimizationTabLeave();
      }
    };
    const onFocus = () => {
      handleOptimizationTabReturn();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [selectedIntegrationId, handleOptimizationTabReturn, handleOptimizationTabLeave]);

  // Sync currentConfig with configValue
  useEffect(() => {
    setCurrentConfig(configValue);
  }, [configValue]);

  // Датасеты только из GET /connector/datasets (без захардкоженных / store defaults)
  const allAvailableDatasets = useMemo(() => {
    const seen = new Set<string>();
    const labels: string[] = [];
    for (const ds of connectorDatasets) {
      const label = (ds.label || '').trim();
      if (label && !seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    }
    return labels;
  }, [connectorDatasets]);

  const resolveConnectorOptimizationDatasets = (): string[] =>
    optimizationDatasets.filter((d) => d.trim());

  const canRunConnectorOptimization = resolveConnectorOptimizationDatasets().length > 0;

  // Синхронизация выбора при смене списка датасетов интеграции (без optimizationDatasets в deps — иначе цикл setState).
  const allAvailableDatasetsKey = allAvailableDatasets.join('\u001f');
  useEffect(() => {
    setOptimizationDatasets((prev) => {
      if (allAvailableDatasets.length === 0) {
        return prev.length === 0 ? prev : [];
      }
      const valid = prev.filter((d) => allAvailableDatasets.includes(d));
      const next = valid.length > 0 ? valid : [allAvailableDatasets[0]];
      if (next.length === prev.length && next.every((d, i) => d === prev[i])) {
        return prev;
      }
      return next;
    });
  }, [allAvailableDatasetsKey, allAvailableDatasets]);

  // Автоматически скроллим вниз при выборе интеграции
  useEffect(() => {
    if (selectedIntegrationId && activeTab === 'integrations') {
      setTimeout(() => {
        const configSection = document.getElementById('connector-config-section');
        if (configSection) {
          configSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300); // Небольшая задержка для загрузки конфигурации
    }
  }, [selectedIntegrationId, activeTab]);

  useEffect(() => {
    setDetailsCreatedByExpanded(false);
  }, [detailsData]);

  // Модели для конфигуратора: из Profile → Models (localStorage) для выбранной интеграции
  const configuratorAvailableModels = useMemo(
    () => getAvailableModelsForConfigurator(selectedIntegrationId || ''),
    [selectedIntegrationId]
  );
  // Читаемые имена для ft:… id (из Profile → Models), чтобы конфигуратор не показывал технический id
  const configuratorModelLabels = useMemo(
    () => getConfiguratorModelLabels(selectedIntegrationId || ''),
    [selectedIntegrationId]
  );

  // Получаем список доступных моделей из конфигуратора (для подсказок по base_model)
  const availableModels = useMemo(() => {
    const models: string[] = [];
    if (currentConfig?.coordinator?.model) {
      models.push(currentConfig.coordinator.model);
    }
    if (currentConfig?.workers && Array.isArray(currentConfig.workers)) {
      currentConfig.workers.forEach((worker) => {
        if (worker.model && !models.includes(worker.model)) {
          models.push(worker.model);
        }
      });
    }
    return models.length > 0 ? models : configuratorAvailableModels;
  }, [currentConfig, configuratorAvailableModels]);

  const invalidateTrainingJobPreview = useCallback(() => {
    setTrainingJobPreviewConfirmed(false);
    trainingJobPreviewPayloadRef.current = null;
    setPreviewResult(null);
  }, []);

  const loadFineTuningBaseModels = useCallback(async () => {
    if (!selectedIntegrationId) {
      setFineTuningBaseModels(listFineTuningBaseModelOptions([], configuratorAvailableModels));
      return;
    }
    try {
      const resp = await connector.listTrainableModels({
        integration_id: selectedIntegrationId,
        limit: 200,
      });
      const items = resp?.data?.items ?? (resp as { items?: import('../types/api').TrainableModelItem[] }).items ?? [];
      setFineTuningBaseModels(
        listFineTuningBaseModelOptions(Array.isArray(items) ? items : [], configuratorAvailableModels)
      );
    } catch {
      setFineTuningBaseModels(listFineTuningBaseModelOptions([], configuratorAvailableModels));
    }
  }, [selectedIntegrationId, configuratorAvailableModels]);

  useEffect(() => {
    if (!showTrainingJobModal || !selectedIntegrationId) return;
    void loadFineTuningBaseModels();
  }, [showTrainingJobModal, selectedIntegrationId, loadFineTuningBaseModels]);

  useEffect(() => {
    if (!showTrainingJobModal || !selectedIntegrationId) return;
    if (trainingJobForm.dataset_ids.length === 0) {
      datasetAgentsPreviewAbortRef.current?.abort();
      datasetAgentsPreviewKeyRef.current = '';
      setDatasetAgentSelections([]);
      setDatasetAgentsPreviewError(null);
      setDatasetAgentsMinExamples(undefined);
      setDatasetAgentsPreviewLoading(false);
      return;
    }

    const datasetIds = [...trainingJobForm.dataset_ids];
    const groupByBaseModel = trainingJobForm.group_by_base_model;
    const datasetKey = `${selectedIntegrationId}\u001f${datasetIds.join(',')}\u001f${groupByBaseModel ? 1 : 0}`;
    // Reaction/merge toggles keep the same agent set, so merge fresh counts into the
    // existing selections; only a new integration/dataset/grouping rebuilds from scratch.
    const isRebuild = datasetKey !== datasetAgentsPreviewKeyRef.current;
    const { filters, agent_filters } = buildDatasetAgentsFilterPayload(agentReactions, agentFilterFlags);

    datasetAgentsPreviewAbortRef.current?.abort();
    const ac = new AbortController();
    datasetAgentsPreviewAbortRef.current = ac;
    setDatasetAgentsPreviewLoading(true);
    setDatasetAgentsPreviewError(null);
    invalidateTrainingJobPreview();

    void connector
      .previewDatasetAgents(
        {
          integration_id: selectedIntegrationId,
          dataset_ids: datasetIds,
          merge_train_validation: Boolean(trainingJobForm.merge_train_validation),
          filters,
          agent_filters,
        },
        { signal: ac.signal }
      )
      .then((resp) => {
        if (datasetAgentsPreviewAbortRef.current !== ac) return;
        const preview = (resp?.data ?? resp) as DatasetAgentsPreviewResponse;
        setDatasetAgentsMinExamples(
          typeof preview.min_examples === 'number' ? preview.min_examples : undefined
        );
        if (isRebuild) {
          const defaultBaseModel = fineTuningBaseModels[0] || configuratorAvailableModels[0] || 'gpt-4.1-mini';
          setDatasetAgentSelections(
            buildDatasetAgentSelectionsFromPreview(preview, defaultBaseModel, groupByBaseModel)
          );
        } else {
          setDatasetAgentSelections((prev) =>
            applyDatasetAgentPreviewToSelections(prev, preview, groupByBaseModel)
          );
        }
        datasetAgentsPreviewKeyRef.current = datasetKey;
      })
      .catch((e: unknown) => {
        if ((e as { code?: string; name?: string })?.code === 'ERR_CANCELED') return;
        if ((e as { name?: string })?.name === 'CanceledError') return;
        if (datasetAgentsPreviewAbortRef.current !== ac) return;
        if (isRebuild) setDatasetAgentSelections([]);
        setDatasetAgentsPreviewError(
          formatJobErrorMessage(
            getErrorMessage(e, 'Failed to load agents from datasets'),
            'Failed to load agents from datasets'
          )
        );
      })
      .finally(() => {
        if (datasetAgentsPreviewAbortRef.current === ac) {
          setDatasetAgentsPreviewLoading(false);
          datasetAgentsPreviewAbortRef.current = null;
        }
      });

    return () => {
      ac.abort();
    };
  }, [
    showTrainingJobModal,
    selectedIntegrationId,
    trainingJobForm.dataset_ids.join('\u001f'),
    trainingJobForm.group_by_base_model,
    trainingJobForm.merge_train_validation,
    agentReactions,
    agentFilterFlags,
    fineTuningBaseModels,
    configuratorAvailableModels,
    invalidateTrainingJobPreview,
  ]);

  useEffect(() => {
    if (!showTrainingJobModal || datasetAgentSelections.length === 0) return;
    const defaultBaseModel = fineTuningBaseModels[0] || configuratorAvailableModels[0] || '';
    if (!defaultBaseModel) return;
    setDatasetAgentSelections((prev) =>
      recalculateMissingAgentGroupEligibility(
        prev.map((row) => (row.base_model.trim() ? row : { ...row, base_model: defaultBaseModel })),
        datasetAgentsMinExamples,
        trainingJobForm.group_by_base_model
      )
    );
  }, [
    fineTuningBaseModels,
    configuratorAvailableModels,
    showTrainingJobModal,
    datasetAgentSelections.length,
    datasetAgentsMinExamples,
    trainingJobForm.group_by_base_model,
  ]);

  useEffect(() => {
    if (!showTrainingJobModal || datasetAgentSelections.length === 0) return;
    setDatasetAgentSelections((prev) =>
      recalculateMissingAgentGroupEligibility(
        prev,
        datasetAgentsMinExamples,
        trainingJobForm.group_by_base_model
      )
    );
  }, [showTrainingJobModal, trainingJobForm.group_by_base_model, datasetAgentsMinExamples, datasetAgentSelections.length]);

  useEffect(() => {
    pollingJobIdRef.current = pollingJobId;
  }, [pollingJobId]);

  // Функция для поллинга статуса training job
  const pollTrainingJobStatus = async (jobId: string, opts?: { force?: boolean }) => {
    if ((!opts?.force && isPolling) || !selectedIntegrationId) return;
    setIsPolling(true);
    try {
      const response = await connector.pollTrainingJob(jobId, selectedIntegrationId);
      const jobData = response?.data || response;
      const enriched = enrichTrainingJobRowForDisplay(jobData);
      const resolvedJobId = String(enriched.id || jobId).trim();
      if (resolvedJobId) {
        trainingJobLiveSnapshotsRef.current.set(resolvedJobId, enriched);
        setTrainingJobHistory((prev) =>
          mergeTrainingJobHistoryRows(prev, trainingJobLiveSnapshotsRef.current)
        );
      }
      /** Live interval polls only update UI / stop polling for the job we are tracking. */
      const stillTrackedLive =
        pollingJobIdRef.current != null && pollingJobIdRef.current === jobId;
      if (opts?.force || stillTrackedLive) {
        setTrainingJobResult(response);
        const status = resolveEffectiveTrainingJobStatus(enriched);
        const active = isActiveJobStatus(status);
        const progressPercent =
          typeof enriched.progress_percent === 'number' ? enriched.progress_percent : jobData?.progress_percent;
        if (status === 'succeeded' || status === 'completed') {
          completeTrainingJobsApiProgress();
        } else if (active && progressPercent != null) {
          syncTrainingJobsApiProgress(progressPercent);
          if (selectedIntegrationId) {
            writePersistedConnectorFtPolling(selectedIntegrationId, resolvedJobId || jobId, progressPercent);
          }
        } else if (!active) {
          resetTrainingJobsApiProgress();
        }
        if (!active) {
          clearConnectorFtPollingPersist(selectedIntegrationId);
          setPollingJobId(null);
          pollingJobIdRef.current = null;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setTrainingJobCancellingId((current) =>
            clearJobCancellationPendingId(current, resolvedJobId || jobId)
          );
          void loadTrainingJobHistory();
        }
      }
    } catch (e: any) {
      console.error('Failed to poll training job status:', e);
      if (pollingJobIdRef.current === jobId) {
        clearConnectorFtPollingPersist(selectedIntegrationId);
        setPollingJobId(null);
        pollingJobIdRef.current = null;
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } finally {
      setIsPolling(false);
    }
  };

  pollTrainingJobStatusRef.current = pollTrainingJobStatus;

  const loadTrainingJobHistory = useCallback(async () => {
    if (!selectedIntegrationId) {
      setTrainingJobHistory([]);
      return;
    }
    setTrainingJobHistoryLoading(true);
    try {
      const resp = await connector.listTrainingJobs({
        integration_id: selectedIntegrationId,
        limit: 20,
        offset: 0,
      });
      const items = resp?.data?.items ?? (resp as { items?: unknown[] })?.items ?? [];
      const normalized = (Array.isArray(items) ? items : []).map((row) =>
        row && typeof row === 'object' && 'data' in row && (row as { data?: unknown }).data
          ? (row as { data: unknown }).data
          : row
      );
      setTrainingJobHistory(mergeTrainingJobHistoryRows(normalized, trainingJobLiveSnapshotsRef.current));
    } catch (e) {
      console.error('Failed to load fine-tuning job history:', e);
    } finally {
      setTrainingJobHistoryLoading(false);
    }
  }, [selectedIntegrationId]);

  useEffect(() => {
    void loadTrainingJobHistory();
  }, [loadTrainingJobHistory]);

  const loadOptimizationJobHistory = useCallback(async () => {
    if (!selectedIntegrationId) {
      setOptimizationJobHistory([]);
      return;
    }
    setOptimizationJobHistoryLoading(true);
    try {
      const resp = await autoTune.listOptimizationJobs({ limit: 20, offset: 0 });
      const items = resp?.data?.items ?? (resp as { items?: unknown[] })?.items ?? [];
      const normalized = (Array.isArray(items) ? items : []).map((row) => unwrapOptimizationJobRow(row));
      const forIntegration = normalized.filter(
        (job) => String((job as { integration_id?: string })?.integration_id || '') === selectedIntegrationId
      );
      setOptimizationJobHistory(
        mergeOptimizationJobHistoryRows(
          forIntegration,
          optimizationJobLiveSnapshotsRef.current,
          optimizationIterations
        )
      );
    } catch (e) {
      console.error('Failed to load config tuning job history:', e);
    } finally {
      setOptimizationJobHistoryLoading(false);
    }
  }, [selectedIntegrationId, optimizationIterations]);

  useEffect(() => {
    void loadOptimizationJobHistory();
  }, [loadOptimizationJobHistory]);

  useEffect(() => {
    evaluationPollingJobIdRef.current = evaluationPollingJobId;
  }, [evaluationPollingJobId]);

  useEffect(() => {
    evaluationJobHistoryRef.current = evaluationJobHistory;
  }, [evaluationJobHistory]);

  const loadEvaluationJobHistory = useCallback(async () => {
    if (!selectedIntegrationId) {
      setEvaluationJobHistory([]);
      return;
    }
    setEvaluationJobHistoryLoading(true);
    try {
      const resp = await autoTune.listEvaluationJobs({ limit: 20, offset: 0 });
      const items = resp?.data?.items ?? (resp as { items?: unknown[] })?.items ?? [];
      const normalized = (Array.isArray(items) ? items : []).map((row) => unwrapEvaluationJobRow(row));
      const forIntegration = normalized.filter(
        (job) => String(job.integration_id || '') === selectedIntegrationId
      );
      setEvaluationJobHistory(
        mergeEvaluationJobHistoryRows(forIntegration, evaluationJobLiveSnapshotsRef.current)
      );
    } catch (e) {
      console.error('Failed to load evaluation job history:', e);
    } finally {
      setEvaluationJobHistoryLoading(false);
    }
  }, [selectedIntegrationId]);

  useEffect(() => {
    void loadEvaluationJobHistory();
  }, [loadEvaluationJobHistory]);

  const collectActiveEvaluationJobIds = useCallback(
    (extraJobId?: string | null): string[] => {
      const ids = new Set<string>();
      if (extraJobId?.trim()) ids.add(extraJobId.trim());
      const tracked = evaluationPollingJobIdRef.current;
      if (tracked) ids.add(tracked);
      for (const row of evaluationJobHistoryRef.current) {
        const jobRow = unwrapEvaluationJobRow(row);
        const id = String(jobRow.id || '').trim();
        if (!id) continue;
        if (selectedIntegrationId && String(jobRow.integration_id || '') !== selectedIntegrationId) {
          continue;
        }
        if (shouldTrackEvaluationJobRow(jobRow)) ids.add(id);
      }
      return Array.from(ids);
    },
    [selectedIntegrationId]
  );

  const pickPrimaryEvaluationJobId = useCallback(
    (excludeJobId?: string): string | null => {
      const ids = collectActiveEvaluationJobIds().filter((id) => id !== excludeJobId);
      if (ids.length === 0) return null;

      const resolveSnap = (id: string): Record<string, unknown> | null => {
        const live = evaluationJobLiveSnapshotsRef.current.get(id);
        const fromHistory = evaluationJobHistoryRef.current.find(
          (row) => String(unwrapEvaluationJobRow(row).id || '').trim() === id
        );
        return live ?? (fromHistory ? unwrapEvaluationJobRow(fromHistory) : null);
      };

      let bestStartedId: string | null = null;
      let bestStartedProgress = -1;

      for (const id of ids) {
        const snap = resolveSnap(id);
        if (!snap || !hasEvaluationJobExecutionStarted(snap)) continue;
        const progress = resolveEvaluationJobProgressDisplay(snap).percent;
        if (progress > bestStartedProgress) {
          bestStartedProgress = progress;
          bestStartedId = id;
        }
      }

      if (bestStartedId) return bestStartedId;
      return ids[0];
    },
    [collectActiveEvaluationJobIds]
  );

  const applyPrimaryEvaluationJobUpdate = useCallback(
    (jobRow: Record<string, unknown>) => {
      setEvaluationJobSnapshot(jobRow as unknown as EvaluationJob);
      const status = resolveEvaluationJobRawStatus(jobRow);
      const active = isActiveEvaluationJobStatus(status);
      const progressDisplay = resolveEvaluationJobProgressDisplay(jobRow);
      if (status === 'succeeded') {
        completeEvaluationJobsApiProgress();
      } else if (active) {
        syncEvaluationJobsApiProgress(progressDisplay.percent);
      } else {
        resetEvaluationJobsApiProgress();
      }
    },
    [completeEvaluationJobsApiProgress, resetEvaluationJobsApiProgress, syncEvaluationJobsApiProgress]
  );

  const promotePrimaryEvaluationJob = useCallback(
    (excludeCompletedId?: string): boolean => {
      const next = pickPrimaryEvaluationJobId(excludeCompletedId);
      if (!next) {
        clearPersistedEvaluationJobId(selectedIntegrationId);
        setEvaluationPollingJobId(null);
        evaluationPollingJobIdRef.current = null;
        setEvaluationJobSnapshot(null);
        resetEvaluationJobsApiProgress();
        return false;
      }

      if (evaluationPollingJobIdRef.current !== next) {
        evaluationPollingJobIdRef.current = next;
        setEvaluationPollingJobId(next);
        if (selectedIntegrationId) persistEvaluationJobId(selectedIntegrationId, next);
        const nextSnap = evaluationJobLiveSnapshotsRef.current.get(next);
        if (nextSnap) {
          syncEvaluationJobsApiProgress(resolveEvaluationJobProgressDisplay(nextSnap).percent);
        } else {
          resetEvaluationJobsApiProgress();
        }
      }

      const snap = evaluationJobLiveSnapshotsRef.current.get(next);
      if (snap) applyPrimaryEvaluationJobUpdate(snap);
      return true;
    },
    [
      applyPrimaryEvaluationJobUpdate,
      pickPrimaryEvaluationJobId,
      resetEvaluationJobsApiProgress,
      selectedIntegrationId,
      syncEvaluationJobsApiProgress,
    ]
  );

  const pollActiveEvaluationJobs = useCallback(
    async (opts?: { force?: boolean; includeJobId?: string }) => {
      if (!selectedIntegrationId) return;
      if (!opts?.force && evaluationJobPollInFlightRef.current) return;

      const jobIds = collectActiveEvaluationJobIds(opts?.includeJobId);
      if (jobIds.length === 0) {
        promotePrimaryEvaluationJob();
        return;
      }

      evaluationJobPollInFlightRef.current = true;
      try {
        const results = await Promise.all(
          jobIds.map(async (jobId) => {
            try {
              const response = await autoTune.getEvaluationJob(jobId);
              const jobRow = unwrapEvaluationJobRow(response?.data ?? response);
              const resolvedJobId = String(jobRow.id || jobId).trim();
              if (resolvedJobId) {
                evaluationJobLiveSnapshotsRef.current.set(resolvedJobId, jobRow);
              }
              return { jobId: resolvedJobId || jobId, jobRow, error: null as unknown };
            } catch (error) {
              return { jobId, jobRow: null as Record<string, unknown> | null, error };
            }
          })
        );

        setEvaluationJobHistory((prev) =>
          mergeEvaluationJobHistoryRows(prev, evaluationJobLiveSnapshotsRef.current)
        );

        for (const { jobId, jobRow, error } of results) {
          if (error || !jobRow) continue;
          if (evaluationDetailsJobIdRef.current === jobId) {
            setEvaluationDetailsJob(jobRow as unknown as EvaluationJob);
          }
          const status = resolveEvaluationJobRawStatus(jobRow);
          if (!isActiveEvaluationJobStatus(status)) {
            setEvaluationJobCancellingId((current) => clearJobCancellationPendingId(current, jobId));
          }
        }

        const primaryId = evaluationPollingJobIdRef.current;
        const primaryResult = primaryId ? results.find((row) => row.jobId === primaryId) : null;

        if (primaryId && primaryResult?.jobRow) {
          const status = resolveEvaluationJobRawStatus(primaryResult.jobRow);
          const active = isActiveEvaluationJobStatus(status);
          applyPrimaryEvaluationJobUpdate(primaryResult.jobRow);
          if (!active) {
            promotePrimaryEvaluationJob(primaryId);
            void loadEvaluationJobHistory();
          }
        } else if (!primaryId) {
          promotePrimaryEvaluationJob();
        }
      } catch (e: unknown) {
        console.error('Failed to poll evaluation jobs:', e);
      } finally {
        evaluationJobPollInFlightRef.current = false;
      }
    },
    [
      applyPrimaryEvaluationJobUpdate,
      collectActiveEvaluationJobIds,
      loadEvaluationJobHistory,
      promotePrimaryEvaluationJob,
      selectedIntegrationId,
    ]
  );

  pollActiveEvaluationJobsRef.current = pollActiveEvaluationJobs;

  const pollEvaluationJobStatus = useCallback(
    async (jobId: string, opts?: { force?: boolean }) => {
      await pollActiveEvaluationJobs({ force: opts?.force, includeJobId: jobId });
    },
    [pollActiveEvaluationJobs]
  );

  pollEvaluationJobStatusRef.current = pollEvaluationJobStatus;

  const handleCloseEvaluationJobDetails = useCallback(() => {
    setEvaluationDetailsModalOpen(false);
    evaluationDetailsJobIdRef.current = null;
    setEvaluationDetailsJob(null);
    setEvaluationDetailsLoading(false);
  }, []);

  const handleOpenEvaluationJobDetails = useCallback(
    async (jobId: string, fallbackRow?: Record<string, unknown>) => {
      const trimmed = jobId.trim();
      if (!trimmed) return;
      setEvaluationDetailsModalOpen(true);
      setEvaluationDetailsLoading(true);
      evaluationDetailsJobIdRef.current = trimmed;
      if (fallbackRow) {
        setEvaluationDetailsJob(fallbackRow as unknown as EvaluationJob);
      } else {
        setEvaluationDetailsJob(null);
      }
      try {
        const response = await autoTune.getEvaluationJob(trimmed);
        const jobRow = unwrapEvaluationJobRow(response?.data ?? response);
        const resolvedJobId = String(jobRow.id || trimmed).trim();
        if (resolvedJobId) {
          evaluationJobLiveSnapshotsRef.current.set(resolvedJobId, jobRow);
          setEvaluationJobHistory((prev) =>
            mergeEvaluationJobHistoryRows(prev, evaluationJobLiveSnapshotsRef.current)
          );
        }
        setEvaluationDetailsJob(jobRow as unknown as EvaluationJob);
        setEvaluationJobSnapshot(jobRow as unknown as EvaluationJob);
        if (shouldTrackEvaluationJobRow(jobRow)) {
          const isAlreadyTrackedJob = evaluationPollingJobIdRef.current === resolvedJobId;
          if (isAlreadyTrackedJob) {
            // Same job already being polled: keep the running progress, just align to the
            // latest backend percent so opening Details never restarts the animation.
            syncEvaluationJobsApiProgress(resolveEvaluationJobProgressDisplay(jobRow).percent);
          } else {
            resetEvaluationJobsApiProgress();
            setEvaluationPollingJobId(resolvedJobId);
            evaluationPollingJobIdRef.current = resolvedJobId;
            if (selectedIntegrationId) persistEvaluationJobId(selectedIntegrationId, resolvedJobId);
          }
        }
      } catch (e: unknown) {
        setNotification({
          isVisible: true,
          type: 'error',
          message: getErrorMessage(e, 'Failed to load evaluation job details.'),
        });
        setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
      } finally {
        setEvaluationDetailsLoading(false);
      }
    },
    [resetEvaluationJobsApiProgress, syncEvaluationJobsApiProgress, selectedIntegrationId]
  );

  const handleOpenEvaluationRunAgain = useCallback(
    (jobRow: Record<string, unknown>, jobId?: string) => {
      setEvaluationRunAgainSeed(parseEvaluationRunAgainSeed(jobRow));
      setEvaluationRunAgainKey(jobId?.trim() || String(jobRow.id || '').trim() || `eval-run-again-${Date.now()}`);
      setShowEvaluationRunModal(true);
    },
    []
  );

  const handleCloseEvaluationRunModal = useCallback(() => {
    setShowEvaluationRunModal(false);
    setEvaluationRunAgainSeed(null);
    setEvaluationRunAgainKey(null);
  }, []);

  const handleEvaluationJobStarted = useCallback(
    (jobId: string) => {
      const trimmed = jobId.trim();
      if (!trimmed || !selectedIntegrationId) return;

      const currentPrimary = evaluationPollingJobIdRef.current;
      const resolveEvaluationJobSnapById = (id: string): Record<string, unknown> | null => {
        const live = evaluationJobLiveSnapshotsRef.current.get(id);
        if (live) return live;
        const fromHistory = evaluationJobHistoryRef.current.find(
          (row) => String(unwrapEvaluationJobRow(row).id || '').trim() === id
        );
        return fromHistory ? unwrapEvaluationJobRow(fromHistory) : null;
      };
      const currentSnap = currentPrimary ? resolveEvaluationJobSnapById(currentPrimary) : null;
      const keepCurrentPrimary = Boolean(
        currentPrimary &&
          currentPrimary !== trimmed &&
          currentSnap &&
          shouldTrackEvaluationJobRow(currentSnap) &&
          hasEvaluationJobExecutionStarted(currentSnap)
      );

      if (!keepCurrentPrimary) {
        resetEvaluationJobsApiProgress();
        setEvaluationPollingJobId(trimmed);
        evaluationPollingJobIdRef.current = trimmed;
        persistEvaluationJobId(selectedIntegrationId, trimmed);
        setEvaluationJobSnapshot(null);
      }

      setNotification({
        isVisible: true,
        type: 'success',
        message: keepCurrentPrimary
          ? 'Evaluation queued. The current run will continue first.'
          : 'Evaluation job started.',
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
      void loadEvaluationJobHistory();
      void pollActiveEvaluationJobs({ force: true, includeJobId: trimmed });
      requestAnimationFrame(() => {
        document.getElementById('evaluation-job-progress')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    [loadEvaluationJobHistory, pollActiveEvaluationJobs, resetEvaluationJobsApiProgress, selectedIntegrationId]
  );

  const handleCancelEvaluationJob = async (jobId: string) => {
    if (!jobId.trim() || evaluationJobCancellingId) return;
    const trimmed = jobId.trim();
    setEvaluationJobCancellingId(trimmed);
    try {
      const res = await autoTune.cancelEvaluationJob(trimmed);
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to cancel evaluation job.');
      }
      await pollEvaluationJobStatus(trimmed, { force: true });
      setNotification({
        isVisible: true,
        type: 'info',
        message: 'Evaluation cancellation requested.',
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } catch (e: unknown) {
      setEvaluationJobCancellingId((current) => clearJobCancellationPendingId(current, trimmed));
      setNotification({
        isVisible: true,
        type: 'error',
        message: getErrorMessage(e, 'Failed to cancel evaluation job.'),
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    }
  };

  const handleRetryEvaluationJob = async (jobId: string, rowKey: string) => {
    if (!jobId.trim() || !rowKey || evaluationJobRetryingRowKey) return;
    setEvaluationJobRetryingRowKey(rowKey);
    try {
      const res = await autoTune.retryEvaluationJob(jobId.trim());
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Failed to retry evaluation job.');
      }
      const newJobId = extractEvaluationJobIdFromResponse(res) || res.data.id;
      if (!newJobId) {
        throw new Error('Retry succeeded but the server did not return a new job id.');
      }
      handleEvaluationJobStarted(newJobId);
    } catch (e: unknown) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: getErrorMessage(e, 'Failed to retry evaluation job.'),
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setEvaluationJobRetryingRowKey(null);
      void loadEvaluationJobHistory();
    }
  };

  const handleRetryTrainingJob = async (jobId: string, rowKey: string) => {
    if (!selectedIntegrationId || !jobId.trim() || !rowKey || trainingJobRetryingRowKey) return;
    setTrainingJobRetryingRowKey(rowKey);
    try {
      const response = await connector.retryTrainingJob(jobId.trim(), selectedIntegrationId);
      const newJobId = assertTrainingJobRetrySucceeded(response);
      setTrainingJobCancellingId(null);
      const liveRow = buildTrainingJobLiveRow(response, newJobId);
      trainingJobLiveSnapshotsRef.current.set(newJobId, liveRow);
      setTrainingJobHistory((prev) =>
        mergeTrainingJobHistoryRows(prev, trainingJobLiveSnapshotsRef.current)
      );
      setTrainingJobResult({ data: liveRow });
      setPollingJobId(newJobId);
      pollingJobIdRef.current = newJobId;
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Fine-tuning job restarted. Tracking the new job.',
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } catch (e: unknown) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: getErrorMessage(e, 'Failed to retry fine-tuning job'),
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setTrainingJobRetryingRowKey(null);
      void loadTrainingJobHistory();
    }
  };

  const stopOptimizationJobPolling = useCallback(() => {
    if (optimizationJobPollRef.current) {
      clearInterval(optimizationJobPollRef.current);
      optimizationJobPollRef.current = null;
    }
    optimizationJobIdRef.current = null;
    optimizationJobStallSinceRef.current = null;
    optimizationJobPollIntervalRef.current = OPTIMIZATION_JOB_POLL_INTERVAL_MS;
    setConnectorOptimizationJobId(null);
    setTrackedOptimizationJobSnapshot(null);
  }, []);

  const finishOptimizationJobPolling = useCallback(
    (jobId: string, job: Record<string, unknown>, integrationId?: string | null) => {
      const resolvedJobId = resolveOptimizationJobRecordId(job) || jobId;
      setOptimizationJobCancellingId((current) => clearJobCancellationPendingId(current, resolvedJobId));
      const enriched = enrichOptimizationJobRowForDisplay(job, optimizationIterations);
      if (resolvedJobId) {
        optimizationJobLiveSnapshotsRef.current.set(resolvedJobId, enriched);
        setOptimizationJobHistory((prev) =>
          mergeOptimizationJobHistoryRows(
            prev,
            optimizationJobLiveSnapshotsRef.current,
            optimizationIterations
          )
        );
      }

      stopOptimizationJobPolling();
      clearPersistedOptimizationJobId(integrationId || selectedIntegrationId);
      unifiedWebSocketService.clearSession();
      setConnectorMultiAgentIsOptimizing(false);
      setConnectorMultiAgentProgressStartTime(null);
      void loadOptimizationJobHistory();

      if (resolvedJobId) {
        dismissedOptimizationJobIdsRef.current.add(resolvedJobId);
      }
      const status = resolveEffectiveOptimizationJobStatus(job);
      const retryable = isOptimizationJobRetryEligible(job);

      if (isSucceededOptimizationJobStatus(status)) {
        setTerminalOptimizationJob(null);
        setOptimizationStoppedByUser(false);
        setShowCompletionMessage(true);
        completeOptimizationJobsApiProgress();
        setSmoothProgressTarget(100, { force: true });
        setConnectorMultiAgentIsOptimized(true);
        return;
      }
      if (isCancelledOptimizationJobStatus(status)) {
        setTerminalOptimizationJob({ id: resolvedJobId, status, retryable });
        setOptimizationStoppedByUser(true);
        setShowCompletionMessage(false);
        setConnectorMultiAgentIsOptimized(false);
        resetOptimizationJobsApiProgress();
        resetSmoothProgress();
        setOptimizationJobCancellingId((current) => clearJobCancellationPendingId(current, resolvedJobId));
        return;
      }
      if (isFailedOptimizationJobStatus(status) || isTerminalOptimizationJobStatus(status)) {
        const rawError = extractJobRawError(job, 'Optimization job failed');
        const { friendly } = resolveOptimizationJobError(job);
        setTerminalOptimizationJob({ id: resolvedJobId, status, rawError, retryable });
        setOptimizationStoppedByUser(false);
        setShowCompletionMessage(false);
        setConnectorMultiAgentIsOptimized(false);
        resetOptimizationJobsApiProgress();
        resetSmoothProgress();
        setNotification({ isVisible: true, type: 'error', message: friendly });
        setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
      }
    },
    [
      completeOptimizationJobsApiProgress,
      loadOptimizationJobHistory,
      optimizationIterations,
      resetOptimizationJobsApiProgress,
      resetSmoothProgress,
      selectedIntegrationId,
      setConnectorMultiAgentIsOptimizing,
      setConnectorMultiAgentIsOptimized,
      setConnectorMultiAgentProgressStartTime,
      setSmoothProgressTarget,
      stopOptimizationJobPolling,
    ]
  );

  const applyOptimizationJobSnapshot = useCallback((job: any) => {
    const effectiveStatus = resolveEffectiveOptimizationJobStatus(job);
    const jobId = resolveOptimizationJobRecordId(job);
    const isCurrentlyPolling = Boolean(jobId && optimizationJobIdRef.current === jobId);
    const isDismissed =
      Boolean(jobId && dismissedOptimizationJobIdsRef.current.has(jobId) && !isCurrentlyPolling);
    const isActive = !isDismissed && shouldTrackOptimizationJobRow(job);
    const resolvedProgress = resolveOptimizationJobProgressPercent(job, optimizationIterations);
    const usesJobsApiProgress = Boolean(connectorOptimizationJobId || optimizationJobIdRef.current);

    if (isSucceededOptimizationJobStatus(effectiveStatus)) {
      if (usesJobsApiProgress) completeOptimizationJobsApiProgress();
      setSmoothProgressTarget(100, { force: true });
      setConnectorMultiAgentIsOptimizing(false);
      setConnectorMultiAgentIsOptimized(true);
    } else if (isActive) {
      if (usesJobsApiProgress) {
        syncOptimizationJobsApiProgress(resolvedProgress);
      } else {
        setSmoothProgressTarget(Math.max(0, Math.min(99, resolvedProgress)));
      }
      setConnectorMultiAgentIsOptimizing(true);
    } else {
      if (usesJobsApiProgress) resetOptimizationJobsApiProgress();
      setSmoothProgressTarget(resolvedProgress, { force: true });
      setConnectorMultiAgentIsOptimizing(false);
    }

    const steps = Array.isArray(job.steps) ? job.steps : [];
    const mapped: any[] = steps.map((step: unknown) => mapOptimizationJobStepToConnectorResult(step, job));
    setConnectorMultiAgentIterationResults(mapped);
    setConnectorMultiAgentOptimizedPrompts(
      mapped
        .filter((step: any) => {
          const seq = typeof step.seq === 'number' ? step.seq : step.iteration;
          return typeof seq === 'number' && seq > 0;
        })
        .map((step: any) => step['master-agent']?.prompt || step.prompt)
        .filter((prompt: unknown): prompt is string => typeof prompt === 'string' && prompt.trim().length > 0)
    );

    if (job.current_config && typeof job.current_config === 'object') {
      const currentResult = mapOptimizationJobStepToConnectorResult(
        {
          seq: resolveOptimizationJobMaxStepNumber(job),
          config: job.current_config,
        },
        job
      );
      processConnectorMultiAgentOptimizationResult(currentResult);
    }

    if (jobId && optimizationJobIdRef.current === jobId) {
      setTrackedOptimizationJobSnapshot(unwrapOptimizationJobRow(job));
    }
  }, [
    completeOptimizationJobsApiProgress,
    processConnectorMultiAgentOptimizationResult,
    resetOptimizationJobsApiProgress,
    setConnectorMultiAgentIterationResults,
    setConnectorMultiAgentOptimizedPrompts,
    setConnectorMultiAgentIsOptimizing,
    setConnectorMultiAgentIsOptimized,
    setSmoothProgressTarget,
    syncOptimizationJobsApiProgress,
    connectorOptimizationJobId,
    optimizationIterations,
  ]);

  const optimizationJobPollFnRef = useRef<(() => Promise<void>) | null>(null);

  const beginOptimizationJobPolling = useCallback(
    (jobId: string) => {
      stopOptimizationJobPolling();

      setOptimizationJobCancellingId(null);
      dismissedOptimizationJobIdsRef.current.delete(jobId);
      optimizationJobIdRef.current = jobId;
      setConnectorOptimizationJobId(jobId);
      resetOptimizationJobsApiProgress();
      setConnectorMultiAgentProgressStartTime(null);
      optimizationJobStallSinceRef.current = null;
      optimizationJobPollIntervalRef.current = OPTIMIZATION_JOB_POLL_INTERVAL_MS;

      const schedulePollInterval = () => {
        if (optimizationJobPollRef.current) {
          clearInterval(optimizationJobPollRef.current);
        }
        optimizationJobPollRef.current = setInterval(() => {
          void pollJob().catch((error) => {
            void handlePollError(error);
          });
        }, optimizationJobPollIntervalRef.current);
      };

      const processOptimizationJobPoll = (job: Record<string, unknown>): boolean => {
        const effectiveStatus = resolveEffectiveOptimizationJobStatus(job);
        const snapshotJob =
          effectiveStatus !== normalizeOptimizationJobStatus(String(job.status || ''))
            ? { ...job, status: effectiveStatus }
            : job;

        applyOptimizationJobSnapshot(snapshotJob);
        const resolvedId = resolveOptimizationJobRecordId(snapshotJob) || jobId;
        const enriched = enrichOptimizationJobRowForDisplay(snapshotJob, optimizationIterations);
        optimizationJobLiveSnapshotsRef.current.set(resolvedId, enriched);
        setOptimizationJobHistory((prev) =>
          mergeOptimizationJobHistoryRows(
            prev,
            optimizationJobLiveSnapshotsRef.current,
            optimizationIterations
          )
        );

        if (isTerminalOptimizationJobStatus(effectiveStatus)) {
          finishOptimizationJobPolling(
            resolveOptimizationJobRecordId(job) || jobId,
            snapshotJob,
            typeof job.integration_id === 'string' ? job.integration_id : selectedIntegrationId
          );
          return true;
        }

        const progress = resolveOptimizationJobProgressPercent(job, optimizationIterations);
        const stepsComplete = isOptimizationJobStepsComplete(job, optimizationIterations);
        const finalizing = progress >= 99 || stepsComplete;

        if (finalizing) {
          if (optimizationJobStallSinceRef.current == null) {
            optimizationJobStallSinceRef.current = Date.now();
          }
          const stalledFor = Date.now() - optimizationJobStallSinceRef.current;
          const inferred = inferOptimizationJobTerminalStatus(job, optimizationIterations);

          if (inferred === 'failed') {
            finishOptimizationJobPolling(
              resolveOptimizationJobRecordId(job) || jobId,
              { ...snapshotJob, status: inferred },
              typeof job.integration_id === 'string' ? job.integration_id : selectedIntegrationId
            );
            return true;
          }

          if (inferred === 'succeeded') {
            finishOptimizationJobPolling(
              resolveOptimizationJobRecordId(job) || jobId,
              { ...snapshotJob, status: 'succeeded' },
              typeof job.integration_id === 'string' ? job.integration_id : selectedIntegrationId
            );
            return true;
          }

          if (stalledFor >= OPTIMIZATION_JOB_MAX_FINALIZING_MS) {
            const forcedStatus = String(job.error || '').trim() ? 'failed' : 'succeeded';
            finishOptimizationJobPolling(
              resolveOptimizationJobRecordId(job) || jobId,
              { ...snapshotJob, status: forcedStatus },
              typeof job.integration_id === 'string' ? job.integration_id : selectedIntegrationId
            );
            return true;
          }

          if (optimizationJobPollIntervalRef.current !== OPTIMIZATION_JOB_FAST_POLL_INTERVAL_MS) {
            optimizationJobPollIntervalRef.current = OPTIMIZATION_JOB_FAST_POLL_INTERVAL_MS;
            schedulePollInterval();
          }
        } else {
          optimizationJobStallSinceRef.current = null;
          if (optimizationJobPollIntervalRef.current !== OPTIMIZATION_JOB_POLL_INTERVAL_MS) {
            optimizationJobPollIntervalRef.current = OPTIMIZATION_JOB_POLL_INTERVAL_MS;
            schedulePollInterval();
          }
        }

        return false;
      };

      const pollJob = async () => {
        if (optimizationJobIdRef.current !== jobId) return;
        const res = await autoTune.getOptimizationJob(jobId);
        if (!res.success || !res.data) {
          throw new Error(res.error?.message || 'Failed to load optimization job.');
        }
        const job = unwrapOptimizationJobRow(res.data);
        processOptimizationJobPoll(job);
      };

      optimizationJobPollFnRef.current = pollJob;

      const handlePollError = async (error: unknown) => {
        if (optimizationJobIdRef.current !== jobId) return;

        try {
          const res = await autoTune.getOptimizationJob(jobId);
          if (res.success && res.data) {
            const job = unwrapOptimizationJobRow(res.data);
            if (processOptimizationJobPoll(job)) {
              return;
            }
            if (shouldTrackOptimizationJobRow(job)) {
              return;
            }
          }
        } catch {
          // fall through to transient poll error UI
        }

        stopOptimizationJobPolling();
        clearPersistedOptimizationJobId(selectedIntegrationId);
        unifiedWebSocketService.clearSession();
        setConnectorMultiAgentIsOptimizing(false);
        setConnectorMultiAgentProgressStartTime(null);
        resetOptimizationJobsApiProgress();
        resetSmoothProgress();
        const msg = getErrorMessage(error, 'Optimization job failed.');
        const { friendly } = resolveJobErrorDisplay(msg, 'Optimization job failed');
        setTerminalOptimizationJob({
          id: jobId,
          status: 'poll_error',
          rawError: msg,
          retryable: false,
        });
        setNotification({ isVisible: true, type: 'error', message: friendly });
        setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
      };

      void pollJob().catch((error) => {
        void handlePollError(error);
      });

      schedulePollInterval();
    },
    [
      applyOptimizationJobSnapshot,
      finishOptimizationJobPolling,
      optimizationIterations,
      resetOptimizationJobsApiProgress,
      resetSmoothProgress,
      selectedIntegrationId,
      setConnectorMultiAgentProgressStartTime,
      stopOptimizationJobPolling,
    ]
  );

  const handleCancelOptimizationJob = async (jobId: string) => {
    const trimmed = jobId.trim();
    if (!trimmed || optimizationJobCancellingId) return;
    setOptimizationJobCancellingId(trimmed);
    try {
      const res = await autoTune.cancelOptimizationJob(trimmed);
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to cancel config tuning job.');
      }
      if (optimizationJobIdRef.current === trimmed) {
        setOptimizationStoppedByUser(true);
        setShowCompletionMessage(false);
      }
      void optimizationJobPollFnRef.current?.();
      void loadOptimizationJobHistory();
      setNotification({
        isVisible: true,
        type: 'info',
        message: 'Config tuning cancellation requested.',
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } catch (e: unknown) {
      setOptimizationJobCancellingId((current) => clearJobCancellationPendingId(current, trimmed));
      setNotification({
        isVisible: true,
        type: 'error',
        message: getErrorMessage(e, 'Failed to cancel config tuning job.'),
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    }
  };

  const handleCancelTrainingJob = async (jobId: string) => {
    if (!selectedIntegrationId || !jobId.trim()) return;
    const trimmed = jobId.trim();
    if (trainingJobCancellingId && trainingJobCancellingId !== trimmed) return;
    setTrainingJobCancellingId(trimmed);
    try {
      const res = await connector.cancelTrainingJob(trimmed, selectedIntegrationId);
      if (!res.success) {
        throw new Error(res.error?.message || 'Failed to cancel fine-tuning job.');
      }
      const cancelRow = buildTrainingJobLiveRow(res.data ?? res, trimmed);
      trainingJobLiveSnapshotsRef.current.set(trimmed, cancelRow);
      setTrainingJobHistory((prev) =>
        mergeTrainingJobHistoryRows(prev, trainingJobLiveSnapshotsRef.current)
      );
      if (pollingJobIdRef.current === trimmed) {
        setTrainingJobResult({ data: cancelRow });
      }
      await pollTrainingJobStatus(trimmed, { force: true });
      void loadTrainingJobHistory();
      setNotification({
        isVisible: true,
        type: 'info',
        message: 'Fine-tuning job cancellation requested.',
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } catch (e: unknown) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: getErrorMessage(e, 'Failed to cancel fine-tuning job.'),
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setTrainingJobCancellingId((current) => clearJobCancellationPendingId(current, trimmed));
    }
  };

  const handleRetryOptimizationJob = async (jobId: string, rowKey: string) => {
    if (!jobId.trim() || !rowKey || optimizationJobRetryingRowKey || connectorMultiAgentIsOptimizing) return;
    setOptimizationJobRetryingRowKey(rowKey);
    try {
      const trimmedJobId = jobId.trim();
      const verifyRes = await autoTune.getOptimizationJob(trimmedJobId);
      if (!verifyRes.success || !verifyRes.data) {
        throw new Error(verifyRes.error?.message || 'Could not load config tuning job before retry.');
      }
      const existingJob = unwrapOptimizationJobRow(verifyRes.data);
      const resolvedJobId = resolveOptimizationJobRecordId(existingJob) || trimmedJobId;
      if (!isOptimizationJobRetryEligible(existingJob)) {
        const currentStatus = String(existingJob.status || 'unknown');
        setTerminalOptimizationJob((prev) =>
          prev && prev.id === resolvedJobId
            ? { ...prev, status: normalizeOptimizationJobStatus(currentStatus), retryable: false }
            : prev
        );
        throw new Error(
          `Retry is only available for failed or cancelled jobs (current status: ${currentStatus}).`
        );
      }

      const response = await autoTune.retryOptimizationJob(resolvedJobId);
      const newJobId = extractOptimizationJobIdFromResponse(response);
      if (!newJobId) {
        throw new Error('Retry succeeded but the server did not return a new job id.');
      }
      setTerminalOptimizationJob(null);
      setOptimizationStoppedByUser(false);
      setShowCompletionMessage(false);
      setConnectorMultiAgentIsOptimized(false);
      resetSmoothProgress();
      optimizationRanThisSession.current = true;
      setConnectorMultiAgentIsOptimizing(true);
      beginOptimizationJobPolling(newJobId);
      void loadOptimizationJobHistory();
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Config tuning job restarted.',
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } catch (e: unknown) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: getErrorMessage(e, 'Failed to retry config tuning job'),
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setOptimizationJobRetryingRowKey(null);
    }
  };

  // Восстановить FT polling после возврата на Connector (sessionStorage на интеграцию)
  useEffect(() => {
    if (!selectedIntegrationId) {
      setPollingJobId(null);
      prevPollingJobIdForProgressRef.current = null;
      return;
    }
    const persisted = readPersistedConnectorFtPolling(
      CONNECTOR_FT_POLLING_STORAGE_PREFIX,
      selectedIntegrationId
    );
    if (!persisted?.jobId) {
      setPollingJobId(null);
      prevPollingJobIdForProgressRef.current = null;
      return;
    }
    ftPollingRestoreSkipResetRef.current = true;
    if (persisted.progressPercent != null) {
      syncTrainingJobsApiProgress(persisted.progressPercent);
    }
    setPollingJobId(persisted.jobId);
    pollingJobIdRef.current = persisted.jobId;
  }, [selectedIntegrationId, syncTrainingJobsApiProgress]);

  useEffect(() => {
    if (!selectedIntegrationId || !pollingJobId) return;
    writePersistedConnectorFtPolling(selectedIntegrationId, pollingJobId);
  }, [pollingJobId, selectedIntegrationId]);

  useEffect(() => {
    if (!selectedIntegrationId) return;
    if (!connectorOptimizationJobId) {
      clearPersistedOptimizationJobId(selectedIntegrationId);
      return;
    }
    try {
      sessionStorage.setItem(
        connectorOptPollingStorageKey(selectedIntegrationId),
        JSON.stringify({ v: 1, jobId: connectorOptimizationJobId })
      );
    } catch {
      // ignore
    }
  }, [connectorOptimizationJobId, selectedIntegrationId]);

  // Восстановить config-tuning Jobs API polling после перезагрузки / смены интеграции
  useEffect(() => {
    if (!selectedIntegrationId) return;
    let cancelled = false;

    (async () => {
      const jobId = readPersistedOptimizationJobId(selectedIntegrationId);

      if (cancelled) return;

      if (!jobId) {
        if (useOptimizationStore.getState().connectorMultiAgentIsOptimizing && !optimizationJobIdRef.current) {
          setConnectorMultiAgentIsOptimizing(false);
          setConnectorMultiAgentProgressStartTime(null);
          resetSmoothProgress();
        }
        return;
      }

      if (dismissedOptimizationJobIdsRef.current.has(jobId)) {
        clearPersistedOptimizationJobId(selectedIntegrationId);
        return;
      }

      if (optimizationJobIdRef.current === jobId) return;

      try {
        const res = await autoTune.getOptimizationJob(jobId);
        if (cancelled) return;
        if (!res.success || !res.data) {
          clearPersistedOptimizationJobId(selectedIntegrationId);
          setConnectorMultiAgentIsOptimizing(false);
          setConnectorMultiAgentProgressStartTime(null);
          resetSmoothProgress();
          return;
        }

        const job = unwrapOptimizationJobRow(res.data);
        const effectiveStatus = resolveEffectiveOptimizationJobStatus(job);
        if (!shouldTrackOptimizationJobRow(job)) {
          clearPersistedOptimizationJobId(selectedIntegrationId);
          applyOptimizationJobSnapshot({ ...job, status: effectiveStatus });
          if (isTerminalOptimizationJobStatus(effectiveStatus)) {
            finishOptimizationJobPolling(jobId, { ...job, status: effectiveStatus }, selectedIntegrationId);
          } else {
            setConnectorMultiAgentIsOptimizing(false);
            setConnectorMultiAgentProgressStartTime(null);
            resetSmoothProgress();
          }
          return;
        }
      } catch {
        if (cancelled) return;
        clearPersistedOptimizationJobId(selectedIntegrationId);
        setConnectorMultiAgentIsOptimizing(false);
        setConnectorMultiAgentProgressStartTime(null);
        resetSmoothProgress();
        return;
      }

      optimizationRanThisSession.current = true;
      beginOptimizationJobPolling(jobId);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    applyOptimizationJobSnapshot,
    beginOptimizationJobPolling,
    finishOptimizationJobPolling,
    resetSmoothProgress,
    selectedIntegrationId,
    setConnectorMultiAgentIsOptimizing,
    setConnectorMultiAgentProgressStartTime,
  ]);

  // После сворачивания вкладки браузера — один актуальный poll (интервал может быть throttled)
  useEffect(() => {
    const bumpFtPoll = () => {
      if (document.visibilityState !== 'visible' || !selectedIntegrationId) return;
      const id = pollingJobIdRef.current;
      if (!id) return;
      void pollTrainingJobStatusRef.current(id, { force: true });
    };
    const bumpOptPoll = () => {
      if (document.visibilityState !== 'visible' || !selectedIntegrationId) return;
      if (!optimizationJobIdRef.current) return;
      void optimizationJobPollFnRef.current?.();
    };
    const bumpEvalPoll = () => {
      if (document.visibilityState !== 'visible' || !selectedIntegrationId) return;
      const id = evaluationPollingJobIdRef.current;
      if (!id) return;
      void pollEvaluationJobStatusRef.current?.(id, { force: true });
    };
    document.addEventListener('visibilitychange', bumpFtPoll);
    window.addEventListener('focus', bumpFtPoll);
    document.addEventListener('visibilitychange', bumpOptPoll);
    window.addEventListener('focus', bumpOptPoll);
    document.addEventListener('visibilitychange', bumpEvalPoll);
    window.addEventListener('focus', bumpEvalPoll);
    return () => {
      document.removeEventListener('visibilitychange', bumpFtPoll);
      window.removeEventListener('focus', bumpFtPoll);
      document.removeEventListener('visibilitychange', bumpOptPoll);
      window.removeEventListener('focus', bumpOptPoll);
      document.removeEventListener('visibilitychange', bumpEvalPoll);
      window.removeEventListener('focus', bumpEvalPoll);
    };
  }, [selectedIntegrationId]);

  // Автоматический поллинг для running jobs
  useEffect(() => {
    if (pollingJobId) {
      if (ftPollingRestoreSkipResetRef.current) {
        ftPollingRestoreSkipResetRef.current = false;
      } else if (prevPollingJobIdForProgressRef.current !== pollingJobId) {
        resetTrainingJobsApiProgress();
      }
      prevPollingJobIdForProgressRef.current = pollingJobId;

      // Поллим сразу при установке jobId
      void pollTrainingJobStatus(pollingJobId);
      
      // Затем поллим каждые 5 секунд
      pollingIntervalRef.current = setInterval(() => {
        void pollTrainingJobStatus(pollingJobId);
      }, 5000);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    } else {
      prevPollingJobIdForProgressRef.current = null;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingJobId]);

  useEffect(() => {
    if (!selectedIntegrationId) {
      setEvaluationPollingJobId(null);
      evaluationPollingJobIdRef.current = null;
      setEvaluationJobSnapshot(null);
      handleCloseEvaluationJobDetails();
      return;
    }
    const persistedId = readPersistedEvaluationJobId(selectedIntegrationId);
    if (persistedId) {
      setEvaluationPollingJobId(persistedId);
      evaluationPollingJobIdRef.current = persistedId;
    }
  }, [handleCloseEvaluationJobDetails, selectedIntegrationId]);

  useEffect(() => {
    if (!selectedIntegrationId) return;

    const tick = () => {
      if (collectActiveEvaluationJobIds().length === 0) return;
      void pollActiveEvaluationJobsRef.current?.();
    };

    void tick();
    if (evaluationJobPollRef.current) {
      clearInterval(evaluationJobPollRef.current);
    }
    evaluationJobPollRef.current = setInterval(tick, EVALUATION_JOB_POLL_INTERVAL_MS);

    return () => {
      if (evaluationJobPollRef.current) {
        clearInterval(evaluationJobPollRef.current);
        evaluationJobPollRef.current = null;
      }
    };
  }, [selectedIntegrationId, collectActiveEvaluationJobIds, evaluationJobHistory.length]);

  // Exponential progress between backend checkpoints (legacy WebSocket timer only).
  useEffect(() => {
    if (connectorOptimizationJobId || optimizationJobIdRef.current) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      return;
    }
    if (connectorMultiAgentIsOptimizing && connectorMultiAgentProgressStartTime !== null) {
      progressIntervalRef.current = setInterval(() => {
        const elapsedSeconds = getEffectiveElapsedSeconds(connectorMultiAgentProgressStartTime);
        const stageProgress = calculateExponentialProgress(elapsedSeconds, connectorMultiAgentExpectedIterationTime);
        const totalProgress = calculateTotalProgress(connectorMultiAgentCurrentProgressStage, optimizationIterations, stageProgress);
        setSmoothProgressTarget(totalProgress);
      }, 100);

      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
  }, [
    connectorMultiAgentIsOptimizing,
    connectorMultiAgentProgressStartTime,
    connectorMultiAgentCurrentProgressStage,
    optimizationIterations,
    connectorMultiAgentExpectedIterationTime,
    setSmoothProgressTarget,
    connectorOptimizationJobId,
  ]);

  // Legacy WebSocket runs: advance stages from intermediate results (Jobs API uses progress_percent polling).
  useEffect(() => {
    if (connectorOptimizationJobId || optimizationJobIdRef.current) return;
    if (!connectorMultiAgentIsOptimizing || connectorMultiAgentIsOptimized) return;

    const optimizedCount = connectorMultiAgentIterationResults.filter(
      (r) => r.seq !== undefined && r.seq > 0
    ).length;

    if (optimizedCount > connectorMultiAgentCurrentProgressStage) {
      if (optimizedCount < optimizationIterations) {
        setConnectorMultiAgentCurrentProgressStage(optimizedCount);
        setConnectorMultiAgentProgressStartTime(Date.now());
        resetProgressPauseTracking();
        const exactProgress = (optimizedCount / optimizationIterations) * 100;
        setSmoothProgressTarget(exactProgress);
      } else if (optimizedCount >= optimizationIterations) {
        setSmoothProgressTarget(99);
      }
    }
  }, [
    connectorOptimizationJobId,
    connectorMultiAgentIterationResults.length,
    connectorMultiAgentIsOptimizing,
    connectorMultiAgentIsOptimized,
    connectorMultiAgentCurrentProgressStage,
    optimizationIterations,
    setSmoothProgressTarget,
    setConnectorMultiAgentCurrentProgressStage,
    setConnectorMultiAgentProgressStartTime,
  ]);

  // Показываем сообщение о завершении только если оптимизация действительно завершилась успешно
  useEffect(() => {
    if (optimizationStoppedByUser) {
      setShowCompletionMessage(false);
      return;
    }
    if (connectorMultiAgentIsOptimized && !connectorMultiAgentIsOptimizing && optimizationRanThisSession.current) {
      setTimeout(() => {
        setShowCompletionMessage(true);
      }, 500);
    }
  }, [connectorMultiAgentIsOptimized, connectorMultiAgentIsOptimizing, optimizationStoppedByUser]);

  // Block body scroll when any modal is open
  useEffect(() => {
    const isModalOpen =
      showCreateForm ||
      showEditForm ||
      showDetailsModal ||
      deleteConfirmModal.isOpen ||
      showOptimizePreviewModal ||
      showConfigTuningModal ||
      showTrainingJobModal ||
      showEvaluationRunModal ||
      evaluationDetailsModalOpen ||
      datasetsBlockingModalOpen ||
      exportConfiguratorModal.open;
    if (isModalOpen) {
      savedScrollYRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollYRef.current}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollToRestore = savedScrollYRef.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollToRestore);
      });
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [
    showCreateForm,
    showEditForm,
    showDetailsModal,
    deleteConfirmModal.isOpen,
    showOptimizePreviewModal,
    showConfigTuningModal,
    showTrainingJobModal,
    showEvaluationRunModal,
    evaluationDetailsModalOpen,
    datasetsBlockingModalOpen,
    exportConfiguratorModal.open,
  ]);

  const handleCreateIntegration = async () => {
    if (createIntegrationValidation.reportValidation(validateIntegrationForm(formData.url))) return;
    setFormLoading(true);
    createIntegrationValidation.clearErrors();
    try {
      await connector.addIntegration({
        url: formData.url,
        apiKey: formData.apiKey || undefined,
      });
      setShowCreateForm(false);
      setFormData({ url: '', apiKey: '' });
      await loadMyIntegrations();
    } catch (e: any) {
      let errorMessage = getErrorMessage(e, 'Failed to create integration');
      
      // Специальная обработка ошибок, связанных с SDK и API key
      const errorResponse = e?.response?.data;
      const errorText = JSON.stringify(errorResponse || {}).toLowerCase();
      
      // Если ошибка связана с SDK и API key
      if (errorText.includes('sdk') && (errorText.includes('api') || errorText.includes('key') || errorText.includes('auth'))) {
        errorMessage = 'This integration requires an API key. Please enter a valid API key to continue.';
      } else if (errorText.includes('sdk')) {
        errorMessage = 'SDK connection error. Please check the URL and API key (if required) and try again.';
      } else if (errorText.includes('api') && (errorText.includes('key') || errorText.includes('required') || errorText.includes('missing'))) {
        errorMessage = 'API key is required for this integration. Please enter a valid API key.';
      }
      
      createIntegrationValidation.reportApiError(errorMessage, INTEGRATION_ERROR_BANNER_ID);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditIntegration = async () => {
    if (!selectedIntegrationId) return;
    if (editIntegrationValidation.reportValidation(validateIntegrationForm(formData.url))) return;
    setFormLoading(true);
    editIntegrationValidation.clearErrors();
    try {
      await connector.patchIntegration(selectedIntegrationId, {
        url: formData.url,
        apiKey: formData.apiKey || undefined,
      });
      setShowEditForm(false);
      setFormData({ url: '', apiKey: '' });
      await loadMyIntegrations();
      if (selectedIntegrationId) {
        await loadConfig(selectedIntegrationId);
      }
    } catch (e: any) {
      let errorMessage = getErrorMessage(e, 'Failed to update integration');
      
      // Специальная обработка ошибок, связанных с SDK и API key
      const errorResponse = e?.response?.data;
      const errorText = JSON.stringify(errorResponse || {}).toLowerCase();
      
      // Если ошибка связана с SDK и API key
      if (errorText.includes('sdk') && (errorText.includes('api') || errorText.includes('key') || errorText.includes('auth'))) {
        errorMessage = 'This integration requires an API key. Please enter a valid API key to continue.';
      } else if (errorText.includes('sdk')) {
        errorMessage = 'SDK connection error. Please check the URL and API key (if required) and try again.';
      } else if (errorText.includes('api') && (errorText.includes('key') || errorText.includes('required') || errorText.includes('missing'))) {
        errorMessage = 'API key is required for this integration. Please enter a valid API key.';
      }
      
      editIntegrationValidation.reportApiError(errorMessage, INTEGRATION_ERROR_BANNER_ID);
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveConfig = async (cfg: AgentConfiguratorState, label?: string) => {
    if (!selectedIntegrationId) throw new Error('No integration selected');
    const merged = mergePreservedConnectorConfig(connectorConfigPreservationRef.current, cfg);
    connectorConfigPreservationRef.current = merged;
    const body = {
      config: {
        config: JSON.stringify(merged),
        dialog: dialogEnvelope || [],
        label: label || undefined,
      },
      label: label || undefined,
    };
    await connector.setIntegrationConfig(selectedIntegrationId, body);
    setCurrentConfig(cfg);
    setConfigValue(cfg);
    // Обновляем configLabel после успешного сохранения
    if (label !== undefined) {
      setConfigLabel(label || undefined);
      setConnectorConfigLabel(label || undefined);
    }
  };

  const handleLoadOptimizedStepIntoConfigurator = useCallback(
    (result: any) => {
      const base = buildAgentConfiguratorStateFromMultiAgentIterationResult(result);
      if (!base) {
        setNotification({
          isVisible: true,
          type: 'error',
          message: OPTIMIZATION_COORDINATOR_MISSING_MESSAGE,
        });
        setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);
        return;
      }
      const approach = (currentConfig ?? configValue)?.approach ?? 'BASELINE';
      const merged: AgentConfiguratorState = { ...base, approach };

      setConnectorCoordinatorPrompt(merged.coordinator.prompt || '');
      setConnectorCoordinatorModel(merged.coordinator.model || 'gpt-4.1-mini');
      setConnectorCoordinatorTemperature(merged.coordinator.temperature ?? 0.8);
      setConnectorCoordinatorDescription(merged.coordinator.description || '');
      setConnectorCoordinatorReminderPrompt(merged.coordinator.reminder_prompt || '');
      setConnectorCoordinatorName(
        typeof merged.coordinator.name === 'string' ? merged.coordinator.name : 'Coordinator'
      );
      setConnectorCoordinatorBackendId(
        typeof merged.coordinator.id === 'string' ? merged.coordinator.id.trim() || undefined : undefined
      );
      setConnectorCoordinatorAgentId(
        typeof merged.coordinator.agent_id === 'string'
          ? merged.coordinator.agent_id.trim() || undefined
          : typeof merged.coordinator.id === 'string'
            ? merged.coordinator.id.trim() || undefined
            : undefined
      );
      setConnectorWorkers(
        merged.workers.map((w, index) => ({
          id: normalizeWorkerIdString(w.id, index),
          name: w.name || '',
          prompt: w.prompt || '',
          model: w.model || 'gpt-4.1-mini',
          temperature: w.temperature ?? 0.7,
          description: w.description || '',
          mode: (w.mode === 'asynchronous' ? 'asynchronous' : 'synchronous') as 'synchronous' | 'asynchronous',
          reminder_prompt: w.reminder_prompt,
        }))
      );
      setConnectorMetaPrompts(mergeMetaPromptsWithFinalStepDefault(merged.metaPrompts));

      connectorConfigPreservationRef.current = null;
      setConfigValue(merged);
      setCurrentConfig(merged);

      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Configuration loaded into the Integration Configurator.',
      });
      setTimeout(() => setNotification((prev) => ({ ...prev, isVisible: false })), NOTIFICATION_AUTO_HIDE_MS);

      document.getElementById('connector-config-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    [
      currentConfig,
      configValue,
      setConnectorCoordinatorPrompt,
      setConnectorCoordinatorModel,
      setConnectorCoordinatorTemperature,
      setConnectorCoordinatorDescription,
      setConnectorCoordinatorReminderPrompt,
      setConnectorCoordinatorName,
      setConnectorCoordinatorBackendId,
      setConnectorCoordinatorAgentId,
      setConnectorWorkers,
      setConnectorMetaPrompts,
    ]
  );

  const handleOptimize = async (): Promise<boolean> => {
    if (!isAuthenticated) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'You need to be authorized to start optimization!',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }

    if (!currentConfig) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Please configure agents before starting optimization.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }

    if (!selectedIntegrationId) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Please select an integration before starting optimization.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }

    const workersWithEmptyNames = currentConfig.workers.filter(worker => !worker.name || worker.name.trim().length === 0);
    if (workersWithEmptyNames.length > 0) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Agent worker name cannot be empty. Please provide a name before running optimization.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }

    // Keep names unique even when user runs optimization without pressing "Save Config".
    const seenAgentNames = new Map<string, string>();
    const coordinatorNameRaw = String(currentConfig.coordinator?.name || '').trim();
    if (coordinatorNameRaw) {
      seenAgentNames.set(coordinatorNameRaw.toLowerCase(), coordinatorNameRaw);
    }
    for (const worker of currentConfig.workers) {
      const workerNameRaw = String(worker.name || '').trim();
      if (!workerNameRaw) continue;
      const key = workerNameRaw.toLowerCase();
      const existing = seenAgentNames.get(key);
      if (existing) {
        setNotification({
          isVisible: true,
          type: 'error',
          message: `Agent name "${workerNameRaw}" is duplicated. Agent names must be unique.`,
        });
        setTimeout(() => {
          setNotification(prev => ({ ...prev, isVisible: false }));
        }, NOTIFICATION_AUTO_HIDE_MS);
        return false;
      }
      seenAgentNames.set(key, workerNameRaw);
    }

    if (!currentConfig.coordinator.prompt.trim()) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Coordinator prompt is required before starting optimization.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }

    const workersWithEmptyPrompts = currentConfig.workers.filter(worker => !worker.prompt || worker.prompt.trim().length === 0);
    if (workersWithEmptyPrompts.length > 0) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Each worker agent prompt is required before starting optimization.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }

    const datasetNamesForOptimization = resolveConnectorOptimizationDatasets();
    const datasetNameForOptimization = formatOptimizationDatasetNameForApi(datasetNamesForOptimization);
    if (!datasetNameForOptimization) {
      setNotification({
        isVisible: true,
        type: 'error',
        message:
          allAvailableDatasets.length === 0
            ? 'No dataset available for this integration. Wait for the list to load, or use the Datasets tab → Import dataset to add training data.'
            : 'Please select at least one dataset before starting optimization.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }

    const requestData = buildConnectorOptimizationPayload({
      integrationId: selectedIntegrationId,
      configLabel: configLabel || '',
      currentConfig,
      optimizationIterations,
      optimizationPolicyUi,
      dataset_name: datasetNameForOptimization,
      selectionScenario,
      evaluationMode,
      evaluatorParams,
    });
    const evaluatorValidationError = validateEvaluatorParamsForm(evaluationMode, evaluatorParams);
    if (evaluatorValidationError) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: evaluatorValidationError,
      });
      setTimeout(() => {
        setNotification((prev) => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }
    if (!hasModifiableOptimizationTargets(requestData.optimization_policy!)) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Select at least one agent or meta-prompt for tuning (not Frozen).',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }

    try {
      optimizationInitialConfigRef.current = currentConfig
        ? (JSON.parse(JSON.stringify(currentConfig)) as AgentConfiguratorState)
        : null;

      // Очищаем все данные и состояния
      setConnectorMultiAgentIterationResults([]);
      setConnectorMultiAgentOptimizedPrompts([]);
      setConnectorMultiAgentIsOptimized(false);
      clearConnectorMultiAgentChat();
      setShowCompletionMessage(false);
      setOptimizationStoppedByUser(false);
      setTerminalOptimizationJob(null);
      
      clearConnectorLegacyOptimizationSession();
      unifiedWebSocketService.clearSession();

      // Устанавливаем параметры экспоненциального прогресса
      setConnectorMultiAgentCurrentProgressStage(0);
      // Jobs API returns real/estimated progress; keep legacy client timer disabled for job runs.
      setConnectorMultiAgentProgressStartTime(null);
      setConnectorMultiAgentExpectedIterationTime(60);
      resetProgressPauseTracking();
      
      resetSmoothProgress();
      
      // Включаем оптимизацию
      optimizationRanThisSession.current = true;
      setConnectorMultiAgentIsOptimizing(true);

      const jobCreate = await autoTune.createOptimizationJob(buildOptimizationJobApiBody(requestData));
      if (!jobCreate.success) {
        const err = new Error(jobCreate.error?.message || 'Failed to create optimization job.');
        (err as Error & { optimizationErrorPayload?: unknown }).optimizationErrorPayload = jobCreate;
        throw err;
      }
      const jobId = extractOptimizationJobIdFromResponse(jobCreate);
      if (!jobId) {
        throw new Error('Failed to create optimization job: server did not return a job id.');
      }
      beginOptimizationJobPolling(jobId);
      void loadOptimizationJobHistory();
      return true;
    } catch (error) {
      console.error('❌ Failed to start optimization:', error);
      setConnectorMultiAgentIsOptimizing(false);
      const msg = formatOptimizationJobStartError(
        error,
        'Failed to start optimization. Please try again.',
        { previewDatasetTokens: optimizePreviewResult?.dataset_tokens ?? null }
      );
      setNotification({
        isVisible: true,
        type: 'error',
        message: msg,
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return false;
    }
  };

  const buildOptimizationPayload = (): ConnectorMultiAgentConfig | null => {
    if (!selectedIntegrationId || !currentConfig) return null;
    const dataset_name = formatOptimizationDatasetNameForApi(resolveConnectorOptimizationDatasets());
    if (!dataset_name) return null;
    return buildConnectorOptimizationPayload({
      integrationId: selectedIntegrationId,
      configLabel: configLabel || '',
      currentConfig,
      optimizationIterations,
      optimizationPolicyUi,
      dataset_name,
      selectionScenario,
      evaluationMode,
      evaluatorParams,
    });
  };

  const handleEvaluationModeChange = (mode: EvaluationMode) => {
    setEvaluationMode(mode);
    setEvaluatorParams((prev) => mergeEvaluatorParamsFormForMode(mode, prev));
  };

  const buildStandaloneEvaluationConfig = useCallback((): Record<string, unknown> | null => {
    if (!currentConfig) return null;
    const mp = currentConfig.metaPrompts;
    return {
      coordinator: optimizationPayloadCoordinator(currentConfig.coordinator),
      workers: currentConfig.workers.map((worker) => optimizationPayloadWorker(worker)),
      ...(mp ? { metaPrompts: { ...mp } } : {}),
    };
  }, [currentConfig]);

  const handleOptimizePreview = async () => {
    const payload = buildOptimizationPayload();
    if (!payload) {
      setOptimizePreviewError(
        allAvailableDatasets.length === 0
          ? 'Select an integration, configure agents, and ensure at least one dataset exists for this integration (see list from the connector).'
          : 'Select at least one dataset in the list above before running optimize preview.'
      );
      setShowOptimizePreviewModal(true);
      return;
    }
    setOptimizePreviewLoading(true);
    setOptimizePreviewError(null);
    setOptimizePreviewResult(null);
    setShowOptimizePreviewModal(true);
    try {
      const response = await autoTune.optimizePreview(buildOptimizationPreviewRequest(payload));
      if (response?.success === false) {
        setOptimizePreviewError(
          formatOptimizationJobStartError(response, 'Preview failed')
        );
        setOptimizePreviewResult(null);
      } else {
        setOptimizePreviewResult(response?.data ?? null);
        setOptimizePreviewError(null);
      }
    } catch (e: unknown) {
      const msg = formatOptimizationJobStartError(e, 'Failed to load optimization preview.');
      setOptimizePreviewError(msg);
      setOptimizePreviewResult(null);
    } finally {
      setOptimizePreviewLoading(false);
    }
  };

  const handleStop = async () => {
    if (optimizationJobCancellingId) return;

    const jobId = connectorOptimizationJobId || optimizationJobIdRef.current;
    if (jobId) {
      await handleCancelOptimizationJob(jobId);
      return;
    }

    if (!connectorMultiAgentIsOptimizing) {
      return;
    }

    setOptimizationJobCancellingId(LEGACY_JOB_STOP_PENDING_ID);
    try {
      console.log('🛑 Stopping connector optimization...');

      const runId = unifiedWebSocketService.getSessionId() || unifiedWebSocketService.getStoredRunId();
      if (runId) {
        await unifiedWebSocketService.stop(runId);
        console.log('✅ Stop command sent successfully');
      }

      if (optimizationJobPollRef.current) {
        clearInterval(optimizationJobPollRef.current);
        optimizationJobPollRef.current = null;
      }
      optimizationJobIdRef.current = null;
      setConnectorOptimizationJobId(null);
      clearPersistedOptimizationJobId(selectedIntegrationId);

      setConnectorMultiAgentIsOptimizing(false);
      setConnectorMultiAgentProgressStartTime(null);
      resetSmoothProgress();
      setShowCompletionMessage(false);
      setOptimizationStoppedByUser(true);
      setConnectorMultiAgentIsOptimized(false);

      unifiedWebSocketService.disconnect(true);

      setNotification({
        isVisible: true,
        type: 'info',
        message: 'Optimization stopped successfully.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    } catch (error) {
      console.error('❌ Failed to stop connector optimization:', error);
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Failed to stop optimization. Please try again.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setOptimizationJobCancellingId((current) =>
        current === LEGACY_JOB_STOP_PENDING_ID ? null : current
      );
    }
  };

  const closeExportConfiguratorModal = () => {
    pendingExportIterationRef.current = null;
    setExportConfiguratorModal({ open: false, mode: 'replace_confirm' });
    setExportConfiguratorBusy(false);
  };

  const applyPendingExportToConfigurator = () => {
    const r = pendingExportIterationRef.current;
    pendingExportIterationRef.current = null;
    setExportConfiguratorModal({ open: false, mode: 'replace_confirm' });
    setExportConfiguratorBusy(false);
    if (r) {
      handleLoadOptimizedStepIntoConfigurator(r);
    }
  };

  const requestExportStepToConfigurator = (result: any) => {
    pendingExportIterationRef.current = result;
    setExportConfiguratorModal({
      open: true,
      mode: connectorMultiAgentIsOptimizing ? 'while_running' : 'replace_confirm',
    });
  };

  const exportStepAndStopOptimization = async () => {
    setExportConfiguratorBusy(true);
    try {
      await handleStop();
    } finally {
      applyPendingExportToConfigurator();
    }
  };

  const handleEditIntegrationFromList = async (integrationId: string, listItem: Record<string, unknown>) => {
    if (selectedIntegrationId !== integrationId) {
      setError('Please select this integration first to edit it');
      return;
    }
    try {
      setFormLoading(true);
      const fullIntegration = await connector.getIntegration(integrationId);
      const integrationData = fullIntegration?.data || fullIntegration;
      const apiKeyValue =
        integrationData?.apiKey ||
        integrationData?.apikey ||
        listItem?.apiKey ||
        listItem?.apikey ||
        '';
      setFormData({
        url: integrationData?.url || String(listItem?.url || ''),
        apiKey: apiKeyValue,
      });
      setShowEditForm(true);
    } catch (e: unknown) {
      console.warn('Failed to load full integration data, using list data:', e);
      const apiKeyValue = listItem?.apiKey || listItem?.apikey || '';
      setFormData({
        url: String(listItem?.url || ''),
        apiKey: String(apiKeyValue || ''),
      });
      setShowEditForm(true);
    } finally {
      setFormLoading(false);
    }
  };

  const handleGetIntegration = async (integrationId: string) => {
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setDetailsError(null);
    try {
      const resp = await connector.getIntegration(integrationId);
      setDetailsData(resp);
    } catch (e: any) {
      setDetailsError(e?.message || 'Failed to get integration details');
      setDetailsData(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Integration ID copied to clipboard!',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    } catch (e: any) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Failed to copy ID',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    }
  };

  const handleDeleteIntegration = (integrationId: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      integrationId,
    });
  };

  const confirmDeleteIntegration = async () => {
    const integrationId = deleteConfirmModal.integrationId;
    if (!integrationId) return;

    setDeleteConfirmModal({ isOpen: false, integrationId: null });
    setLoading(true);
    setError(null);
    try {
      await connector.deleteIntegration(integrationId);
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Integration deleted successfully!',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      
      // If deleted integration was selected, clear selection
      if (selectedIntegrationId === integrationId) {
        selectIntegration(null);
        setConfigValue(null);
        setConfigLabel(undefined);
        setDialogEnvelope([]);
      }
      
      // Reload integrations list
      await loadMyIntegrations();
    } catch (e: any) {
      const errorMessage = getErrorMessage(e, 'Failed to delete integration');
      setError(errorMessage);
      setNotification({
        isVisible: true,
        type: 'error',
        message: errorMessage,
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setLoading(false);
    }
  };

  // Открыть модалку для создания Training Job
  const handleOpenTrainingJobModal = () => {
    if (!selectedIntegrationId) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Please select an integration first.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return;
    }

    if (!currentConfig) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Please configure agents before starting Fine Tuning Job.',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return;
    }

    setAgentReactions({});
    setAgentFilterFlags({});
    setDatasetAgentSelections([]);
    setDatasetAgentsPreviewError(null);
    setDatasetAgentsMinExamples(undefined);
    setTrainingJobPreviewConfirmed(false);
    trainingJobPreviewPayloadRef.current = null;
    setPreviewResult(null);
    if (selectedIntegrationId) {
      void loadConnectorDatasets(selectedIntegrationId);
    }
    setShowTrainingJobModal(true);
  };

  const buildAgentFiltersForName = (agentName: string) =>
    buildAgentReactionFilter(agentReactions[agentName], agentFilterFlags[agentName]);

  const toggleTrainingAgentReaction = (agentName: string, type: 'like' | 'dislike') => {
    setAgentReactions((prev) => {
      const current = prev[agentName] ?? { like: true, dislike: true };
      return {
        ...prev,
        [agentName]: {
          ...current,
          [type]: !current[type],
        },
      };
    });
    invalidateTrainingJobPreview();
  };

  const toggleTrainingAgentFilterFlag = (agentName: string, type: 'comment' | 'groundtruth') => {
    setAgentFilterFlags((prev) => {
      const current = prev[agentName] ?? { comment: true, groundtruth: true };
      return {
        ...prev,
        [agentName]: {
          ...current,
          [type]: !current[type],
        },
      };
    });
    invalidateTrainingJobPreview();
  };

  const listTrainingAgentNames = useCallback(
    () =>
      Array.from(
        new Set(
          datasetAgentSelections
            .map((row) => row.agent_name)
            .filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
        )
      ),
    [datasetAgentSelections]
  );

  const setBulkTrainingAgentReaction = useCallback(
    (type: 'like' | 'dislike', enabled: boolean) => {
      const names = listTrainingAgentNames();
      if (names.length === 0) return;
      setAgentReactions((prev) => {
        const next = { ...prev };
        for (const name of names) {
          const current = next[name] ?? { like: true, dislike: true };
          next[name] = { ...current, [type]: enabled };
        }
        return next;
      });
      invalidateTrainingJobPreview();
    },
    [listTrainingAgentNames, invalidateTrainingJobPreview]
  );

  const setBulkTrainingAgentFilterFlag = useCallback(
    (type: 'comment' | 'groundtruth', enabled: boolean) => {
      const names = listTrainingAgentNames();
      if (names.length === 0) return;
      setAgentFilterFlags((prev) => {
        const next = { ...prev };
        for (const name of names) {
          const current = next[name] ?? { comment: true, groundtruth: true };
          next[name] = { ...current, [type]: enabled };
        }
        return next;
      });
      invalidateTrainingJobPreview();
    },
    [listTrainingAgentNames, invalidateTrainingJobPreview]
  );

  const buildTrainingAgents = () =>
    buildTrainingAgentsPayloadFromSelections(datasetAgentSelections, {
      modelNamePrefix: trainingJobForm.model_name,
      groupByBaseModel: trainingJobForm.group_by_base_model,
      buildFilters: buildAgentFiltersForName,
    });

  const handleDatasetAgentSelectionsChange = useCallback(
    (next: DatasetAgentTrainingSelection[]) => {
      setDatasetAgentSelections(
        recalculateMissingAgentGroupEligibility(
          next,
          datasetAgentsMinExamples,
          trainingJobForm.group_by_base_model
        )
      );
    },
    [datasetAgentsMinExamples, trainingJobForm.group_by_base_model]
  );

  const closeTrainingJobModal = useCallback(() => {
    trainingJobPreviewAbortRef.current?.abort();
    trainingJobCreateAbortRef.current?.abort();
    datasetAgentsPreviewAbortRef.current?.abort();
    trainingJobPreviewAbortRef.current = null;
    trainingJobCreateAbortRef.current = null;
    datasetAgentsPreviewAbortRef.current = null;
    trainingJobPreviewPayloadRef.current = null;
    setPreviewLoading(false);
    setTrainingJobLoading(false);
    setDatasetAgentsPreviewLoading(false);
    setShowTrainingJobModal(false);
    trainingJobValidation.clearErrors();
    setAgentReactions({});
    setAgentFilterFlags({});
    setDatasetAgentSelections([]);
    setDatasetAgentsPreviewError(null);
    setDatasetAgentsMinExamples(undefined);
    setTrainingJobPreviewConfirmed(false);
    setPreviewResult(null);
    setTrainingJobForm(() => ({
      model_name: '',
      dataset_ids: [],
      group_by_base_model: true,
      merge_train_validation: true,
      limit_history: true,
      history_depth: DEFAULT_HISTORY_DEPTH,
    }));
  }, [trainingJobValidation]);

  const closeConfigTuningModal = useCallback(() => {
    setShowConfigTuningModal(false);
  }, []);

  const handleOpenConfigTuningModal = () => {
    if (!selectedIntegrationId) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Please select an integration first.',
      });
      setTimeout(() => {
        setNotification((prev) => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return;
    }
    if (!currentConfig) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Please configure agents before starting config tuning.',
      });
      setTimeout(() => {
        setNotification((prev) => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      return;
    }
    if (selectedIntegrationId) {
      void loadConnectorDatasets(selectedIntegrationId);
    }
    setOptimizationPolicyUi(createDefaultOptimizationPolicyUi(currentConfig));
    setShowConfigTuningModal(true);
  };

  useEffect(() => {
    if (!showConfigTuningModal || !currentConfig) return;
    setOptimizationPolicyUi((prev) => syncOptimizationPolicyUi(currentConfig, prev));
  }, [showConfigTuningModal, currentConfig]);

  const handleConfigChangeForTuning = useCallback(
    (next: AgentConfiguratorState) => {
      setCurrentConfig(next);
      setConfigValue(next);
    },
    []
  );

  useEffect(() => {
    setNltPatchReview(null);
    setNltFocusedPatchPath(null);
    setNltOpen(false);
    optimizationInitialConfigRef.current = null;
  }, [selectedIntegrationId]);

  const nltConfiguratorInitialConfig = useMemo(() => {
    if (!nltPatchReview) {
      return configValue;
    }
    return mergeConfigWithFieldDecisions(
      nltPatchReview.baseConfig,
      nltPatchReview.proposedConfig,
      nltPatchReview.fieldDiffs,
      nltPatchReview.fieldDecisions
    );
  }, [configValue, nltPatchReview]);

  const nltBeforeChatConfig = useMemo(() => {
    if (!nltOpen) return null;
    return nltPatchReview?.baseConfig ?? configValue;
  }, [configValue, nltOpen, nltPatchReview]);

  const nltAfterChatConfig = useMemo(() => {
    if (!nltOpen) return null;
    return currentConfig ?? nltConfiguratorInitialConfig ?? configValue;
  }, [configValue, currentConfig, nltConfiguratorInitialConfig, nltOpen]);

  const optimizationCompareConfigs = useMemo(
    () =>
      resolveOptimizationCompareConfigs({
        iterationResults: connectorMultiAgentIterationResults,
        selectedStepIndex: connectorMultiAgentSelectedIterationIndex,
        fallbackInitialConfig: optimizationInitialConfigRef.current ?? currentConfig ?? configValue,
      }),
    [
      connectorMultiAgentIterationResults,
      connectorMultiAgentSelectedIterationIndex,
      currentConfig,
      configValue,
    ]
  );

  const handleNltApplyPatch = useCallback(() => {
    if (!nltPatchReview) return;
    const { baseConfig, proposedConfig, fieldDiffs, fieldDecisions } = nltPatchReview;
    const finalDecisions = finalizeFieldDecisionsForApply(fieldDiffs, fieldDecisions);
    const mergedConfig = mergeConfigWithFieldDecisions(
      baseConfig,
      proposedConfig,
      fieldDiffs,
      finalDecisions
    );
    setCurrentConfig(mergedConfig);
    setConfigValue(mergedConfig);
    setNltPatchReview(null);
    setNltFocusedPatchPath(null);
  }, [nltPatchReview]);

  useEffect(() => {
    if (!nltPatchReview) return;
    const { fieldDiffs, fieldDecisions } = nltPatchReview;
    if (fieldDiffs.length > 0 && allFieldsAccepted(fieldDiffs, fieldDecisions)) {
      handleNltApplyPatch();
    }
  }, [nltPatchReview, handleNltApplyPatch]);

  const handleNltRejectPatch = useCallback(() => {
    if (!nltPatchReview) return;
    setCurrentConfig(nltPatchReview.baseConfig);
    setConfigValue(nltPatchReview.baseConfig);
    setNltPatchReview(null);
    setNltFocusedPatchPath(null);
  }, [nltPatchReview]);

  const handleNltNavigatePatchField = useCallback((path: string) => {
    setActiveTab('integrations');
    setNltFocusedPatchPath((prev) => {
      if (prev === path) {
        window.setTimeout(() => setNltFocusedPatchPath(path), 0);
        return null;
      }
      return path;
    });
  }, []);

  const agentConfiguratorPatchReview = useMemo((): AgentConfiguratorPatchReview | null => {
    if (!nltPatchReview) return null;
    const hideBulkActions = allFieldsAccepted(
      nltPatchReview.fieldDiffs,
      nltPatchReview.fieldDecisions
    );
    return {
      baseConfig: nltPatchReview.baseConfig,
      proposedConfig: nltPatchReview.proposedConfig,
      fieldDecisions: nltPatchReview.fieldDecisions,
      focusedPatchPath: nltFocusedPatchPath,
      hideBulkActions,
      onFieldDecision: (path, decision) => {
        setNltPatchReview((prev) =>
          prev
            ? {
                ...prev,
                fieldDecisions: { ...prev.fieldDecisions, [path]: decision },
              }
            : null
        );
      },
      onApplyAll: () => handleNltApplyPatch(),
      onRejectAll: () => handleNltRejectPatch(),
      applying: false,
    };
  }, [
    nltPatchReview,
    nltFocusedPatchPath,
    handleNltApplyPatch,
    handleNltRejectPatch,
  ]);

  const handleStartConfigTuningJob = async () => {
    if (configTuningJobSubmitting) return;
    setConfigTuningJobSubmitting(true);
    try {
      const started = await handleOptimize();
      if (started) {
        setShowConfigTuningModal(false);
      }
    } finally {
      setConfigTuningJobSubmitting(false);
    }
  };

  useEffect(() => {
    if (!showTrainingJobModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeTrainingJobModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showTrainingJobModal, closeTrainingJobModal]);

  useEffect(() => {
    if (!showConfigTuningModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeConfigTuningModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showConfigTuningModal, closeConfigTuningModal]);

  /** Same JSON body for POST /training/jobs/preview and POST /training/jobs (saved datasets only). */
  const buildTrainingJobRequestBody = (): TrainingJobRequestBody | null => {
    if (!selectedIntegrationId) return null;
    const agents = buildTrainingAgents();
    if (agents.length === 0) return null;
    return {
      integration_id: selectedIntegrationId,
      model_name: trainingJobForm.model_name.trim(),
      dataset_ids: [...trainingJobForm.dataset_ids],
      group_by_base_model: Boolean(trainingJobForm.group_by_base_model),
      merge_train_validation: Boolean(trainingJobForm.merge_train_validation),
      limit_history: trainingJobForm.limit_history,
      history_depth: trainingJobForm.limit_history ? clampHistoryDepth(trainingJobForm.history_depth, DEFAULT_HISTORY_DEPTH) : null,
      agents,
    };
  };

  const buildTrainingJobValidation = useCallback(
    (requirePreview = false) =>
      validateTrainingJobForm({
        model_name: trainingJobForm.model_name,
        modelNameMaxLen: TRAINING_JOB_MODEL_NAME_MAX_LEN,
        dataset_ids: trainingJobForm.dataset_ids,
        hasEligibleAgents: countSelectedEligibleDatasetAgents(datasetAgentSelections) > 0,
        missingBaseModelAgents: datasetAgentSelectionsMissingBaseModel(datasetAgentSelections),
        previewConfirmed: requirePreview ? trainingJobPreviewConfirmed : undefined,
      }),
    [trainingJobForm, datasetAgentSelections, trainingJobPreviewConfirmed]
  );

  useEffect(() => {
    if (!showTrainingJobModal) return;
    trainingJobValidation.revalidateIfNeeded(buildTrainingJobValidation(false));
  }, [
    showTrainingJobModal,
    trainingJobForm,
    datasetAgentSelections,
    trainingJobPreviewConfirmed,
    buildTrainingJobValidation,
    trainingJobValidation.revalidateIfNeeded,
  ]);

  useEffect(() => {
    if (!showTrainingJobModal) return;
    invalidateTrainingJobPreview();
  }, [
    showTrainingJobModal,
    trainingJobForm.dataset_ids.join('\u001f'),
    trainingJobForm.model_name,
    trainingJobForm.group_by_base_model,
    trainingJobForm.merge_train_validation,
    trainingJobForm.limit_history,
    trainingJobForm.history_depth,
    JSON.stringify(datasetAgentSelections),
    JSON.stringify(agentReactions),
    JSON.stringify(agentFilterFlags),
    invalidateTrainingJobPreview,
  ]);

  // Preview Training Job before launching
  const handlePreviewTrainingJob = async () => {
    if (!selectedIntegrationId) return;
    if (trainingJobValidation.reportValidation(buildTrainingJobValidation(false))) return;
    const previewPayload = buildTrainingJobRequestBody();
    if (!previewPayload) return;
    trainingJobPreviewAbortRef.current?.abort();
    const ac = new AbortController();
    trainingJobPreviewAbortRef.current = ac;
    setPreviewLoading(true);
    trainingJobValidation.clearErrors();
    setPreviewResult(null);
    setTrainingJobPreviewConfirmed(false);
    trainingJobPreviewPayloadRef.current = null;
    try {
      const resp = await connector.previewTrainingJob(previewPayload, { signal: ac.signal });
      const data = (resp?.data ?? resp) as TrainingPreviewResponse;
      const failedAgents = data.failed_agents ?? [];
      const hasFailedGroups = (data.groups ?? []).some((g) => g.status === 'failed');
      if (failedAgents.length > 0 || hasFailedGroups) {
        setPreviewResult(data);
        trainingJobPreviewPayloadRef.current = null;
        setTrainingJobPreviewConfirmed(false);
        trainingJobValidation.reportApiError(
          failedAgents.length > 0
            ? `Preview failed for: ${failedAgents.map((a) => a.agent_name).join(', ')}`
            : 'Preview returned failed training groups. Fix the issues below before starting.',
          TRAINING_JOB_PREVIEW_ID
        );
        return;
      }
      setPreviewResult(data);
      trainingJobPreviewPayloadRef.current = previewPayload;
      setTrainingJobPreviewConfirmed(true);
    } catch (e: any) {
      if (isTrainingRequestCanceled(e)) {
        return;
      }
      const status = e?.response?.status;
      const data = e?.response?.data;
      const failedAgents = data?.details?.failed_agents as Array<{ agent_name?: string; error?: string }> | undefined;
      let message: string;
      if (status === 400 && Array.isArray(failedAgents) && failedAgents.length > 0) {
        message = formatTrainingJobFailedAgentsErrorMessage({
          message: data?.message,
          failedAgents,
          fallbackMessage: 'None of the selected agents could be previewed for fine-tuning.',
        });
      } else {
        message = formatTrainingJobUserErrorMessage(getErrorMessage(e, 'Preview failed'));
      }
      trainingJobValidation.reportApiError(message, TRAINING_JOB_ERROR_BANNER_ID);
    } finally {
      setPreviewLoading(false);
      if (trainingJobPreviewAbortRef.current === ac) {
        trainingJobPreviewAbortRef.current = null;
      }
    }
  };

  // Реальное создание Training Job (вызывается из модалки)
  const handleCreateTrainingJob = async () => {
    if (!selectedIntegrationId) return;

    if (trainingJobValidation.reportValidation(buildTrainingJobValidation(true))) return;

    const createPayload = trainingJobPreviewPayloadRef.current ?? buildTrainingJobRequestBody();
    if (!createPayload || !trainingJobPreviewConfirmed) {
      trainingJobValidation.reportValidation(buildTrainingJobValidation(true));
      return;
    }

    trainingJobCreateAbortRef.current?.abort();
    const ac = new AbortController();
    trainingJobCreateAbortRef.current = ac;
    setTrainingJobLoading(true);
    trainingJobValidation.clearErrors();
    setTrainingJobResult(null);
    try {
      const response = await connector.createTrainingJob(createPayload, { signal: ac.signal });

      setTrainingJobResult(response);
      const envelope = response as { data?: { id?: string }; id?: string } | null | undefined;
      const jobPayload = envelope?.data ?? envelope;
      const jobId = jobPayload && typeof jobPayload === 'object' ? (jobPayload as { id?: string }).id : undefined;
      if (jobId) {
        setPollingJobId(jobId);
      }
      setShowTrainingJobModal(false);
      setAgentReactions({});
      setAgentFilterFlags({});
      setNotification({
        isVisible: true,
        type: 'success',
        message: 'Fine tuning job created successfully!',
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
      void loadTrainingJobHistory();
      
    } catch (e: any) {
      if (isTrainingRequestCanceled(e)) {
        return;
      }
      let errorMessage = formatTrainingJobUserErrorMessage(getErrorMessage(e, 'Failed to create fine tuning job'));
      const details = e?.response?.data?.details as { failed_agents?: Array<{ agent_name: string; error: string }> } | undefined;
      const failedAgents = details?.failed_agents;

      if (e?.response?.status === 502) {
        errorMessage = 'Training service is temporarily unavailable. Please try again later or contact support if the problem persists.';
      } else if (e?.response?.status === 404) {
        errorMessage = 'Training functionality is not available on this environment. This feature may be disabled for your deployment.';
      } else if (e?.response?.status >= 500) {
        errorMessage = 'Training service error. Please try again later or contact support if the problem persists.';
      } else if (failedAgents && failedAgents.length > 0) {
        errorMessage = formatTrainingJobFailedAgentsErrorMessage({
          message: e?.response?.data?.message,
          failedAgents,
          fallbackMessage: errorMessage,
          lineSeparator: '\n',
        });
      }

      setNotification({
        isVisible: true,
        type: 'error',
        message: errorMessage,
      });
      trainingJobValidation.reportApiError(errorMessage, TRAINING_JOB_ERROR_BANNER_ID);
      setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
      }, NOTIFICATION_AUTO_HIDE_MS);
    } finally {
      setTrainingJobLoading(false);
      if (trainingJobCreateAbortRef.current === ac) {
        trainingJobCreateAbortRef.current = null;
      }
    }
  };

  return (
    <div className={`connector-page connector-shell-page${nltOpen ? ' connector-page--nlt-open' : ''}`}>
      {/* Show error outside modals only if no modal is open */}
      {error && !showCreateForm && !showEditForm && (
        <div className="connector-message error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <ConnectorTabBar activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Floating NLT button */}
      <button
        type="button"
        className={`nlt-fab-btn${nltOpen ? ' nlt-fab-btn--active' : ''}${!selectedIntegrationId ? ' nlt-fab-btn--disabled' : ''}`}
        onClick={() => {
          if (!selectedIntegrationId) return;
          setNltOpen((v) => !v);
        }}
        disabled={!selectedIntegrationId}
        title={selectedIntegrationId ? 'Natural Language Tuner' : 'Select an integration first'}
        aria-label="Toggle NLT panel"
      >
        {nltOpen
          ? <><i className="fas fa-times" aria-hidden="true" /> Close</>
          : <><i className="fas fa-magic" aria-hidden="true" /> NLT</>
        }
      </button>


      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <ConnectorSectionShell
          eyebrow={getConnectorTab('integrations').eyebrow}
          title={getConnectorTab('integrations').title}
          subtitle={getConnectorTab('integrations').subtitle}
          icon={getConnectorTab('integrations').icon}
          onHelp={() => setShowOnboarding(true)}
          actions={
            <>
              <button
                onClick={() => void loadMyIntegrations()}
                className="connector-shell-btn connector-shell-btn--ghost"
                disabled={loading}
              >
                <i className="fas fa-sync-alt" aria-hidden="true" />
                {loading ? 'Loading…' : 'Refresh'}
              </button>
              <button
                id="create-integration-btn"
                onClick={() => {
                  setShowCreateForm(true);
                  setFormData({ url: '', apiKey: '' });
                  setError(null);
                }}
                className="connector-shell-btn connector-shell-btn--primary"
                disabled={loading}
              >
                <i className="fas fa-plus" aria-hidden="true" />
                Create Integration
              </button>
            </>
          }
        >
        <>

      {/* Create Integration Form */}
      {showCreateForm && (
        <div className="connector-form-modal">
          <div className="connector-form-content" ref={createIntegrationFormRef}>
            <h3>Create New Integration</h3>
            {createIntegrationValidation.bannerError && (
              <div
                id={INTEGRATION_ERROR_BANNER_ID}
                className="connector-message error"
                style={{ marginBottom: '16px' }}
                role="alert"
              >
                <i className="fas fa-exclamation-circle"></i> {createIntegrationValidation.bannerError}
              </div>
            )}
            <ModalFormField
              id={INTEGRATION_URL_ID}
              label="URL"
              required
              error={createIntegrationValidation.fieldErrors[INTEGRATION_URL_ID]}
              className="form-group"
            >
              <input
                id={INTEGRATION_URL_ID}
                type="text"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://dev.sdk.aipsy.tech"
                disabled={formLoading}
                aria-invalid={Boolean(createIntegrationValidation.fieldErrors[INTEGRATION_URL_ID])}
              />
            </ModalFormField>
            <div className="form-group">
              <label>
                API Key
                <input
                  type="text"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  placeholder="Enter API key if required"
                  disabled={formLoading}
                />
              </label>
              <div className="form-hint-text" style={{ marginTop: '6px', fontSize: '12px', color: '#5B7893' }}>
                Optional: Used for authentication if your integration requires it. Some integrations may require an API key to function properly.
              </div>
            </div>
            <div className="form-actions">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ url: '', apiKey: '' });
                  createIntegrationValidation.clearErrors();
                }}
                className="btn-secondary"
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateIntegration}
                className="btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="connector-form-modal">
          <div className="connector-form-content" style={{ maxWidth: '600px' }}>
            <h3>Integration Details</h3>
            {detailsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner spinner--small"></div>
                <p>Loading details...</p>
              </div>
            ) : detailsError ? (
              <div className="connector-message error" style={{ margin: '0 0 20px 0' }}>
                <i className="fas fa-exclamation-circle"></i> {detailsError}
              </div>
            ) : detailsData ? (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {(() => {
                    const merged = mergeIntegrationDetailLayers(detailsData);
                    const idStr = String(merged.id ?? merged._id ?? '').trim();
                    const urlStr = String(merged.url ?? merged.endpoint ?? '').trim();
                    const apiKeyStr = pickIntegrationApiKey(merged);
                    const createdRaw = merged.created_at ?? merged.createdAt;
                    const updatedRaw = merged.updated_at ?? merged.updatedAt;
                    const cb = getConnectorIntegrationCreatedByDisplay(merged as any);
                    const cbExpandable = cb.full !== cb.text && cb.full !== '-';
                    return (
                      <>
                  <div>
                    <label style={{ fontWeight: 600, color: '#0A2A47', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                      Integration ID
                    </label>
                    <div style={{ 
                      padding: '10px 12px', 
                      background: '#F5F9FE', 
                      borderRadius: '6px', 
                      fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: '13px',
                      wordBreak: 'break-all'
                    }}>
                      {idStr || '-'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, color: '#0A2A47', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                      URL
                    </label>
                    <div style={{ 
                      padding: '10px 12px', 
                      background: '#F5F9FE', 
                      borderRadius: '6px', 
                      fontSize: '14px',
                      wordBreak: 'break-all'
                    }}>
                      {urlStr || '-'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, color: '#0A2A47', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                      API Key
                    </label>
                    <div style={{
                      padding: '10px 12px',
                      background: '#F5F9FE',
                      borderRadius: '6px',
                      fontSize: '14px',
                      wordBreak: 'break-all',
                      fontFamily: apiKeyStr ? 'monospace' : 'inherit',
                    }}>
                      {apiKeyStr ?? '—'}
                    </div>
                  </div>
                        {cb.text !== '-' && (
                          <div>
                            <label style={{ fontWeight: 600, color: '#0A2A47', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                              Created by
                            </label>
                            <div
                              role={cbExpandable ? 'button' : undefined}
                              tabIndex={cbExpandable ? 0 : undefined}
                              onClick={() => cbExpandable && setDetailsCreatedByExpanded((v) => !v)}
                              onKeyDown={(e) => {
                                if (!cbExpandable) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setDetailsCreatedByExpanded((v) => !v);
                                }
                              }}
                              style={{
                                padding: '10px 12px',
                                background: '#F5F9FE',
                                borderRadius: '6px',
                                fontSize: '14px',
                                wordBreak: detailsCreatedByExpanded ? 'break-all' : 'normal',
                                cursor: cbExpandable ? 'pointer' : 'default',
                              }}
                              title={
                                cbExpandable
                                  ? detailsCreatedByExpanded
                                    ? 'Click to show shortened'
                                    : 'Click to show full value'
                                  : cb.title || undefined
                              }
                            >
                              {detailsCreatedByExpanded ? cb.full : cb.text}
                            </div>
                          </div>
                        )}
                  <div>
                    <label style={{ fontWeight: 600, color: '#0A2A47', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                      Created At
                    </label>
                    <div style={{
                      padding: '10px 12px',
                      background: '#F5F9FE',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}>
                      {formatConnectorIntegrationCreatedAt(createdRaw)}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, color: '#0A2A47', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                      Updated At
                    </label>
                    <div style={{
                      padding: '10px 12px',
                      background: '#F5F9FE',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}>
                      {formatConnectorIntegrationCreatedAt(updatedRaw)}
                    </div>
                  </div>
                      </>
                    );
                  })()}
                  <div>
                    <label style={{ fontWeight: 600, color: '#0A2A47', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                      Full Data (JSON)
                    </label>
                    <pre style={{ 
                      padding: '12px', 
                      background: '#F5F9FE', 
                      borderRadius: '6px', 
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: '200px',
                      margin: 0
                    }}>
                      {JSON.stringify(detailsData, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="form-actions" style={{ marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setDetailsData(null);
                  setDetailsError(null);
                }}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Integration Form */}
      {showEditForm && selectedIntegration && (
        <div className="connector-form-modal">
          <div className="connector-form-content" ref={editIntegrationFormRef}>
            <h3>Edit Integration</h3>
            {editIntegrationValidation.bannerError && (
              <div
                id={INTEGRATION_ERROR_BANNER_ID}
                className="connector-message error"
                style={{ marginBottom: '16px' }}
                role="alert"
              >
                <i className="fas fa-exclamation-circle"></i> {editIntegrationValidation.bannerError}
              </div>
            )}
            <ModalFormField
              id={INTEGRATION_URL_ID}
              label="URL"
              required
              error={editIntegrationValidation.fieldErrors[INTEGRATION_URL_ID]}
              className="form-group"
            >
              <input
                id={INTEGRATION_URL_ID}
                type="text"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://dev.sdk.aipsy.tech"
                disabled={formLoading}
                aria-invalid={Boolean(editIntegrationValidation.fieldErrors[INTEGRATION_URL_ID])}
              />
            </ModalFormField>
            <div className="form-group">
              <label>
                API Key
                <input
                  type="text"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  placeholder="Enter API key if required"
                  disabled={formLoading}
                />
              </label>
              <div className="form-hint-text" style={{ marginTop: '6px', fontSize: '12px', color: '#5B7893' }}>
                Optional: Used for authentication if your integration requires it. Some integrations may require an API key to function properly.
              </div>
            </div>
            <div className="form-actions">
              <button
                onClick={() => {
                  setShowEditForm(false);
                  setFormData({ url: '', apiKey: '' });
                  editIntegrationValidation.clearErrors();
                }}
                className="btn-secondary"
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleEditIntegration}
                className="btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Updating…' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Integrations List */}
      <ConnectorIntegrationsPicker
        integrations={integrations.map((it) => ({
          id: String(it?.id || it?._id || ''),
          url: String(it?.url || it?.endpoint || '-'),
          createdAtLabel: formatConnectorIntegrationCreatedAt(it?.created_at),
          raw: it as Record<string, unknown>,
        }))}
        selectedIntegrationId={selectedIntegrationId}
        onSelect={selectIntegration}
        onEdit={(integration) => void handleEditIntegrationFromList(integration.id, integration.raw)}
        onDetails={handleGetIntegration}
        onCopyId={handleCopyId}
        onDelete={handleDeleteIntegration}
        onCreate={() => {
          setShowCreateForm(true);
          setFormData({ url: '', apiKey: '' });
          setError(null);
        }}
      />

          {/* Config Editor */}
          {selectedIntegrationId && (
            <div className="connector-config-layout">
            <div className="connector-config-section" id="connector-config-section">
              <h3>
                Integration Configurator {selectedIntegrationId ? `(${selectedIntegrationId.substring(0, 8)}...)` : ''}
              </h3>

              {configLoading ? (
                <div className="connector-loading">
                  <div className="spinner spinner--small"></div>
                  <p>Loading config…</p>
                </div>
              ) : (
                <>
                  {configError && (
                    <div className="connector-message error">
                      <i className="fas fa-exclamation-circle"></i> {configError}
                    </div>
                  )}

                  <div style={{ marginBottom: '-20px' }}>
                    <AgentConfigurator
                      key={nltPatchReview ? `nlt-patch-${nltPatchReview.pendingRun.id}` : 'connector-config'}
                      hideApproach
                      hideReminderPromptPosition
                      availableModels={configuratorAvailableModels}
                      modelLabels={configuratorModelLabels}
                      initialConfig={nltConfiguratorInitialConfig}
                      initialLabel={configLabel}
                      patchReview={agentConfiguratorPatchReview}
                      onImportRawConfig={(raw) => {
                        connectorConfigPreservationRef.current = raw;
                      }}
                      onSave={async (cfg, label) => {
                        setCurrentConfig(cfg);
                        await handleSaveConfig(cfg, label);
                        if (nltPatchReview) {
                          setNltPatchReview(null);
                        }
                      }}
                      onConfigChange={(cfg) => {
                        setCurrentConfig(cfg);
                        // Initial configuration must stay frozen for the current optimization run.
                        // Once optimization has started (or results already exist), do not pull initial snapshot from configurator.
                        const shouldSyncInitialSnapshot =
                          !connectorMultiAgentIsOptimizing && connectorMultiAgentIterationResults.length === 0;
                        if (!shouldSyncInitialSnapshot) {
                          return;
                        }
                        if (cfg?.coordinator) {
                          setConnectorCoordinatorPrompt(cfg.coordinator.prompt || '');
                          setConnectorCoordinatorModel(cfg.coordinator.model || 'gpt-4.1-mini');
                          setConnectorCoordinatorTemperature(cfg.coordinator.temperature ?? 0.8);
                          setConnectorCoordinatorDescription(cfg.coordinator.description || '');
                          setConnectorCoordinatorReminderPrompt(cfg.coordinator.reminder_prompt || '');
                          setConnectorCoordinatorName(
                            typeof cfg.coordinator.name === 'string' ? cfg.coordinator.name : 'Coordinator'
                          );
                          setConnectorCoordinatorBackendId(
                            typeof cfg.coordinator.id === 'string'
                              ? cfg.coordinator.id.trim() || undefined
                              : undefined
                          );
                          setConnectorCoordinatorAgentId(
                            typeof cfg.coordinator.agent_id === 'string'
                              ? cfg.coordinator.agent_id.trim() || undefined
                              : typeof cfg.coordinator.id === 'string'
                                ? cfg.coordinator.id.trim() || undefined
                                : undefined
                          );
                        }
                        if (cfg?.workers && Array.isArray(cfg.workers)) {
                          setConnectorWorkers(
                            cfg.workers.map((w, index) => ({
                              id: normalizeWorkerIdString(w.id, index),
                              name: w.name || '',
                              prompt: w.prompt || '',
                              model: w.model || 'gpt-4.1-mini',
                              temperature: w.temperature ?? 0.7,
                              description: w.description || '',
                              // Preserve sync/async mode (avoid defaulting to synchronous)
                              mode: (w.mode === 'asynchronous' ? 'asynchronous' : 'synchronous') as 'synchronous' | 'asynchronous',
                              reminder_prompt: w.reminder_prompt,
                            }))
                          );
                        }
                        setConnectorMetaPrompts(mergeMetaPromptsWithFinalStepDefault(cfg?.metaPrompts));
                        setConnectorReminderPromptPosition(
                          normalizeReminderPromptPosition(cfg?.reminder_prompt_position)
                        );
                      }}
                      onCancel={undefined}
                    />
                  </div>

                  {nltOpen && (
                    <NLTConfigCompareChats
                      mode="nlt"
                      integrationId={selectedIntegrationId}
                      beforeConfig={nltBeforeChatConfig}
                      afterConfig={nltAfterChatConfig}
                      compareKey="nlt"
                      onQuoteToNlt={(quote) => {
                        setNltPendingQuote(quote);
                        setNltOpen(true);
                      }}
                    />
                  )}

                  {/* Optimization Section */}
                  <div id="optimize-section" className="connector-optimize-section">
                    <div className="connector-optimize-actions-bar">
                      <button
                        type="button"
                        id="connector-optimize-open-modal-btn"
                        onClick={handleOpenConfigTuningModal}
                        disabled={!currentConfig || !selectedIntegrationId}
                        style={{
                          padding: '12px 24px',
                          borderRadius: '10px',
                          border: 'none',
                          background: !currentConfig || !selectedIntegrationId ? '#C6E0F7' : 'var(--brand-primary, #FF8300)',
                          color: '#FFFFFF',
                          fontSize: '15px',
                          fontWeight: 700,
                          cursor: !currentConfig || !selectedIntegrationId ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s ease',
                          boxShadow: !currentConfig || !selectedIntegrationId ? 'none' : '0 2px 8px rgba(255, 131, 0, 0.3)',
                          minHeight: '44px',
                        }}
                        title="Configure and start config tuning job"
                      >
                        <i className="fas fa-rocket" aria-hidden />
                        <span>Start Config Tuning Job</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          if (selectedIntegrationId) void loadConnectorDatasets(selectedIntegrationId);
                          setEvaluationRunAgainSeed(null);
                          setEvaluationRunAgainKey(null);
                          setShowEvaluationRunModal(true);
                        }}
                        disabled={!currentConfig || !selectedIntegrationId}
                        style={{
                          padding: '12px 24px',
                          borderRadius: '10px',
                          border: '1px solid #0099FA',
                          background: !currentConfig || !selectedIntegrationId ? '#F5F9FE' : '#FFFFFF',
                          color: !currentConfig || !selectedIntegrationId ? '#7F9AB3' : '#0099FA',
                          fontSize: '15px',
                          fontWeight: 700,
                          cursor: !currentConfig || !selectedIntegrationId ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          minHeight: '44px',
                        }}
                        title="Run evaluation on a dataset batch without config tuning"
                      >
                        <i className="fas fa-chart-line" aria-hidden />
                        <span>Run Evaluation</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleOpenTrainingJobModal}
                        disabled={trainingJobLoading || !currentConfig || !selectedIntegrationId}
                        style={{
                          padding: '12px 24px',
                          borderRadius: '10px',
                          border: '1px solid var(--brand-primary, #FF8300)',
                          background: trainingJobLoading || !currentConfig || !selectedIntegrationId ? '#F5F9FE' : '#FFFFFF',
                          color: trainingJobLoading || !currentConfig || !selectedIntegrationId ? '#7F9AB3' : 'var(--brand-primary, #FF8300)',
                          fontSize: '15px',
                          fontWeight: 700,
                          cursor: trainingJobLoading || !currentConfig || !selectedIntegrationId ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 0 0 1px #E3F0FB',
                          minHeight: '44px',
                        }}
                        title="Start Fine Tuning Job for agents marked as trainable"
                      >
                        {trainingJobLoading ? (
                          <>
                            <div className="spinner spinner--small" style={{ width: '16px', height: '16px' }} />
                            <span>Starting…</span>
                          </>
                        ) : (
                          <>
                            <i className="fas fa-graduation-cap" aria-hidden />
                            <span>Start Fine Tuning Job</span>
                          </>
                        )}
                      </button>

                      {(connectorMultiAgentIsOptimizing || isOptimizationCancelPending) ? (
                        <button
                          type="button"
                          onClick={() => void handleStop()}
                          disabled={isOptimizationCancelPending}
                          aria-busy={isOptimizationCancelPending}
                          style={{
                            padding: '12px 28px',
                            borderRadius: '10px',
                            border: 'none',
                            background: isOptimizationCancelPending ? '#C6E0F7' : '#FF8300',
                            color: '#FFFFFF',
                            fontSize: '15px',
                            fontWeight: 700,
                            cursor: isOptimizationCancelPending ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 0 0 1px #E3F0FB',
                            minHeight: '44px',
                            opacity: isOptimizationCancelPending ? 0.85 : 1,
                          }}
                          title="Cancel queued or running config tuning job"
                        >
                          <i
                            className={`fas ${isOptimizationCancelPending ? 'fa-spinner fa-spin' : 'fa-ban'}`}
                            aria-hidden
                          />
                          <span>{getJobCancelButtonLabel(isOptimizationCancelPending, 'Cancel job')}</span>
                        </button>
                      ) : null}

                      {(isEvaluationJobRunning || isEvaluationCancelPending) && evaluationPollingJobId ? (
                        <button
                          type="button"
                          onClick={() => void handleCancelEvaluationJob(evaluationPollingJobId)}
                          disabled={isEvaluationCancelPending}
                          aria-busy={isEvaluationCancelPending}
                          style={{
                            padding: '12px 28px',
                            borderRadius: '10px',
                            border: 'none',
                            background: isEvaluationCancelPending ? '#C6E0F7' : '#FF8300',
                            color: '#FFFFFF',
                            fontSize: '15px',
                            fontWeight: 700,
                            cursor: isEvaluationCancelPending ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 0 0 1px #E3F0FB',
                            minHeight: '44px',
                            opacity: isEvaluationCancelPending ? 0.85 : 1,
                          }}
                          title="Cancel queued or running evaluation job"
                        >
                          <i
                            className={`fas ${isEvaluationCancelPending ? 'fa-spinner fa-spin' : 'fa-ban'}`}
                            aria-hidden
                          />
                          <span>{getJobCancelButtonLabel(isEvaluationCancelPending, 'Cancel evaluation')}</span>
                        </button>
                      ) : null}
                    </div>

                    {/* Progress Bar */}
                    {connectorMultiAgentIsOptimizing || isOptimizationCancelPending ? (
                      <div className="connector-optimize-progress">
                        <OptimizationJobProgressBar
                          percent={connectorOptimizationProgressDisplay}
                          message={optimizationProgressMessage}
                          meta={trackedOptimizationJobProgressDisplay?.meta}
                          job={trackedOptimizationJobSnapshot}
                        />
                      </div>
                    ) : null}

                    {(isEvaluationJobRunning || isEvaluationCancelPending) ? (
                      <div id="evaluation-job-progress" style={{ marginBottom: '20px' }}>
                        <EvaluationJobProgressBar
                          percent={evaluationProgressDisplay}
                          message={
                            isEvaluationCancelPending ? 'Cancelling…' : evaluationProgressBarMeta?.message
                          }
                          meta={evaluationProgressBarMeta?.meta}
                          title="Evaluation progress"
                          accentColor="#0099FA"
                        />
                      </div>
                    ) : null}

                    {/* Completion Message */}
                    {showCompletionMessage && connectorMultiAgentIsOptimized && !connectorMultiAgentIsOptimizing && !optimizationStoppedByUser && (
                      <div style={{
                        padding: '16px',
                        background: 'rgba(0, 153, 250, 0.1)',
                        border: '1px solid #0099FA',
                        borderRadius: '8px',
                        marginTop: '20px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <i className="fas fa-check-circle" style={{ color: '#0099FA', fontSize: '20px' }}></i>
                        <div>
                          <div style={{ fontWeight: 600, color: '#0A2A47', marginBottom: '4px' }}>Optimization Completed!</div>
                          <div style={{ fontSize: '14px', color: '#5B7893' }}>Check the results below to see the improvements.</div>
                        </div>
                      </div>
                    )}

                    {/* Stopped Message */}
                    {optimizationStoppedByUser && !connectorMultiAgentIsOptimizing && (
                      <div style={{
                        padding: '16px',
                        background: 'rgba(91, 120, 147, 0.1)',
                        border: '1px solid #7F9AB3',
                        borderRadius: '8px',
                        marginTop: '20px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '12px',
                        flexWrap: 'wrap',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: 220 }}>
                          <i className="fas fa-stop-circle" style={{ color: '#5B7893', fontSize: '20px', marginTop: 2 }} aria-hidden />
                          <div>
                            <div style={{ fontWeight: 600, color: '#0A2A47', marginBottom: '4px' }}>Config Tuning Stopped</div>
                            <div style={{ fontSize: '14px', color: '#5B7893' }}>
                              The run was cancelled before completion. Partial results below are not marked as a successful finish.
                            </div>
                          </div>
                        </div>
                        {terminalOptimizationJob?.retryable ? (() => {
                          const isTerminalRetrying = isJobRetryUiRowActive(optimizationJobRetryingRowKey, {
                            kind: 'terminal',
                            jobId: terminalOptimizationJob.id,
                          });
                          return (
                          <button
                            type="button"
                            className="connector-shell-btn connector-shell-btn--primary"
                            onClick={() =>
                              void handleRetryOptimizationJob(
                                terminalOptimizationJob.id,
                                buildJobRetryUiRowKey({ kind: 'terminal', jobId: terminalOptimizationJob.id })
                              )
                            }
                            disabled={isTerminalRetrying}
                            aria-busy={isTerminalRetrying}
                          >
                            <i
                              className={`fas ${isTerminalRetrying ? 'fa-spinner fa-spin' : 'fa-redo'}`}
                              aria-hidden
                            />
                            {isTerminalRetrying ? 'Retrying…' : 'Retry'}
                          </button>
                          );
                        })() : null}
                      </div>
                    )}

                    {terminalOptimizationJob &&
                    !connectorMultiAgentIsOptimizing &&
                    !isCancelledOptimizationJobStatus(terminalOptimizationJob.status) &&
                    !isSucceededOptimizationJobStatus(terminalOptimizationJob.status) &&
                    (isFailedOptimizationJobStatus(terminalOptimizationJob.status) ||
                      isTerminalOptimizationJobStatus(terminalOptimizationJob.status) ||
                      terminalOptimizationJob.status === 'poll_error') && (
                      <div style={{
                        padding: '16px',
                        background: 'rgba(255, 131, 0, 0.08)',
                        border: '1px solid #FFD9B3',
                        borderRadius: '8px',
                        marginTop: '20px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '12px',
                        flexWrap: 'wrap',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: 220 }}>
                          <i className="fas fa-times-circle" style={{ color: '#FF8300', fontSize: '20px', marginTop: 2 }} aria-hidden />
                          <div>
                            <div style={{ fontWeight: 600, color: '#0A2A47', marginBottom: '4px' }}>Config Tuning Failed</div>
                            <FailedJobErrorMessage
                              rawError={terminalOptimizationJob.rawError}
                              fallbackMessage="The config tuning job did not complete successfully."
                            />
                          </div>
                        </div>
                        {terminalOptimizationJob.retryable ? (() => {
                          const isTerminalRetrying = isJobRetryUiRowActive(optimizationJobRetryingRowKey, {
                            kind: 'terminal',
                            jobId: terminalOptimizationJob.id,
                          });
                          return (
                          <button
                            type="button"
                            className="connector-shell-btn connector-shell-btn--primary"
                            onClick={() =>
                              void handleRetryOptimizationJob(
                                terminalOptimizationJob.id,
                                buildJobRetryUiRowKey({ kind: 'terminal', jobId: terminalOptimizationJob.id })
                              )
                            }
                            disabled={isTerminalRetrying}
                            aria-busy={isTerminalRetrying}
                          >
                            <i
                              className={`fas ${isTerminalRetrying ? 'fa-spinner fa-spin' : 'fa-redo'}`}
                              aria-hidden
                            />
                            {isTerminalRetrying ? 'Retrying…' : 'Retry'}
                          </button>
                          );
                        })() : null}
                      </div>
                    )}

                    {/* Optimization Results */}
                    <div style={{ marginTop: '32px' }}>
                      {/* Configuration Visualization */}
                      <div style={{ marginBottom: '32px' }}>
                        <MultiAgentConfigVisualization
                          isConnector={true}
                          onLoadOptimizedStepIntoConfigurator={requestExportStepToConfigurator}
                        />
                      </div>

                      <div style={{ marginBottom: '24px' }}>
                        <NLTConfigCompareChats
                          mode="optimization"
                          integrationId={selectedIntegrationId}
                          beforeConfig={optimizationCompareConfigs.before}
                          afterConfig={optimizationCompareConfigs.after}
                          compareKey={`opt-${connectorMultiAgentSelectedIterationIndex}-${connectorMultiAgentIterationResults.length}`}
                          afterSubtitle={`${optimizationCompareConfigs.stepLabel} config from optimization`}
                        />
                      </div>

                      {/* Evaluation Graphs */}
                      <ConnectorEvaluationGraphs
                        isOptimizing={connectorMultiAgentIsOptimizing}
                        progress={connectorOptimizationProgressDisplay}
                        requestedIterations={optimizationIterations}
                        primaryMetricId={selectionScenario}
                      />
                    </div>

                    {/* Training Job Result */}
                    {trainingJobResult && (() => {
                        const jobData = trainingJobResult?.data || trainingJobResult;
                        const resultJobId = resolveTrainingJobRecordId(jobData);
                        const resultJobStatus = resolveEffectiveTrainingJobStatus(jobData);
                        const ftJobErrorDisplay =
                          jobData?.status === 'failed'
                            ? resolveFineTuningJobError(jobData as Record<string, unknown>)
                            : null;
                        const ftRows = getFineTuningGroups(jobData);
                        const getStatusColor = (status: string) => {
                          switch (status?.toLowerCase()) {
                            case 'running':
                            case 'queued': return '#0099FA';
                            case 'completed':
                            case 'succeeded': return '#0072C6';
                            case 'failed': return '#FF8300';
                            case 'pending': return '#FF8300';
                            case 'cancelled': return '#5B7893';
                            default: return '#5B7893';
                          }
                        };
                        const formatDate = (dateString: string) => {
                          if (!dateString) return '-';
                          try {
                            return new Date(dateString).toLocaleString();
                          } catch {
                            return dateString;
                          }
                        };
                        const getStatusClass = (status: string) => {
                          switch (status?.toLowerCase()) {
                            case 'succeeded':
                            case 'completed': return 'training-job-status-completed';
                            case 'partially_succeeded': return 'training-job-status-partially-succeeded';
                            case 'failed': return 'training-job-status-failed';
                            case 'running': return 'training-job-status-running';
                            case 'queued':
                            case 'pending': return 'training-job-status-pending';
                            case 'cancelled': return 'training-job-status-failed';
                            default: return 'training-job-status-running';
                          }
                        };
                        const ftRowTitle = (r: Record<string, unknown>) => {
                          const names = r.agent_names;
                          if (Array.isArray(names) && names.length > 0) {
                            return names.filter((x) => typeof x === 'string').join(', ');
                          }
                          const an = r.agent_name;
                          if (typeof an === 'string' && an.trim()) return an;
                          const mn = r.model_name;
                          if (typeof mn === 'string' && mn.trim()) return mn;
                          return '—';
                        };
                        return (
                          <div id="training-job-result-details" className="training-job-result">
                            <div className="training-job-result-header">
                              <h4 className="training-job-result-title">
                                {jobData?.status === 'succeeded' || jobData?.status === 'completed' ? (
                                  <i className="fas fa-check-circle" style={{ color: '#0072C6', fontSize: '20px' }}></i>
                                ) : jobData?.status === 'failed' ? (
                                  <i className="fas fa-times-circle" style={{ color: '#FF8300', fontSize: '20px' }}></i>
                                ) : jobData?.status === 'partially_succeeded' ? (
                                  <i className="fas fa-exclamation-triangle" style={{ color: '#FF8300', fontSize: '20px' }}></i>
                                ) : (
                                  <i className="fas fa-clock" style={{ color: '#0099FA', fontSize: '20px' }}></i>
                                )}
                                Fine Tuning Job {jobData?.status === 'succeeded' || jobData?.status === 'completed' ? 'Completed' : jobData?.status === 'failed' ? 'Failed' : jobData?.status === 'partially_succeeded' ? 'Partially Succeeded' : 'Details'}
                              </h4>
                              <div className="training-job-result-actions">
                                {resultJobId && canCancelJob(resultJobStatus) && (() => {
                                  const isFtCancelPending = isJobCancellationPending(
                                    resultJobId,
                                    trainingJobCancellingId,
                                    resultJobStatus
                                  );
                                  return (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      void handleCancelTrainingJob(resultJobId);
                                    }}
                                    disabled={isFtCancelPending || isPolling}
                                    aria-busy={isFtCancelPending}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      border: 'none',
                                      background: isFtCancelPending ? '#C6E0F7' : '#FF8300',
                                      color: '#FFFFFF',
                                      cursor: isFtCancelPending ? 'not-allowed' : 'pointer',
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                    }}
                                    title="Cancel queued or running fine-tuning job"
                                  >
                                    {isFtCancelPending ? (
                                      <>
                                        <div className="spinner spinner--small" style={{ width: '12px', height: '12px' }}></div>
                                        <span>{getJobCancelButtonLabel(true)}</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-ban" aria-hidden />
                                        <span>{getJobCancelButtonLabel(false)}</span>
                                      </>
                                    )}
                                  </button>
                                  );
                                })()}
                                {resultJobId && canRetryTrainingJob(resultJobStatus) && (() => {
                                  const resultRetryScope = {
                                    kind: 'result' as const,
                                    jobId: resultJobId,
                                  };
                                  const isResultRetrying = isJobRetryUiRowActive(
                                    trainingJobRetryingRowKey,
                                    resultRetryScope
                                  );
                                  return (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      void handleRetryTrainingJob(
                                        resultJobId,
                                        buildJobRetryUiRowKey(resultRetryScope)
                                      );
                                    }}
                                    disabled={isResultRetrying || isPolling}
                                    aria-busy={isResultRetrying}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      border: '1px solid #FF8300',
                                      background: isResultRetrying ? '#C6E0F7' : '#FFFFFF',
                                      color: isResultRetrying ? '#5B7893' : '#FF8300',
                                      cursor: isResultRetrying || isPolling ? 'not-allowed' : 'pointer',
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                    }}
                                    title="Create a new fine-tuning job from this one"
                                  >
                                    {isResultRetrying ? (
                                      <>
                                        <div className="spinner spinner--small" style={{ width: '12px', height: '12px' }}></div>
                                        <span>Retrying…</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-redo" aria-hidden />
                                        <span>Retry</span>
                                      </>
                                    )}
                                  </button>
                                  );
                                })()}
                                {resultJobId && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      void pollTrainingJobStatus(resultJobId, { force: true });
                                    }}
                                    disabled={isPolling}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      border: '1px solid #0099FA',
                                      background: isPolling ? '#C6E0F7' : '#0099FA',
                                      color: '#FFFFFF',
                                      cursor: isPolling ? 'not-allowed' : 'pointer',
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isPolling) {
                                        e.currentTarget.style.background = '#0099FA';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isPolling) {
                                        e.currentTarget.style.background = '#0099FA';
                                      }
                                    }}
                                    title="Refresh status"
                                  >
                                    {isPolling ? (
                                      <>
                                        <div className="spinner spinner--small" style={{ width: '12px', height: '12px' }}></div>
                                        <span>Updating...</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-sync-alt"></i>
                                        <span>Refresh</span>
                                      </>
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    clearConnectorFtPollingPersist(selectedIntegrationId);
                                    setTrainingJobResult(null);
                                    setPollingJobId(null);
                                    if (pollingIntervalRef.current) {
                                      clearInterval(pollingIntervalRef.current);
                                      pollingIntervalRef.current = null;
                                    }
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#5B7893',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    transition: 'all 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#E3F0FB';
                                    e.currentTarget.style.color = '#0A2A47';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'none';
                                    e.currentTarget.style.color = '#5B7893';
                                  }}
                                  title="Close"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            </div>

                            {ftJobErrorDisplay ? (
                              <div style={{ marginBottom: 16 }}>
                                <FailedJobErrorMessage
                                  rawError={ftJobErrorDisplay.raw}
                                  friendlyMessage={ftJobErrorDisplay.friendly}
                                  details={ftJobErrorDisplay.details}
                                  fallbackMessage="Fine-tuning job failed"
                                />
                              </div>
                            ) : null}

                            {/* Main Job Info */}
                            <div className="training-job-info-grid">
                              <div className="training-job-info-item">
                                <label className="training-job-info-label">
                                  Internal Job ID
                                </label>
                                <div className="training-job-info-value" style={{ fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                  <span>{jobData?.id || '-'}</span>
                                  {jobData?.id && (
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(jobData.id);
                                        setNotification({
                                          isVisible: true,
                                          type: 'success',
                                          message: 'Internal Job ID copied to clipboard!',
                                        });
                                        setTimeout(() => {
                                          setNotification(prev => ({ ...prev, isVisible: false }));
                                        }, NOTIFICATION_AUTO_HIDE_MS);
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#0099FA',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginLeft: 'auto',
                                      }}
                                      title="Copy Internal Job ID"
                                    >
                                      <i className="fas fa-copy" style={{ fontSize: '12px' }}></i>
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div className="training-job-info-item">
                                <label className="training-job-info-label">
                                  Status
                                </label>
                                <div className={`training-job-status ${getStatusClass(jobData?.status)}`}>
                                  <i className="fas fa-circle" style={{ fontSize: '8px' }}></i>
                                  {getTrainingJobStatusLabel(jobData?.status)}
                                </div>
                              </div>

                              {resolveFineTuningJobProgressMetrics(
                                jobData?.status,
                                jobData?.progress_percent,
                                pollingJobId === resultJobId ? trainingJobsApiProgress : undefined
                              ).show ? (
                                <div className="training-job-info-item" style={{ gridColumn: '1 / -1' }}>
                                  <FineTuningJobProgress
                                    status={jobData?.status}
                                    progressPercent={jobData?.progress_percent}
                                    displayPercent={
                                      pollingJobId === resultJobId ? trainingJobsApiProgress : undefined
                                    }
                                  />
                                </div>
                              ) : null}

                              <div className="training-job-info-item">
                                <label className="training-job-info-label">
                                  Integration ID
                                </label>
                                <div className="training-job-info-value" style={{ fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                  {jobData?.integration_id || '-'}
                                </div>
                              </div>

                              <div className="training-job-info-item">
                                <label className="training-job-info-label">
                                  Created At
                                </label>
                                <div className="training-job-info-value">
                                  {formatDate(jobData?.created_at)}
                                </div>
                              </div>
                              {jobData?.completed_at && (
                                <div className="training-job-info-item">
                                  <label className="training-job-info-label">
                                    Completed At
                                  </label>
                                  <div className="training-job-info-value">
                                    {formatDate(jobData.completed_at)}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Training Period */}
                            {(jobData?.training_period_from || jobData?.training_period_to) && (
                              <div style={{ marginBottom: '24px', padding: '16px', background: '#FFFFFF', borderRadius: '8px' }}>
                                <label style={{ fontSize: '13px', color: '#4A6683', fontWeight: 600, marginBottom: '12px', display: 'block' }}>
                                  Training Period
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                  <div>
                                    <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>From</div>
                                    <div style={{ fontSize: '14px', color: '#0A2A47', fontWeight: 500 }}>
                                      {formatDate(jobData.training_period_from)}
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>To</div>
                                    <div style={{ fontSize: '14px', color: '#0A2A47', fontWeight: 500 }}>
                                      {formatDate(jobData.training_period_to)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Fine-tuning groups (API) or legacy agents[] */}
                            {ftRows.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '13px', color: '#4A6683', fontWeight: 600, marginBottom: '12px', display: 'block' }}>
                                  {jobData?.group_by_base_model !== false ? 'Fine-tuning groups' : 'Agents'} ({ftRows.length})
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                  {ftRows.map((agent: Record<string, unknown>, index: number) => (
                                    <div key={(typeof agent.job_id === 'string' && agent.job_id) || (typeof agent.base_model === 'string' && agent.base_model) || String(index)} style={{ 
                                      padding: '16px', 
                                      background: '#FFFFFF', 
                                      borderRadius: '8px', 
                                      border: '1px solid #C6E0F7'
                                    }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#0A2A47' }}>
                                          {ftRowTitle(agent)}
                                        </div>
                                        <div style={{ 
                                          padding: '6px 12px', 
                                          background: `${getStatusColor(String(agent.status || ''))}15`, 
                                          borderRadius: '6px',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          color: getStatusColor(String(agent.status || '')),
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px'
                                        }}>
                                          <i className="fas fa-circle" style={{ fontSize: '6px' }}></i>
                                          {typeof agent.status === 'string' ? agent.status : '-'}
                                        </div>
                                      </div>
                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '13px' }}>
                                        <div>
                                          <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Model Name</div>
                                          <div style={{ color: '#0A2A47', fontWeight: 500 }}>{typeof agent.model_name === 'string' ? agent.model_name : '-'}</div>
                                        </div>
                                        <div>
                                          <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Base Model</div>
                                          <div style={{ color: '#0A2A47', fontWeight: 500 }}>
                                            {(typeof agent.base_model_label === 'string' && agent.base_model_label) ||
                                              (typeof agent.base_model === 'string' && agent.base_model) ||
                                              '-'}
                                          </div>
                                        </div>
                                        {agent.fine_tuned_model_id != null && String(agent.fine_tuned_model_id).trim() !== '' && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Fine-tuned model ID</div>
                                            <div style={{ color: '#0A2A47', fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                                              {String(agent.fine_tuned_model_id)}
                                            </div>
                                          </div>
                                        )}
                                        {'dataset_id' in agent && agent.dataset_id != null && String(agent.dataset_id).trim() !== '' && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Dataset ID</div>
                                            <div style={{ color: '#0A2A47', fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                                              {String(agent.dataset_id)}
                                            </div>
                                          </div>
                                        )}
                                        {'job_id' in agent && agent.job_id != null && String(agent.job_id).trim() !== '' && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Training Service Job ID</div>
                                            <div style={{ color: '#0A2A47', fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                                              {String(agent.job_id)}
                                            </div>
                                          </div>
                                        )}
                                        {'started_at' in agent && agent.started_at != null && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Started At</div>
                                            <div style={{ color: '#0A2A47', fontSize: '12px' }}>
                                              {formatDate(String(agent.started_at))}
                                            </div>
                                          </div>
                                        )}
                                        {'completed_at' in agent && agent.completed_at != null && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Completed At</div>
                                            <div style={{ color: '#0A2A47', fontSize: '12px' }}>
                                              {formatDate(String(agent.completed_at))}
                                            </div>
                                          </div>
                                        )}
                                        {typeof agent.dataset_tokens_raw === 'number' && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Dataset tokens (raw)</div>
                                            <div style={{ color: '#0A2A47', fontWeight: 500 }}>{agent.dataset_tokens_raw.toLocaleString()}</div>
                                          </div>
                                        )}
                                        {typeof agent.dataset_tokens_prepared === 'number' && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Dataset tokens (prepared)</div>
                                            <div style={{ color: '#0A2A47', fontWeight: 500 }}>{agent.dataset_tokens_prepared.toLocaleString()}</div>
                                          </div>
                                        )}
                                        {'training_token_count' in agent && agent.training_token_count != null && typeof agent.training_token_count === 'number' && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Training Tokens</div>
                                            <div style={{ color: '#0A2A47', fontWeight: 500 }}>{agent.training_token_count.toLocaleString()}</div>
                                          </div>
                                        )}
                                        {'validation_token_count' in agent && agent.validation_token_count != null && typeof agent.validation_token_count === 'number' && (
                                          <div>
                                            <div style={{ fontSize: '11px', color: '#5B7893', marginBottom: '4px' }}>Validation Tokens</div>
                                            <div style={{ color: '#0A2A47', fontWeight: 500 }}>{agent.validation_token_count.toLocaleString()}</div>
                                          </div>
                                        )}
                                      </div>
                                      {agent?.error != null && String(agent.error).trim() !== '' && (() => {
                                        const agentErrorRaw = String(agent.error).trim();
                                        const agentFriendly = formatFineTuningGroupErrorMessage(agentErrorRaw);
                                        const hideDuplicateAgentError =
                                          ftJobErrorDisplay != null &&
                                          (isFineTuningProviderLevelError(agentErrorRaw) ||
                                            normalizeJobErrorCompare(agentFriendly) ===
                                              normalizeJobErrorCompare(ftJobErrorDisplay.friendly));
                                        if (hideDuplicateAgentError) return null;
                                        return (
                                        <div style={{ marginTop: 10 }}>
                                          <FailedJobErrorMessage
                                            rawError={agentErrorRaw}
                                            friendlyMessage={agentFriendly}
                                            fallbackMessage="Agent failed"
                                            compact
                                          />
                                        </div>
                                        );
                                      })()}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          </div>
                        );
                      })()}

                    <div id="evaluation-job-history" className="training-job-history" style={{ marginBottom: 24 }}>
                      <div className="training-job-history-header">
                        <div>
                          <h4>
                            <i className="fas fa-history" aria-hidden />
                            Evaluation Jobs
                          </h4>
                          <p className="training-job-history-pagination-info">
                            Recent evaluation runs for this integration. Cancel queued or running jobs; retry failed or cancelled runs.
                          </p>
                        </div>
                        <button
                          type="button"
                          className="connector-shell-btn connector-shell-btn--ghost"
                          onClick={() => void loadEvaluationJobHistory()}
                          disabled={evaluationJobHistoryLoading || !selectedIntegrationId}
                        >
                          <i className={`fas ${evaluationJobHistoryLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} aria-hidden />
                          Refresh
                        </button>
                      </div>
                      {evaluationJobHistoryLoading && evaluationJobHistory.length === 0 ? (
                        <div className="training-job-history-loading">
                          <div className="spinner spinner--small" />
                          <p>Loading jobs…</p>
                        </div>
                      ) : evaluationJobHistory.length === 0 ? (
                        <div className="training-job-history-empty">
                          <i className="fas fa-chart-line" aria-hidden />
                          <p>No evaluation jobs yet for this integration.</p>
                        </div>
                      ) : (
                        <div className="training-job-history-list">
                          {evaluationJobHistory.map((job, index) => {
                            const jobRow = unwrapEvaluationJobRow(job);
                            const jobId = resolveEvaluationJobRecordId(jobRow);
                            const jobStatus = resolveEvaluationJobRawStatus(jobRow);
                            const jobDisplayStatus = resolveEvaluationJobDisplayStatus(jobRow);
                            const isTrackedActiveJob = Boolean(
                              jobId && evaluationPollingJobId === jobId && shouldTrackEvaluationJobRow(jobRow)
                            );
                            const jobProgress = isTrackedActiveJob
                              ? Math.round(evaluationProgressDisplay)
                              : resolveEvaluationJobProgressDisplay(jobRow).percent;
                            const progressMeta = resolveEvaluationJobProgressDisplay(jobRow);
                            const errorDisplay =
                              jobStatus === 'failed' && jobRow.error
                                ? resolveEvaluationJobError(jobRow)
                                : null;
                            const evalModeRaw = String(jobRow.evaluation_mode || '');
                            const evalModeLabel =
                              evalModeRaw && evalModeRaw in EVALUATION_MODE_LABELS
                                ? EVALUATION_MODE_LABELS[evalModeRaw as EvaluationMode]
                                : evalModeRaw || 'Evaluation job';
                            const canRunAgain = isTerminalEvaluationJobStatus(jobStatus);
                            const canRetry = canRetryEvaluationJob(jobStatus);
                            const isCancelling = isJobCancellationPending(
                              jobId,
                              evaluationJobCancellingId,
                              resolveEvaluationJobRawStatus(jobRow)
                            );
                            const isRetrying = isJobRetryUiRowActive(evaluationJobRetryingRowKey, {
                              kind: 'history',
                              jobId,
                              index,
                            });
                            const createdAt = jobRow.created_at
                              ? new Date(String(jobRow.created_at)).toLocaleString()
                              : '—';
                            return (
                              <div key={jobId || `eval-hist-${index}`} className="training-job-history-item">
                                <div className="training-job-history-item-header">
                                  <div className="training-job-history-item-title">
                                    <i className="fas fa-chart-line" aria-hidden />
                                    <span>{evalModeLabel}</span>
                                  </div>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: '#5B7893',
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    {formatEvaluationJobDisplayStatusLabel(jobDisplayStatus)}
                                  </span>
                                </div>
                                <div className="training-job-history-item-details">
                                  <div className="training-job-history-item-detail">
                                    <span className="detail-label">Created</span>
                                    <span className="detail-value">{createdAt}</span>
                                  </div>
                                  <div className="training-job-history-item-detail">
                                    <span className="detail-label">Progress</span>
                                    <span className="detail-value">{jobProgress}%</span>
                                  </div>
                                  {progressMeta.message ? (
                                    <div className="training-job-history-item-detail">
                                      <span className="detail-label">Status</span>
                                      <span className="detail-value">{progressMeta.message}</span>
                                    </div>
                                  ) : null}
                                  {progressMeta.meta ? (
                                    <div className="training-job-history-item-detail">
                                      <span className="detail-label">Stage</span>
                                      <span className="detail-value">{progressMeta.meta}</span>
                                    </div>
                                  ) : null}
                                  {jobId ? (
                                    <div className="training-job-history-item-detail">
                                      <span className="detail-label">Job ID</span>
                                      <span className="detail-value" style={{ fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 }}>
                                        {jobId}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                                {errorDisplay ? (
                                  <FailedJobErrorMessage
                                    rawError={errorDisplay.raw}
                                    friendlyMessage={errorDisplay.friendly}
                                    details={errorDisplay.details}
                                    fallbackMessage="Evaluation job failed"
                                    compact
                                  />
                                ) : null}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                  {jobId ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn connector-shell-btn--ghost"
                                      style={{ padding: '6px 12px', fontSize: 12 }}
                                      onClick={() => void handleOpenEvaluationJobDetails(jobId, jobRow)}
                                    >
                                      <i className="fas fa-eye" aria-hidden /> Details
                                    </button>
                                  ) : null}
                                  {jobId && canRunAgain ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn connector-shell-btn--ghost"
                                      style={{ padding: '6px 12px', fontSize: 12 }}
                                      onClick={() => handleOpenEvaluationRunAgain(jobRow, jobId)}
                                      disabled={isEvaluationJobRunning}
                                    >
                                      <i className="fas fa-play" aria-hidden /> Run again
                                    </button>
                                  ) : null}
                                  {jobId && canCancelEvaluationJob(jobStatus) ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn"
                                      style={{
                                        padding: '6px 12px',
                                        fontSize: 12,
                                        background: '#FF8300',
                                        color: '#FFFFFF',
                                        border: 'none',
                                      }}
                                      onClick={() => void handleCancelEvaluationJob(jobId)}
                                      disabled={isCancelling}
                                      aria-busy={isCancelling}
                                    >
                                      <i className={`fas ${isCancelling ? 'fa-spinner fa-spin' : 'fa-ban'}`} aria-hidden />
                                      {getJobCancelButtonLabel(isCancelling)}
                                    </button>
                                  ) : null}
                                  {jobId && canRetry ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn connector-shell-btn--primary"
                                      style={{ padding: '6px 12px', fontSize: 12 }}
                                      onClick={() =>
                                        void handleRetryEvaluationJob(
                                          jobId,
                                          buildJobRetryUiRowKey({ kind: 'history', jobId, index })
                                        )
                                      }
                                      disabled={isRetrying}
                                      aria-busy={isRetrying}
                                    >
                                      <i className={`fas ${isRetrying ? 'fa-spinner fa-spin' : 'fa-redo'}`} aria-hidden />
                                      {isRetrying ? 'Retrying…' : 'Retry'}
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div id="config-tuning-job-history" className="training-job-history" style={{ marginBottom: 24 }}>
                      <div className="training-job-history-header">
                        <div>
                          <h4>
                            <i className="fas fa-history" aria-hidden />
                            Config Tuning Jobs
                          </h4>
                          <p className="training-job-history-pagination-info">
                            Recent jobs for this integration. Cancel queued or running jobs; retry failed or cancelled runs.
                          </p>
                        </div>
                        <button
                          type="button"
                          className="connector-shell-btn connector-shell-btn--ghost"
                          onClick={() => void loadOptimizationJobHistory()}
                          disabled={optimizationJobHistoryLoading || !selectedIntegrationId}
                        >
                          <i className={`fas ${optimizationJobHistoryLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} aria-hidden />
                          Refresh
                        </button>
                      </div>
                      {optimizationJobHistoryLoading && optimizationJobHistory.length === 0 ? (
                        <div className="training-job-history-loading">
                          <div className="spinner spinner--small" />
                          <p>Loading jobs…</p>
                        </div>
                      ) : optimizationJobHistory.length === 0 ? (
                        <div className="training-job-history-empty">
                          <i className="fas fa-rocket" aria-hidden />
                          <p>No config tuning jobs yet for this integration.</p>
                        </div>
                      ) : (
                        <div className="training-job-history-list">
                          {optimizationJobHistory.map((job, index) => {
                            const jobRow = unwrapOptimizationJobRow(job);
                            const jobId = resolveOptimizationJobRecordId(jobRow);
                            const jobStatus = resolveEffectiveOptimizationJobStatus(jobRow);
                            const isTrackedActiveJob =
                              Boolean(
                                jobId &&
                                  connectorOptimizationJobId === jobId &&
                                  shouldTrackOptimizationJobRow(jobRow)
                              );
                            const jobProgressDisplay = resolveOptimizationJobProgressDisplay(
                              jobRow,
                              optimizationIterations
                            );
                            const jobProgress = isTrackedActiveJob
                              ? Math.round(connectorOptimizationProgressDisplay)
                              : jobProgressDisplay.percent;
                            const runnerDisplay = {
                              message: jobProgressDisplay.message,
                              meta: jobProgressDisplay.meta,
                            };
                            const canRetry = isOptimizationJobRetryEligible(jobRow);
                            const isCancelling = isJobCancellationPending(
                              jobId,
                              optimizationJobCancellingId,
                              jobStatus
                            );
                            const isRetrying = isJobRetryUiRowActive(optimizationJobRetryingRowKey, {
                              kind: 'history',
                              jobId,
                              index,
                            });
                            const createdAt = jobRow?.created_at
                              ? new Date(String(jobRow.created_at)).toLocaleString()
                              : '—';
                            return (
                              <div key={jobId || `opt-hist-${index}`} className="training-job-history-item">
                                <div className="training-job-history-item-header">
                                  <div className="training-job-history-item-title">
                                    <i className="fas fa-rocket" aria-hidden />
                                    <span>
                                      {typeof jobRow.config_label === 'string' && jobRow.config_label.trim()
                                        ? jobRow.config_label.trim()
                                        : 'Config tuning job'}
                                    </span>
                                  </div>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: '#5B7893', textTransform: 'capitalize' }}>
                                    {jobStatus || '—'}
                                  </span>
                                </div>
                                <div className="training-job-history-item-details">
                                  <div className="training-job-history-item-detail">
                                    <span className="detail-label">Created</span>
                                    <span className="detail-value">{createdAt}</span>
                                  </div>
                                  <div className="training-job-history-item-detail">
                                    <span className="detail-label">Progress</span>
                                    <span className="detail-value">{jobProgress}%</span>
                                  </div>
                                  {runnerDisplay.message ? (
                                    <div className="training-job-history-item-detail">
                                      <span className="detail-label">Status</span>
                                      <span className="detail-value">{runnerDisplay.message}</span>
                                    </div>
                                  ) : null}
                                  {runnerDisplay.meta ? (
                                    <div className="training-job-history-item-detail">
                                      <span className="detail-label">Stage</span>
                                      <span className="detail-value">{runnerDisplay.meta}</span>
                                    </div>
                                  ) : null}
                                  {jobId ? (
                                    <div className="training-job-history-item-detail">
                                      <span className="detail-label">Job ID</span>
                                      <span className="detail-value" style={{ fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 }}>
                                        {jobId}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                                {jobStatus === 'failed' || isFailedOptimizationJobStatus(jobStatus) ? (
                                  <FailedJobErrorMessage
                                    rawError={extractJobRawError(jobRow, 'Optimization job failed')}
                                    fallbackMessage="Optimization job failed"
                                    compact
                                  />
                                ) : null}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                  {jobId && canCancelJob(jobStatus) ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn"
                                      style={{ padding: '6px 12px', fontSize: 12, background: '#FF8300', color: '#FFFFFF', border: 'none' }}
                                      onClick={() => void handleCancelOptimizationJob(jobId)}
                                      disabled={isCancelling}
                                      aria-busy={isCancelling}
                                    >
                                      <i className={`fas ${isCancelling ? 'fa-spinner fa-spin' : 'fa-ban'}`} aria-hidden />
                                      {getJobCancelButtonLabel(isCancelling)}
                                    </button>
                                  ) : null}
                                  {jobId && shouldTrackOptimizationJobRow(jobRow) ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn connector-shell-btn--ghost"
                                      style={{ padding: '6px 12px', fontSize: 12 }}
                                      onClick={() => {
                                        optimizationRanThisSession.current = true;
                                        setConnectorMultiAgentProgressStartTime(null);
                                        setTerminalOptimizationJob(null);
                                        setOptimizationStoppedByUser(false);
                                        setShowCompletionMessage(false);
                                        resetSmoothProgress();
                                        resetOptimizationJobsApiProgress();
                                        setConnectorMultiAgentIsOptimizing(true);
                                        beginOptimizationJobPolling(jobId);
                                        requestAnimationFrame(() => {
                                          document
                                            .getElementById('optimize-section')
                                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        });
                                      }}
                                    >
                                      <i className="fas fa-chart-line" aria-hidden /> Track
                                    </button>
                                  ) : null}
                                  {jobId && canRetry ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn connector-shell-btn--primary"
                                      style={{ padding: '6px 12px', fontSize: 12 }}
                                      onClick={() =>
                                        void handleRetryOptimizationJob(
                                          jobId,
                                          buildJobRetryUiRowKey({ kind: 'history', jobId, index })
                                        )
                                      }
                                      disabled={isRetrying}
                                      aria-busy={isRetrying}
                                    >
                                      <i className={`fas ${isRetrying ? 'fa-spinner fa-spin' : 'fa-redo'}`} aria-hidden />
                                      {isRetrying ? 'Retrying…' : 'Retry'}
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div id="training-job-history" className="training-job-history">
                      <div className="training-job-history-header">
                        <div>
                          <h4>
                            <i className="fas fa-history" aria-hidden />
                            Fine Tuning Jobs
                          </h4>
                          <p className="training-job-history-pagination-info">
                            Recent jobs for this integration. Cancel queued or running jobs; retry failed or cancelled runs.
                          </p>
                        </div>
                        <button
                          type="button"
                          className="connector-shell-btn connector-shell-btn--ghost"
                          onClick={() => void loadTrainingJobHistory()}
                          disabled={trainingJobHistoryLoading || !selectedIntegrationId}
                        >
                          <i className={`fas ${trainingJobHistoryLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} aria-hidden />
                          Refresh
                        </button>
                      </div>
                      {trainingJobHistoryLoading && trainingJobHistory.length === 0 ? (
                        <div className="training-job-history-loading">
                          <div className="spinner spinner--small" />
                          <p>Loading jobs…</p>
                        </div>
                      ) : trainingJobHistory.length === 0 ? (
                        <div className="training-job-history-empty">
                          <i className="fas fa-graduation-cap" aria-hidden />
                          <p>No fine-tuning jobs yet for this integration.</p>
                        </div>
                      ) : (
                        <div className="training-job-history-list">
                          {trainingJobHistory.map((job, index) => {
                            const jobRow = job as Record<string, unknown>;
                            const jobId = resolveTrainingJobRecordId(jobRow);
                            const jobStatus = resolveEffectiveTrainingJobStatus(jobRow);
                            const isTrackedActiveJob =
                              Boolean(jobId && pollingJobId === jobId && isActiveJobStatus(jobStatus));
                            const jobProgress = isTrackedActiveJob
                              ? Math.round(trainingJobsApiProgress)
                              : typeof jobRow.progress_percent === 'number'
                                ? Math.round(jobRow.progress_percent)
                                : 0;
                            const canRetry = canRetryTrainingJob(jobStatus);
                            const isCancelling = isJobCancellationPending(
                              jobId,
                              trainingJobCancellingId,
                              jobStatus
                            );
                            const isRetrying = isJobRetryUiRowActive(trainingJobRetryingRowKey, {
                              kind: 'history',
                              jobId,
                              index,
                            });
                            const createdAt = jobRow?.created_at
                              ? new Date(String(jobRow.created_at)).toLocaleString()
                              : '—';
                            const ftJobError =
                              jobStatus === 'failed'
                                ? resolveFineTuningJobError(jobRow as Record<string, unknown>)
                                : null;
                            return (
                              <div key={jobId || `ft-hist-${index}`} className="training-job-history-item">
                                <div className="training-job-history-item-header">
                                  <div className="training-job-history-item-title">
                                    <i className="fas fa-graduation-cap" aria-hidden />
                                    <span>
                                      {typeof jobRow.model_name === 'string' && jobRow.model_name.trim()
                                        ? jobRow.model_name.trim()
                                        : 'Fine-tuning job'}
                                    </span>
                                  </div>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: '#5B7893',
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    {jobStatus || '—'}
                                  </span>
                                </div>
                                <div className="training-job-history-item-details">
                                  <div className="training-job-history-item-detail">
                                    <span className="detail-label">Created</span>
                                    <span className="detail-value">{createdAt}</span>
                                  </div>
                                  <div className="training-job-history-item-detail">
                                    <span className="detail-label">Progress</span>
                                    <span className="detail-value">{jobProgress}%</span>
                                  </div>
                                  {jobId ? (
                                    <div className="training-job-history-item-detail">
                                      <span className="detail-label">Job ID</span>
                                      <span className="detail-value" style={{ fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 }}>
                                        {jobId}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                                {ftJobError ? (
                                  <FailedJobErrorMessage
                                    rawError={ftJobError.raw}
                                    friendlyMessage={ftJobError.friendly}
                                    details={ftJobError.details}
                                    fallbackMessage="Fine-tuning job failed"
                                    compact
                                  />
                                ) : null}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                  {jobId ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn connector-shell-btn--ghost"
                                      style={{ padding: '6px 12px', fontSize: 12 }}
                                      onClick={() => {
                                        setTrainingJobResult({ data: jobRow });
                                        setPollingJobId(jobId);
                                        pollingJobIdRef.current = jobId;
                                        document.getElementById('training-job-result-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                      }}
                                    >
                                      <i className="fas fa-eye" aria-hidden /> Details
                                    </button>
                                  ) : null}
                                  {jobId && canCancelJob(jobStatus) ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn"
                                      style={{
                                        padding: '6px 12px',
                                        fontSize: 12,
                                        background: '#FF8300',
                                        color: '#FFFFFF',
                                        border: 'none',
                                      }}
                                      onClick={() => void handleCancelTrainingJob(jobId)}
                                      disabled={isCancelling}
                                      aria-busy={isCancelling}
                                    >
                                      <i
                                        className={`fas ${isCancelling ? 'fa-spinner fa-spin' : 'fa-ban'}`}
                                        aria-hidden
                                      />
                                      {getJobCancelButtonLabel(isCancelling)}
                                    </button>
                                  ) : null}
                                  {jobId && canRetry ? (
                                    <button
                                      type="button"
                                      className="connector-shell-btn connector-shell-btn--primary"
                                      style={{ padding: '6px 12px', fontSize: 12 }}
                                      onClick={() =>
                                        void handleRetryTrainingJob(
                                          jobId,
                                          buildJobRetryUiRowKey({ kind: 'history', jobId, index })
                                        )
                                      }
                                      disabled={isRetrying}
                                      aria-busy={isRetrying}
                                    >
                                      <i
                                        className={`fas ${isRetrying ? 'fa-spinner fa-spin' : 'fa-redo'}`}
                                        aria-hidden
                                      />
                                      {isRetrying ? 'Retrying…' : 'Retry'}
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          )}
        </>
        </ConnectorSectionShell>
      )}

      {/* Virtual Users Tab */}
      {activeTab === 'virtual-users' && (
        <ConnectorSectionShell
          eyebrow={getConnectorTab('virtual-users').eyebrow}
          title={getConnectorTab('virtual-users').title}
          subtitle={getConnectorTab('virtual-users').subtitle}
          icon={getConnectorTab('virtual-users').icon}
          onHelp={() => setShowOnboarding(true)}
        >
          <div id="virtual-users-content" className="connector-section-panel">
          {!selectedIntegrationId ? (
            <ConnectorSelectIntegrationPrompt
              description="Choose an integration to create virtual users, personas, and experiments."
              onGoToIntegrations={() => setActiveTab('integrations')}
            />
          ) : (
            <VUserPanel 
              integrationId={selectedIntegrationId} 
              onOpenOnboarding={() => {
                setActiveTab('virtual-users');
                setShowOnboarding(true);
              }}
            />
          )}
          </div>
        </ConnectorSectionShell>
      )}

      {/* Datasets Tab */}
      {(activeTab === 'datasets' || datasetsBlockingModalOpen) && (
        <ConnectorSectionShell
          className={
            activeTab !== 'datasets' && datasetsBlockingModalOpen
              ? 'connector-section--keepalive'
              : undefined
          }
          eyebrow={getConnectorTab('datasets').eyebrow}
          title={getConnectorTab('datasets').title}
          subtitle={getConnectorTab('datasets').subtitle}
          icon={getConnectorTab('datasets').icon}
          onHelp={() => {
            setActiveTab('datasets');
            setShowOnboarding(true);
          }}
        >
          <div id="datasets-content" className="connector-section-panel connector-section-panel--flush">
          {!selectedIntegrationId ? (
            <ConnectorSelectIntegrationPrompt
              description="Choose an integration to import data, export dialogs, and manage training datasets."
              onGoToIntegrations={() => setActiveTab('integrations')}
            />
          ) : (
          <DatasetsPage 
            integrationId={selectedIntegrationId}
            embedded
            openExperimentId={pendingOpenExperimentId}
            onOpenExperimentHandled={() => setPendingOpenExperimentId(null)}
            onBlockingModalOpenChange={setDatasetsBlockingModalOpen}
            onOpenOnboarding={() => {
              setActiveTab('datasets');
              setShowOnboarding(true);
            }}
          />
          )}
          </div>
        </ConnectorSectionShell>
      )}

      {activeTab === 'chat-viewer' && (
        <ConnectorSectionShell
          eyebrow={getConnectorTab('chat-viewer').eyebrow}
          title={getConnectorTab('chat-viewer').title}
          subtitle={getConnectorTab('chat-viewer').subtitle}
          icon={getConnectorTab('chat-viewer').icon}
          onHelp={() => setShowOnboarding(true)}
        >
          <div id="chat-viewer-content" className="connector-section-panel">
            {!selectedIntegrationId ? (
              <ConnectorSelectIntegrationPrompt
                description="Choose an integration to browse dialogs and label messages."
                onGoToIntegrations={() => setActiveTab('integrations')}
              />
            ) : (
              <ConnectorChatViewerPanel integrationId={selectedIntegrationId} />
            )}
          </div>
        </ConnectorSectionShell>
      )}

      {activeTab === 'workflow' && (
        <ConnectorSectionShell
          eyebrow={getConnectorTab('workflow').eyebrow}
          title={getConnectorTab('workflow').title}
          subtitle={getConnectorTab('workflow').subtitle}
          icon={getConnectorTab('workflow').icon}
        >
          <div id="workflow-tab-content" className={`connector-section-panel${selectedIntegrationId ? ' cwf-section-panel' : ''}`}>
            {!selectedIntegrationId ? (
              <ConnectorSelectIntegrationPrompt
                description="Choose an integration to read and edit the workflow graph of the connected system."
                icon="fa-sitemap"
                onGoToIntegrations={() => setActiveTab('integrations')}
              />
            ) : (
              <ConnectorWorkflowPanel
                integrationId={selectedIntegrationId}
                isAdmin={String(user?.role || '').toLowerCase() === 'admin'}
                datasets={allAvailableDatasets}
                datasetsLoading={datasetsLoading}
                onNltBridgeChange={setWorkflowEditBridge}
                onOpenFineTuning={handleOpenTrainingJobModal}
              />
            )}
          </div>
        </ConnectorSectionShell>
      )}

      {activeTab === 'rag-documents' && (
        <ConnectorSectionShell
          eyebrow={getConnectorTab('rag-documents').eyebrow}
          title={getConnectorTab('rag-documents').title}
          subtitle={getConnectorTab('rag-documents').subtitle}
          icon={getConnectorTab('rag-documents').icon}
          onHelp={() => setShowOnboarding(true)}
        >
          <div id="rag-documents-tab-content">
          <div id="rag-documents-panel-anchor" className="connector-section-panel">
          {!selectedIntegrationId ? (
            <ConnectorSelectIntegrationPrompt
              description="Choose an integration to upload and manage RAG documents."
              onGoToIntegrations={() => setActiveTab('integrations')}
            />
          ) : (
            <ConnectorRagDocumentsPanel integrationId={selectedIntegrationId} />
          )}
          </div>
          </div>
        </ConnectorSectionShell>
      )}

      {activeTab === 'auto-fine-tuning' && (
        <ConnectorSectionShell
          eyebrow={getConnectorTab('auto-fine-tuning').eyebrow}
          title={getConnectorTab('auto-fine-tuning').title}
          subtitle={getConnectorTab('auto-fine-tuning').subtitle}
          icon={getConnectorTab('auto-fine-tuning').icon}
          onHelp={() => setShowOnboarding(true)}
        >
          <div id="auto-fine-tuning-content" className="connector-section-panel">
          {!selectedIntegrationId ? (
            <ConnectorSelectIntegrationPrompt
              description="Choose an integration to configure scheduled fine-tuning jobs."
              onGoToIntegrations={() => setActiveTab('integrations')}
            />
          ) : (
          <AutoFineTuningPanel
            integrationId={selectedIntegrationId}
            configFromConfigurator={currentConfig}
            agentReactions={agentReactions}
            onAgentReactionsChange={setAgentReactions}
          />
          )}
          </div>
        </ConnectorSectionShell>
      )}

      {activeTab === 'pipelines' && (
        <ConnectorSectionShell
          eyebrow={getConnectorTab('pipelines').eyebrow}
          title={getConnectorTab('pipelines').title}
          subtitle={getConnectorTab('pipelines').subtitle}
          icon={getConnectorTab('pipelines').icon}
          onHelp={() => setShowOnboarding(true)}
        >
          <div id="pipelines-content" className="connector-section-panel">
            {!selectedIntegrationId ? (
              <ConnectorSelectIntegrationPrompt
                description="Choose an integration to create and manage automation pipelines."
                onGoToIntegrations={() => setActiveTab('integrations')}
              />
            ) : (
            <PipelinePanel
              integrationId={selectedIntegrationId}
              configFromConfigurator={currentConfig}
              optimizationPolicyUi={optimizationPolicyUi}
              onOptimizationPolicyUiChange={setOptimizationPolicyUi}
              optimizationIterations={optimizationIterations}
              onOptimizationIterationsChange={setOptimizationIterations}
              onOpenExperiment={(experimentId) => {
                setPendingOpenExperimentId(experimentId);
                setActiveTab('datasets');
              }}
            />
            )}
          </div>
        </ConnectorSectionShell>
      )}

      {activeTab === 'fine-tuning-graph' && (
        <ConnectorSectionShell
          eyebrow={getConnectorTab('fine-tuning-graph').eyebrow}
          title={getConnectorTab('fine-tuning-graph').title}
          subtitle={getConnectorTab('fine-tuning-graph').subtitle}
          icon={getConnectorTab('fine-tuning-graph').icon}
          onHelp={() => setShowOnboarding(true)}
        >
          <div id="fine-tuning-graph-content" className="connector-section-panel">
            {!selectedIntegrationId ? (
              <ConnectorSelectIntegrationPrompt
                description="Choose an integration to view the fine-tuning models graph."
                onGoToIntegrations={() => setActiveTab('integrations')}
              />
            ) : (
              <FineTuningGraphPanel integrationId={selectedIntegrationId} />
            )}
          </div>
        </ConnectorSectionShell>
      )}

      {/* Notification */}
      {notification.isVisible && (
        <Notification
          type={notification.type}
          message={notification.message}
          isVisible={notification.isVisible}
          onClose={() => setNotification(prev => ({ ...prev, isVisible: false }))}
          duration={NOTIFICATION_AUTO_HIDE_MS}
          position="top-right"
        />
      )}

      {/* Config Tuning Job Modal */}
      {showConfigTuningModal && currentConfig && (
        <TuningModalShell
          onClose={closeConfigTuningModal}
          title="Start Config Tuning Job"
          subtitle="Set optimization parameters, preview estimated cost, then start config tuning."
          iconClassName="fas fa-rocket"
          variant="config"
          footer={
            <>
              <button
                type="button"
                onClick={closeConfigTuningModal}
                className="tuning-modal__btn tuning-modal__btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleOptimizePreview()}
                disabled={
                  optimizePreviewLoading ||
                  configTuningJobSubmitting ||
                  !currentConfig ||
                  !selectedIntegrationId ||
                  !canRunConnectorOptimization
                }
                className="tuning-modal__btn tuning-modal__btn--preview"
              >
                {optimizePreviewLoading ? (
                  <>
                    <div className="spinner spinner--small" style={{ width: 16, height: 16 }} />
                    <span>Loading…</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-calculator" aria-hidden />
                    <span>Optimize preview</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => void handleStartConfigTuningJob()}
                disabled={
                  configTuningJobSubmitting ||
                  !currentConfig ||
                  !canRunConnectorOptimization
                }
                title={
                  !canRunConnectorOptimization
                    ? 'Select at least one dataset to start config tuning'
                    : undefined
                }
                className="tuning-modal__btn tuning-modal__btn--primary"
              >
                {configTuningJobSubmitting ? (
                  <>
                    <div className="spinner spinner--small" style={{ width: 16, height: 16 }} />
                    <span>Starting…</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-rocket" aria-hidden />
                    <span>Start job</span>
                  </>
                )}
              </button>
            </>
          }
        >
          {connectorMultiAgentIsOptimizing ? (
            <div className="connector-message info" role="status" style={{ marginBottom: 16 }}>
              <i className="fas fa-info-circle" aria-hidden />{' '}
              A config tuning job is already running. You can configure and start another job; progress below
              will track the new run.
            </div>
          ) : null}
          <ConnectorConfigTuningFields
            currentConfig={currentConfig}
            onConfigChange={handleConfigChangeForTuning}
            disabled={configTuningJobSubmitting}
            datasets={allAvailableDatasets}
            selectedDatasets={optimizationDatasets}
            onDatasetsChange={setOptimizationDatasets}
            datasetsLoading={datasetsLoading}
            iterations={optimizationIterations}
            onIterationsChange={setOptimizationIterations}
            optimizationPolicyUi={optimizationPolicyUi}
            onOptimizationPolicyUiChange={setOptimizationPolicyUi}
            selectionScenario={selectionScenario}
            onSelectionScenarioChange={setSelectionScenario}
            evaluationMode={evaluationMode}
            onEvaluationModeChange={handleEvaluationModeChange}
            evaluatorParams={evaluatorParams}
            onEvaluatorParamsChange={setEvaluatorParams}
            availableModels={configuratorAvailableModels}
          />
        </TuningModalShell>
      )}

      {/* Fine Tuning Job Modal */}
      {showTrainingJobModal && currentConfig && (
        <TuningModalShell
          onClose={closeTrainingJobModal}
          title="Start Fine Tuning Job"
          subtitle="Select datasets, review agents (including historical ones), choose base models, preview token estimate, then start."
          iconClassName="fas fa-graduation-cap"
          variant="fine-tuning"
          size="wide"
          bodyRef={trainingJobModalScrollRef}
          footer={
            <>
              <button
                type="button"
                onClick={closeTrainingJobModal}
                className="tuning-modal__btn tuning-modal__btn--secondary"
                title="Close and cancel any in-flight Preview or Start request"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handlePreviewTrainingJob()}
                disabled={previewLoading || trainingJobLoading}
                className="tuning-modal__btn tuning-modal__btn--preview"
              >
                {previewLoading ? (
                  <>
                    <div className="spinner spinner--small" style={{ width: 16, height: 16 }} />
                    <span>Previewing…</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-eye" aria-hidden />
                    <span>Preview</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => void handleCreateTrainingJob()}
                disabled={
                  trainingJobLoading ||
                  previewLoading ||
                  !trainingJobPreviewConfirmed ||
                  !trainingJobForm.model_name.trim() ||
                  trainingJobForm.dataset_ids.length === 0 ||
                  countSelectedEligibleDatasetAgents(datasetAgentSelections) === 0
                }
                className="tuning-modal__btn tuning-modal__btn--primary"
              >
                {trainingJobLoading ? (
                  <>
                    <div className="spinner spinner--small" style={{ width: 16, height: 16 }} />
                    <span>Starting…</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-graduation-cap" aria-hidden />
                    <span>Start Training Job</span>
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="ft-modal-form">
            {trainingJobValidation.bannerError && (
              <div
                id={TRAINING_JOB_ERROR_BANNER_ID}
                className="tuning-modal__alert tuning-modal__alert--error"
                role="alert"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                <i className="fas fa-exclamation-circle" aria-hidden />
                <span>{trainingJobValidation.bannerError}</span>
              </div>
            )}
            <div
              id={TRAINING_JOB_DATASETS_ID}
              className={`ft-modal-section connector-ft-dataset-picker${trainingJobValidation.fieldErrors[TRAINING_JOB_DATASETS_ID] ? ' ft-modal-section--invalid modal-form-field--invalid' : ''}`}
            >
              <div className="connector-ft-dataset-picker__head">
                <p className="connector-ft-dataset-picker__title">
                  Saved datasets <span style={{ color: '#FF8300' }}>*</span>
                </p>
                <span className="connector-ft-dataset-picker__count">
                  {trainingJobForm.dataset_ids.length} selected
                </span>
              </div>
              {trainingJobValidation.fieldErrors[TRAINING_JOB_DATASETS_ID] ? (
                <p className="modal-form-field__error" role="alert" style={{ marginBottom: 8 }}>
                  {trainingJobValidation.fieldErrors[TRAINING_JOB_DATASETS_ID]}
                </p>
              ) : null}
              <div className="connector-ft-dataset-picker__toolbar">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={trainingJobLoading || datasetsLoading}
                  onClick={() => {
                    const ids = connectorDatasets
                      .map((d) => (typeof d.id === 'string' ? d.id.trim() : ''))
                      .filter(Boolean);
                    const unique = ids.filter((id, i) => ids.indexOf(id) === i);
                    setTrainingJobForm((p) => ({ ...p, dataset_ids: unique }));
                  }}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={trainingJobLoading || trainingJobForm.dataset_ids.length === 0}
                  onClick={() => setTrainingJobForm((p) => ({ ...p, dataset_ids: [] }))}
                >
                  Deselect all
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={trainingJobLoading || !selectedIntegrationId}
                  onClick={() => {
                    if (selectedIntegrationId) void loadConnectorDatasets(selectedIntegrationId);
                  }}
                >
                  Refresh list
                </button>
              </div>
              <div
                className="connector-ft-dataset-picker__list"
                role="listbox"
                aria-multiselectable="true"
                aria-label="Saved datasets for fine-tuning"
                style={trainingJobLoading ? { opacity: 0.85 } : undefined}
              >
                {datasetsLoading ? (
                  <div className="connector-ft-dataset-picker__loading">Loading datasets…</div>
                ) : connectorDatasets.length === 0 ? (
                  <div className="connector-ft-dataset-picker__empty">
                    No saved datasets in the registry for this integration. Import or create a dataset first, then refresh.
                  </div>
                ) : (
                  connectorDatasets.map((ds, idx) => {
                    const id = typeof ds.id === 'string' ? ds.id.trim() : '';
                    const label =
                      (typeof ds.label === 'string' && ds.label.trim()) ? ds.label.trim() : id || 'Untitled dataset';
                    const sourceRaw = (ds as Record<string, unknown>).source;
                    const source =
                      typeof sourceRaw === 'string' && sourceRaw.trim() ? sourceRaw.trim() : '';
                    const disabled = !id || trainingJobLoading;
                    const checked = Boolean(id && trainingJobForm.dataset_ids.includes(id));
                    return (
                      <label
                        key={id || `dataset-row-${idx}`}
                        className={`connector-ft-dataset-row${checked ? ' connector-ft-dataset-row--selected' : ''}${disabled ? ' connector-ft-dataset-row--disabled' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => {
                            if (!id) return;
                            setTrainingJobForm((prev) => {
                              if (prev.dataset_ids.includes(id)) {
                                return {
                                  ...prev,
                                  dataset_ids: prev.dataset_ids.filter((x) => x !== id),
                                };
                              }
                              return { ...prev, dataset_ids: [...prev.dataset_ids, id] };
                            });
                          }}
                        />
                        <span className="connector-ft-dataset-row__body">
                          <span className="connector-ft-dataset-row__title-line">
                            <span className="connector-ft-dataset-row__name">{label}</span>
                            {source ? (
                              <span className="connector-ft-dataset-row__source">{source}</span>
                            ) : null}
                          </span>
                          {id ? (
                            <span className="connector-ft-dataset-row__id">{id}</span>
                          ) : (
                            <span className="connector-ft-dataset-row__warning">
                              Missing registry id — this row cannot be used for training.
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
              <p className="connector-ft-dataset-picker__hint">
                Select datasets first — agents are discovered from them, including historical configs not in the current
                integration setup.
              </p>
            </div>

            <div
              id={TRAINING_JOB_AGENTS_ID}
              className={`ft-modal-section${trainingJobValidation.fieldErrors[TRAINING_JOB_AGENTS_ID] ? ' ft-modal-section--invalid modal-form-field--invalid' : ''}`}
            >
              {trainingJobValidation.fieldErrors[TRAINING_JOB_AGENTS_ID] ? (
                <p className="modal-form-field__error" role="alert" style={{ marginBottom: 12 }}>
                  {trainingJobValidation.fieldErrors[TRAINING_JOB_AGENTS_ID]}
                </p>
              ) : null}
              <p className="ft-modal-section__title">
                <span className="ft-modal-section__title-icon" aria-hidden>
                  <i className="fas fa-users" />
                </span>
                Agents from datasets ({countSelectedEligibleDatasetAgents(datasetAgentSelections)})
              </p>
              <DatasetAgentsPreviewPanel
                selections={datasetAgentSelections}
                baseModelOptions={fineTuningBaseModels.length > 0 ? fineTuningBaseModels : configuratorAvailableModels}
                minExamples={datasetAgentsMinExamples}
                loading={datasetAgentsPreviewLoading}
                error={datasetAgentsPreviewError}
                disabled={trainingJobLoading || previewLoading}
                agentReactions={agentReactions}
                agentFilterFlags={agentFilterFlags}
                onToggleReaction={toggleTrainingAgentReaction}
                onToggleFilterFlag={toggleTrainingAgentFilterFlag}
                onBulkSetReaction={setBulkTrainingAgentReaction}
                onBulkSetFilterFlag={setBulkTrainingAgentFilterFlag}
                onChange={handleDatasetAgentSelectionsChange}
              />
            </div>

            {/* Model Name */}
            <div className={`ft-modal-section ft-modal-field${trainingJobValidation.fieldErrors[TRAINING_JOB_MODEL_NAME_ID] ? ' ft-modal-section--invalid' : ''}`}>
            <ModalFormField
              id={TRAINING_JOB_MODEL_NAME_ID}
              label={
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', width: '100%' }}>
                  <span>
                    Model Name<span className="modal-form-field__required"> *</span>
                  </span>
                  <span className="ft-modal-field__counter">
                    {trainingJobForm.model_name.length}/{TRAINING_JOB_MODEL_NAME_MAX_LEN}
                  </span>
                </span>
              }
              error={trainingJobValidation.fieldErrors[TRAINING_JOB_MODEL_NAME_ID]}
              hint={`Up to ${TRAINING_JOB_MODEL_NAME_MAX_LEN} characters. This will be the alias name for your fine-tuned model. Backend will auto-increment if the name is taken.`}
              className="ft-modal-field"
            >
              <input
                id={TRAINING_JOB_MODEL_NAME_ID}
                type="text"
                className="ft-modal-input"
                value={trainingJobForm.model_name}
                maxLength={TRAINING_JOB_MODEL_NAME_MAX_LEN}
                onChange={(e) =>
                  setTrainingJobForm({
                    ...trainingJobForm,
                    model_name: e.target.value.slice(0, TRAINING_JOB_MODEL_NAME_MAX_LEN),
                  })
                }
                placeholder="Enter unique model name (e.g., my_model_4)"
                disabled={trainingJobLoading}
                aria-required
                aria-invalid={Boolean(trainingJobValidation.fieldErrors[TRAINING_JOB_MODEL_NAME_ID])}
              />
            </ModalFormField>
            </div>

            <div className="ft-modal-section">
              <p className="ft-modal-section__title">
                <span className="ft-modal-section__title-icon" aria-hidden>
                  <i className="fas fa-sliders-h" />
                </span>
                Training options
              </p>
              <div className="ft-modal-field" style={{ marginBottom: 14 }}>
              <label className="cct-label" htmlFor="ft-group-by-base-model-yes">
                Group by base model
              </label>
              <div className="ft-modal-segment" id="ft-group-by-base-model">
                <button
                  type="button"
                  id="ft-group-by-base-model-yes"
                  onClick={() => setTrainingJobForm({ ...trainingJobForm, group_by_base_model: true })}
                  disabled={trainingJobLoading}
                  className={`ft-modal-segment__btn${trainingJobForm.group_by_base_model ? ' ft-modal-segment__btn--active' : ''}`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setTrainingJobForm({ ...trainingJobForm, group_by_base_model: false })}
                  disabled={trainingJobLoading}
                  className={`ft-modal-segment__btn${!trainingJobForm.group_by_base_model ? ' ft-modal-segment__btn--active' : ''}`}
                >
                  No
                </button>
              </div>
              <p className="ft-modal-section__hint" style={{ marginBottom: 0, marginTop: 8 }}>
                Yes: one FT group per base model. No: keep per-agent rows/jobs.
              </p>
              </div>

              <div className="ft-modal-field" style={{ marginBottom: 0 }}>
                <BoolToggleRow
                  on={trainingJobForm.merge_train_validation}
                  disabled={trainingJobLoading}
                  onToggle={() =>
                    setTrainingJobForm({
                      ...trainingJobForm,
                      merge_train_validation: !trainingJobForm.merge_train_validation,
                    })
                  }
                  label="Merge train & validation"
                />
                <p className="ft-modal-section__hint" style={{ marginTop: 8, marginBottom: 0 }}>
                  When enabled, training and validation splits are combined into a single dataset for fine-tuning.
                </p>
              </div>

              <div style={{ marginTop: 16 }}>
              <HistoryContextControl
                limited={trainingJobForm.limit_history}
                onLimitedChange={(v) =>
                  setTrainingJobForm({
                    ...trainingJobForm,
                    limit_history: v,
                  })
                }
                depthValue={numberInputDisplay(trainingJobForm.history_depth, DEFAULT_HISTORY_DEPTH)}
                onDepthValueChange={(value) =>
                  setTrainingJobForm({
                    ...trainingJobForm,
                    history_depth: value,
                  })
                }
                disabled={trainingJobLoading}
                depthInputId="connector-training-history-depth"
              />
              </div>
            </div>

            {/* Preview Result */}
            {trainingJobValidation.fieldErrors[TRAINING_JOB_PREVIEW_ID] ? (
              <div id={TRAINING_JOB_PREVIEW_ID} className="modal-form-field__error" role="alert" style={{ marginBottom: 16 }}>
                {trainingJobValidation.fieldErrors[TRAINING_JOB_PREVIEW_ID]}
              </div>
            ) : null}
            {previewResult ? <TrainingPreviewResultView data={previewResult} /> : null}
          </div>
        </TuningModalShell>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        title="Delete Integration"
        message="Are you sure you want to delete this integration? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isConfirmLoading={loading}
        onConfirm={confirmDeleteIntegration}
        onCancel={() => setDeleteConfirmModal({ isOpen: false, integrationId: null })}
      />

      <ConfirmModal
        isOpen={exportConfiguratorModal.open && exportConfiguratorModal.mode === 'replace_confirm'}
        title="Replace Integration Configurator?"
        message="The selected optimization step will overwrite the current Integration Configurator fields. Any unsaved edits in the configurator will be lost unless you save before continuing. Optimization is not stopped by this action."
        confirmText="Replace configuration"
        cancelText="Cancel"
        isConfirmLoading={exportConfiguratorBusy}
        onConfirm={() => {
          applyPendingExportToConfigurator();
        }}
        onCancel={closeExportConfiguratorModal}
      />

      {exportConfiguratorModal.open && exportConfiguratorModal.mode === 'while_running' && (
        <div
          className="confirm-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-configurator-running-title"
          style={{ zIndex: 2100 }}
        >
          <div className="confirm-modal">
            <div className="confirm-modal-header">
              <h3 id="export-configurator-running-title" className="confirm-modal-title">
                Export while optimization is running
              </h3>
              <button
                type="button"
                className="confirm-modal-close"
                onClick={closeExportConfiguratorModal}
                disabled={exportConfiguratorBusy}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-modal-message">
                Loading this step will replace the current Integration Configurator configuration. Optimization is still in
                progress — additional steps may keep arriving until you stop the run from the Stop button.
              </p>
              <p className="confirm-modal-message" style={{ marginTop: 12 }}>
                Choose an action:
              </p>
            </div>
            <div
              className="confirm-modal-footer"
              style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}
            >
              <button
                type="button"
                className="confirm-modal-btn confirm-modal-btn-secondary"
                onClick={closeExportConfiguratorModal}
                disabled={exportConfiguratorBusy}
              >
                Don&apos;t export
              </button>
              <button
                type="button"
                className="confirm-modal-btn confirm-modal-btn-primary"
                onClick={() => applyPendingExportToConfigurator()}
                disabled={exportConfiguratorBusy}
              >
                Export only
              </button>
              <button
                type="button"
                className="confirm-modal-btn confirm-modal-btn-primary"
                onClick={() => void exportStepAndStopOptimization()}
                disabled={exportConfiguratorBusy}
                style={{
                  background: '#FF8300',
                  borderColor: '#FF8300',
                }}
              >
                {exportConfiguratorBusy ? 'Working…' : 'Export and stop optimization'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`nlt-dock${nltOpen ? '' : ' nlt-dock--hidden'}`}
        role="complementary"
        aria-label="NLT panel"
        aria-hidden={!nltOpen}
      >
        <NLTSidebar
          integrationId={selectedIntegrationId}
          mode={activeTab === 'workflow' ? 'workflow' : 'config'}
          workflowBridge={nltWorkflowBridge}
          connectorConfig={currentConfig ?? configValue}
          patchReview={nltPatchReview}
          onPatchReviewChange={setNltPatchReview}
          onFocusConfigurator={() => setActiveTab('integrations')}
          onApplyPatch={handleNltApplyPatch}
          onRejectPatch={handleNltRejectPatch}
          focusedPatchPath={nltFocusedPatchPath}
          onNavigatePatchField={handleNltNavigatePatchField}
          pendingQuote={nltPendingQuote}
          onClearQuote={() => setNltPendingQuote(null)}
          datasets={allAvailableDatasets}
          selectedDatasets={optimizationDatasets}
          onDatasetsChange={setOptimizationDatasets}
          datasetsLoading={datasetsLoading}
          onClose={() => {
            setNltOpen(false);
            setNltPendingQuote(null);
          }}
        />
      </div>

      {/* Onboarding */}
      {/* Optimize Preview Modal — outside tab panels so results survive tab switches */}
      {showOptimizePreviewModal && (
        <div className="connector-form-modal" style={{ zIndex: 10050 }}>
          <div className="connector-form-content" style={{ maxWidth: '720px', width: '95vw' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-calculator" style={{ color: '#0099FA' }}></i>
              Optimize preview
            </h3>
            {optimizePreviewLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner spinner--small"></div>
                <p>Loading preview...</p>
              </div>
            ) : optimizePreviewError ? (
              <div className="connector-message error" style={{ margin: '0 0 16px 0' }}>
                <i className="fas fa-exclamation-circle"></i> {optimizePreviewError}
              </div>
            ) : null}
            {!optimizePreviewLoading && optimizePreviewResult != null && !optimizePreviewError && (
              <div className="preview-result-wrap">
                <OptimizationPreviewResultView data={optimizePreviewResult} />
              </div>
            )}
            <div className="form-actions" style={{ marginTop: '20px' }}>
              <button
                onClick={() => {
                  setShowOptimizePreviewModal(false);
                  setOptimizePreviewResult(null);
                  setOptimizePreviewError(null);
                }}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <EvaluationJobDetailsModal
        open={evaluationDetailsModalOpen}
        onClose={handleCloseEvaluationJobDetails}
        job={evaluationDetailsJob}
        loading={evaluationDetailsLoading}
        trackedProgressPercent={
          evaluationDetailsJob?.id && evaluationPollingJobId === evaluationDetailsJob.id
            ? evaluationProgressDisplay
            : undefined
        }
        isTrackedActive={Boolean(
          evaluationDetailsJob?.id &&
            evaluationPollingJobId === evaluationDetailsJob.id &&
            isEvaluationJobRunning
        )}
        onCancel={(jobId) => void handleCancelEvaluationJob(jobId)}
        onRetry={(jobId) =>
          void handleRetryEvaluationJob(jobId, buildJobRetryUiRowKey({ kind: 'details', jobId }))
        }
        onRunAgain={(job) => {
          handleCloseEvaluationJobDetails();
          handleOpenEvaluationRunAgain(job as unknown as Record<string, unknown>, job.id);
        }}
        cancellingId={evaluationJobCancellingId}
        retryingRowKey={evaluationJobRetryingRowKey}
        isAnyJobRunning={isEvaluationJobRunning}
      />

      <EvaluationRunModal
        open={showEvaluationRunModal}
        onClose={handleCloseEvaluationRunModal}
        integrationId={selectedIntegrationId}
        datasets={allAvailableDatasets}
        datasetsLoading={datasetsLoading}
        buildConfig={buildStandaloneEvaluationConfig}
        onJobStarted={handleEvaluationJobStarted}
        activeJob={evaluationJobSnapshot}
        isJobRunning={isEvaluationJobRunning}
        runAgainSeed={evaluationRunAgainSeed}
        runAgainKey={evaluationRunAgainKey}
      />

      <ConnectorOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onSelectIntegration={selectIntegration}
        selectedIntegrationId={selectedIntegrationId}
        integrations={integrations}
      />
    </div>
  );
};

export default ConnectorPage;
